<template>
  <div class="dashboard-container">
    <!-- SIDEBAR -->
    <Sidebar @open-contacts="openContactsModal" />

    <!-- MAIN -->
    <main class="main">
      <!-- TOPBAR -->
      <Topbar />

      <!-- CONTENT -->
      <div class="content">

      <!-- MONTH TABS -->
      <div class="month-tabs">
        <div
          class="month-tab"
          :class="{ active: activeMonth === 'current' }"
          @click="activeMonth = 'current'"
        >This Month — {{ getCurrentMonth() }}</div>
        <div
          class="month-tab"
          :class="{ active: activeMonth === 'last' }"
          @click="activeMonth = 'last'"
        >Last Month — {{ getLastMonth() }}</div>
      </div>

      <!-- PROPERTY SELECTOR -->
      <div class="prop-selector-row" v-if="activeMonth === 'current'">
        <span class="prop-label">Property</span>
        <div class="dd-wrap" v-click-outside="() => currentDropdownOpen = false">
          <button
            type="button"
            class="dd-trigger"
            :class="{ open: currentDropdownOpen }"
            :disabled="loading"
            @click="currentDropdownOpen = !currentDropdownOpen"
          >
            <span class="dd-trigger-icon" :class="currentSelection.type === 'group' ? 'is-group' : 'is-prop'">
              <svg v-if="currentSelection.type === 'group'" viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="1.5" y="2" width="5.5" height="4" rx="1"/><rect x="9" y="2" width="5.5" height="4" rx="1"/><rect x="1.5" y="10" width="5.5" height="4" rx="1"/><path d="M11.5 10v4M9.5 12h4" stroke-linecap="round"/></svg>
              <svg v-else viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 7l6-5 6 5v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke-linejoin="round"/></svg>
            </span>
            <span class="dd-trigger-label">
              <template v-if="currentSelection.type === 'group' && currentGroup">{{ currentGroup.name }}<span class="dd-trigger-pill">{{ currentGroup.member_count }} linked</span></template>
              <template v-else>{{ propertyNameById(selectedCurrentProperty) }}</template>
            </span>
            <svg class="dd-trigger-chev" viewBox="0 0 10 6" width="10" height="6"><path d="M0 0l5 6 5-6z" fill="currentColor"/></svg>
          </button>
          <div v-if="currentDropdownOpen" class="dd-panel">
            <div class="dd-section-title">Properties</div>
            <button
              type="button"
              v-for="property in properties"
              :key="'p'+property.id"
              class="dd-item"
              :class="{ active: currentSelection.type === 'property' && selectedCurrentProperty.toString() === property.id.toString() }"
              @click="onSelectCurrent(property.id.toString())"
            >
              <span class="dd-item-icon is-prop">
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 7l6-5 6 5v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke-linejoin="round"/></svg>
              </span>
              <span class="dd-item-name">{{ property.name }}</span>
              <svg v-if="currentSelection.type === 'property' && selectedCurrentProperty.toString() === property.id.toString()" class="dd-item-check" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><polyline points="2,7 6,11 12,3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <template v-if="groups.length">
              <div class="dd-section-title">Linked Groups</div>
              <button
                type="button"
                v-for="group in groups"
                :key="'g'+group.id"
                class="dd-item"
                :class="{ active: currentSelection.type === 'group' && currentSelection.id === group.id }"
                @click="onSelectCurrent('group:'+group.id)"
              >
                <span class="dd-item-icon is-group">
                  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="1.5" y="2" width="5.5" height="4" rx="1"/><rect x="9" y="2" width="5.5" height="4" rx="1"/><rect x="1.5" y="10" width="5.5" height="4" rx="1"/><path d="M11.5 10v4M9.5 12h4" stroke-linecap="round"/></svg>
                </span>
                <span class="dd-item-name">{{ group.name }}</span>
                <span class="dd-item-pill">{{ group.member_count }} linked</span>
                <svg v-if="currentSelection.type === 'group' && currentSelection.id === group.id" class="dd-item-check" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><polyline points="2,7 6,11 12,3" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </template>
          </div>
        </div>
      </div>
      <div class="prop-selector-row" v-else>
        <span class="prop-label">Property</span>
        <div class="dd-wrap" v-click-outside="() => lastDropdownOpen = false">
          <button
            type="button"
            class="dd-trigger"
            :class="{ open: lastDropdownOpen }"
            :disabled="loadingLastMonth"
            @click="lastDropdownOpen = !lastDropdownOpen"
          >
            <span class="dd-trigger-icon" :class="lastMonthSelection.type === 'group' ? 'is-group' : 'is-prop'">
              <svg v-if="lastMonthSelection.type === 'group'" viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="1.5" y="2" width="5.5" height="4" rx="1"/><rect x="9" y="2" width="5.5" height="4" rx="1"/><rect x="1.5" y="10" width="5.5" height="4" rx="1"/><path d="M11.5 10v4M9.5 12h4" stroke-linecap="round"/></svg>
              <svg v-else viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 7l6-5 6 5v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke-linejoin="round"/></svg>
            </span>
            <span class="dd-trigger-label">
              <template v-if="lastMonthSelection.type === 'group' && lastMonthGroup">{{ lastMonthGroup.name }}<span class="dd-trigger-pill">{{ lastMonthGroup.member_count }} linked</span></template>
              <template v-else>{{ propertyNameById(selectedLastMonthProperty) }}</template>
            </span>
            <svg class="dd-trigger-chev" viewBox="0 0 10 6" width="10" height="6"><path d="M0 0l5 6 5-6z" fill="currentColor"/></svg>
          </button>
          <div v-if="lastDropdownOpen" class="dd-panel">
            <div class="dd-section-title">Properties</div>
            <button
              type="button"
              v-for="property in properties"
              :key="'p'+property.id"
              class="dd-item"
              :class="{ active: lastMonthSelection.type === 'property' && selectedLastMonthProperty.toString() === property.id.toString() }"
              @click="onSelectLastMonth(property.id.toString())"
            >
              <span class="dd-item-icon is-prop">
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M2 7l6-5 6 5v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z" stroke-linejoin="round"/></svg>
              </span>
              <span class="dd-item-name">{{ property.name }}</span>
              <svg v-if="lastMonthSelection.type === 'property' && selectedLastMonthProperty.toString() === property.id.toString()" class="dd-item-check" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><polyline points="2,7 6,11 12,3" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <template v-if="groups.length">
              <div class="dd-section-title">Linked Groups</div>
              <button
                type="button"
                v-for="group in groups"
                :key="'g'+group.id"
                class="dd-item"
                :class="{ active: lastMonthSelection.type === 'group' && lastMonthSelection.id === group.id }"
                @click="onSelectLastMonth('group:'+group.id)"
              >
                <span class="dd-item-icon is-group">
                  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="1.5" y="2" width="5.5" height="4" rx="1"/><rect x="9" y="2" width="5.5" height="4" rx="1"/><rect x="1.5" y="10" width="5.5" height="4" rx="1"/><path d="M11.5 10v4M9.5 12h4" stroke-linecap="round"/></svg>
                </span>
                <span class="dd-item-name">{{ group.name }}</span>
                <span class="dd-item-pill">{{ group.member_count }} linked</span>
                <svg v-if="lastMonthSelection.type === 'group' && lastMonthSelection.id === group.id" class="dd-item-check" viewBox="0 0 14 14" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><polyline points="2,7 6,11 12,3" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- LOADING STATES -->
      <div v-if="(activeMonth === 'current' && loading) || (activeMonth === 'last' && loadingLastMonth)" class="loading-state">
        <div class="spinner"></div>
        <p>Loading reservations...</p>
      </div>

      <!-- CURRENT MONTH — INDIVIDUAL VIEW -->
      <template v-if="activeMonth === 'current' && !loading && currentSelection.type === 'property'">
        <div class="single-summary">
          <div>
            <div class="single-name">{{ getSelectedPropertyName() || propertyNameById(selectedCurrentProperty) }}</div>
            <div class="single-meta">
              <span>{{ currentMonthReservations.length }} bookings</span>
              <span>{{ getTotalNights(currentMonthReservations) }} nights</span>
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:16px;">
            <div class="single-total">{{ formatCurrency(getTotalRevenue(currentMonthReservations)) }}</div>
            <div class="single-actions">
              <button class="btn" @click="downloadReportPropertyExcel(propertyNameById(selectedCurrentProperty), 'current')">Report Excel</button>
              <button class="btn" @click="downloadPropertyExcel(propertyNameById(selectedCurrentProperty), 'current')">Excel</button>
              <button class="btn btn-purple" @click="openReportModal(propertyNameById(selectedCurrentProperty), 'current')">View Report</button>
            </div>
          </div>
        </div>
        <div class="indiv-section">
          <table>
            <colgroup>
              <col class="col-guest" />
              <col class="col-source" />
              <col class="col-checkin" />
              <col class="col-checkout" />
              <col class="col-nights" />
              <col class="col-amount" />
              <col class="col-services" />
            </colgroup>
            <thead>
              <tr>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th style="text-align:right;">Total Amount</th>
                <th style="text-align:right;">Services</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in currentMonthReservations" :key="r.id" :class="{ 'segment-row': r.isSegment || r.is_segment }">
                <td class="guest-name">
                  {{ r.guest_name || r.guestName }}
                  <span v-if="(r.isSegment || r.is_segment) && ((r.segmentInfo && r.segmentInfo.total > 1) || (r.segment_info && r.segment_info.total > 1))" class="split-badge" :title="getSegmentTooltip(r)">SPLIT {{ (r.segmentInfo && r.segmentInfo.part) || (r.segment_info && r.segment_info.part) }}/{{ (r.segmentInfo && r.segmentInfo.total) || (r.segment_info && r.segment_info.total) }}</span>
                </td>
                <td><span class="src-badge" :class="sourceBadgeClass(r.channel_name || r.otaChannelName)">{{ (r.channel_name || r.otaChannelName || 'Direct').toUpperCase() }}</span></td>
                <td>{{ formatDate(r.arrival_date || r.arrivalDate) }}</td>
                <td>{{ formatDate(r.departure_date || r.departureDate) }}</td>
                <td>{{ r.total_nights || r.totalNights }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_reservation_price || r.totalReservationPrice) }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_services_price || r.totalServicesPrice) }}</td>
              </tr>
              <tr v-if="currentMonthReservations.length === 0"><td colspan="7" style="text-align:center;color:#8b8aaa;padding:24px;">No reservations for this month</td></tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- CURRENT MONTH — GROUP VIEW -->
      <template v-if="activeMonth === 'current' && !loading && currentSelection.type === 'group' && currentGroup">
        <div class="group-summary">
          <div>
            <div class="group-name">
              {{ currentGroup.name }}
              <span class="group-pill">{{ currentGroup.member_count }} linked</span>
            </div>
            <div class="group-meta">
              <span>{{ currentGroupSummary.totalBookings }} bookings</span>
              <span>{{ currentGroupSummary.totalNights }} nights</span>
              <span v-if="currentGroup.owner">Owner: {{ currentGroup.owner.full_legal_name || currentGroup.owner.name }} · {{ currentGroup.owner.primary_email || currentGroup.owner.email }}</span>
            </div>
          </div>
          <div>
            <div class="group-total">{{ formatCurrency(currentGroupSummary.totalRevenue) }}</div>
            <div class="group-total-label">combined gross revenue</div>
          </div>
        </div>
        <div class="group-actions">
          <button class="btn" @click="downloadGroupExcel('current', true)">Report Excel</button>
          <button class="btn" @click="downloadGroupExcel('current', false)">Excel</button>
          <button class="btn btn-purple" @click="viewGroupReport('current')">View Report</button>
        </div>
        <div
          v-for="(section, idx) in bookingsByPropertyFromGroup(currentGroup, currentGroupBookingsByProperty)"
          :key="section.propertyId"
          class="prop-section"
        >
          <div class="prop-section-header">
            <div class="prop-section-name">{{ section.propertyName }}</div>
            <div class="prop-section-meta">
              <span>{{ section.reservations.length }} bookings</span>
              <span>{{ getTotalNights(section.reservations) }} nights</span>
              <span class="prop-section-total">{{ formatCurrency(getTotalRevenue(section.reservations)) }}</span>
            </div>
          </div>
          <table>
            <colgroup>
              <col class="col-guest" />
              <col class="col-source" />
              <col class="col-checkin" />
              <col class="col-checkout" />
              <col class="col-nights" />
              <col class="col-amount" />
              <col class="col-services" />
            </colgroup>
            <thead>
              <tr>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th style="text-align:right;">Total Amount</th>
                <th style="text-align:right;">Services</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in section.reservations" :key="r.id" :class="{ 'segment-row': r.isSegment || r.is_segment }">
                <td class="guest-name">
                  {{ r.guest_name || r.guestName }}
                  <span v-if="(r.isSegment || r.is_segment) && ((r.segmentInfo && r.segmentInfo.total > 1) || (r.segment_info && r.segment_info.total > 1))" class="split-badge" :title="getSegmentTooltip(r)">SPLIT {{ (r.segmentInfo && r.segmentInfo.part) || (r.segment_info && r.segment_info.part) }}/{{ (r.segmentInfo && r.segmentInfo.total) || (r.segment_info && r.segment_info.total) }}</span>
                </td>
                <td><span class="src-badge" :class="sourceBadgeClass(r.channel_name || r.otaChannelName)">{{ (r.channel_name || r.otaChannelName || 'Direct').toUpperCase() }}</span></td>
                <td>{{ formatDate(r.arrival_date || r.arrivalDate) }}</td>
                <td>{{ formatDate(r.departure_date || r.departureDate) }}</td>
                <td>{{ r.total_nights || r.totalNights }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_reservation_price || r.totalReservationPrice) }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_services_price || r.totalServicesPrice) }}</td>
              </tr>
              <tr v-if="section.reservations.length === 0"><td colspan="7" style="text-align:center;color:#8b8aaa;padding:18px;">No reservations</td></tr>
            </tbody>
          </table>
          <div
            v-if="idx === bookingsByPropertyFromGroup(currentGroup, currentGroupBookingsByProperty).length - 1"
            class="shared-costs-bar"
          >
            <span class="sc-label">Shared group costs</span>
            <span class="sc-item">Cleaning {{ formatCurrency(currentGroup.shared_cleaning_fee) }} <span class="sc-note">(shared across {{ currentGroup.member_count }} linked)</span></span>
            <span class="sc-item">Laundry {{ formatCurrency(currentGroup.shared_laundry_fee) }} <span class="sc-note">(shared across {{ currentGroup.member_count }} linked)</span></span>
          </div>
        </div>
      </template>

      <!-- LAST MONTH — INDIVIDUAL VIEW -->
      <template v-if="activeMonth === 'last' && !loadingLastMonth && lastMonthSelection.type === 'property'">
        <div class="single-summary">
          <div>
            <div class="single-name">{{ propertyNameById(selectedLastMonthProperty) }}</div>
            <div class="single-meta">
              <span>{{ lastMonthReservations.length }} bookings</span>
              <span>{{ getTotalNights(lastMonthReservations) }} nights</span>
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:16px;">
            <div class="single-total">{{ formatCurrency(getTotalRevenue(lastMonthReservations)) }}</div>
            <div class="single-actions">
              <button class="btn" @click="downloadReportPropertyExcel(propertyNameById(selectedLastMonthProperty), 'lastmonth')">Report Excel</button>
              <button class="btn" @click="downloadPropertyExcel(propertyNameById(selectedLastMonthProperty), 'lastmonth')">Excel</button>
              <button class="btn btn-purple" @click="openReportModal(propertyNameById(selectedLastMonthProperty), 'lastmonth')">View Report</button>
            </div>
          </div>
        </div>
        <div class="indiv-section">
          <table>
            <colgroup>
              <col class="col-guest" />
              <col class="col-source" />
              <col class="col-checkin" />
              <col class="col-checkout" />
              <col class="col-nights" />
              <col class="col-amount" />
              <col class="col-services" />
            </colgroup>
            <thead>
              <tr>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th style="text-align:right;">Total Amount</th>
                <th style="text-align:right;">Services</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in lastMonthReservations" :key="r.id" :class="{ 'segment-row': r.isSegment || r.is_segment }">
                <td class="guest-name">
                  {{ r.guest_name || r.guestName }}
                  <span v-if="(r.isSegment || r.is_segment) && ((r.segmentInfo && r.segmentInfo.total > 1) || (r.segment_info && r.segment_info.total > 1))" class="split-badge" :title="getSegmentTooltip(r)">SPLIT {{ (r.segmentInfo && r.segmentInfo.part) || (r.segment_info && r.segment_info.part) }}/{{ (r.segmentInfo && r.segmentInfo.total) || (r.segment_info && r.segment_info.total) }}</span>
                </td>
                <td><span class="src-badge" :class="sourceBadgeClass(r.channel_name || r.otaChannelName)">{{ (r.channel_name || r.otaChannelName || 'Direct').toUpperCase() }}</span></td>
                <td>{{ formatDate(r.arrival_date || r.arrivalDate) }}</td>
                <td>{{ formatDate(r.departure_date || r.departureDate) }}</td>
                <td>{{ r.total_nights || r.totalNights }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_reservation_price || r.totalReservationPrice) }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_services_price || r.totalServicesPrice) }}</td>
              </tr>
              <tr v-if="lastMonthReservations.length === 0"><td colspan="7" style="text-align:center;color:#8b8aaa;padding:24px;">No reservations for last month</td></tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- LAST MONTH — GROUP VIEW -->
      <template v-if="activeMonth === 'last' && !loadingLastMonth && lastMonthSelection.type === 'group' && lastMonthGroup">
        <div class="group-summary">
          <div>
            <div class="group-name">
              {{ lastMonthGroup.name }}
              <span class="group-pill">{{ lastMonthGroup.member_count }} linked</span>
            </div>
            <div class="group-meta">
              <span>{{ lastMonthGroupSummary.totalBookings }} bookings</span>
              <span>{{ lastMonthGroupSummary.totalNights }} nights</span>
              <span v-if="lastMonthGroup.owner">Owner: {{ lastMonthGroup.owner.full_legal_name || lastMonthGroup.owner.name }} · {{ lastMonthGroup.owner.primary_email || lastMonthGroup.owner.email }}</span>
            </div>
          </div>
          <div>
            <div class="group-total">{{ formatCurrency(lastMonthGroupSummary.totalRevenue) }}</div>
            <div class="group-total-label">combined gross revenue</div>
          </div>
        </div>
        <div class="group-actions">
          <button class="btn" @click="downloadGroupExcel('last', true)">Report Excel</button>
          <button class="btn" @click="downloadGroupExcel('last', false)">Excel</button>
          <button class="btn btn-purple" @click="viewGroupReport('last')">View Report</button>
        </div>
        <div
          v-for="(section, idx) in bookingsByPropertyFromGroup(lastMonthGroup, lastMonthGroupBookingsByProperty)"
          :key="section.propertyId"
          class="prop-section"
        >
          <div class="prop-section-header">
            <div class="prop-section-name">{{ section.propertyName }}</div>
            <div class="prop-section-meta">
              <span>{{ section.reservations.length }} bookings</span>
              <span>{{ getTotalNights(section.reservations) }} nights</span>
              <span class="prop-section-total">{{ formatCurrency(getTotalRevenue(section.reservations)) }}</span>
            </div>
          </div>
          <table>
            <colgroup>
              <col class="col-guest" />
              <col class="col-source" />
              <col class="col-checkin" />
              <col class="col-checkout" />
              <col class="col-nights" />
              <col class="col-amount" />
              <col class="col-services" />
            </colgroup>
            <thead>
              <tr>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th style="text-align:right;">Total Amount</th>
                <th style="text-align:right;">Services</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in section.reservations" :key="r.id" :class="{ 'segment-row': r.isSegment || r.is_segment }">
                <td class="guest-name">
                  {{ r.guest_name || r.guestName }}
                  <span v-if="(r.isSegment || r.is_segment) && ((r.segmentInfo && r.segmentInfo.total > 1) || (r.segment_info && r.segment_info.total > 1))" class="split-badge" :title="getSegmentTooltip(r)">SPLIT {{ (r.segmentInfo && r.segmentInfo.part) || (r.segment_info && r.segment_info.part) }}/{{ (r.segmentInfo && r.segmentInfo.total) || (r.segment_info && r.segment_info.total) }}</span>
                </td>
                <td><span class="src-badge" :class="sourceBadgeClass(r.channel_name || r.otaChannelName)">{{ (r.channel_name || r.otaChannelName || 'Direct').toUpperCase() }}</span></td>
                <td>{{ formatDate(r.arrival_date || r.arrivalDate) }}</td>
                <td>{{ formatDate(r.departure_date || r.departureDate) }}</td>
                <td>{{ r.total_nights || r.totalNights }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_reservation_price || r.totalReservationPrice) }}</td>
                <td style="text-align:right;">{{ formatCurrency(r.total_services_price || r.totalServicesPrice) }}</td>
              </tr>
              <tr v-if="section.reservations.length === 0"><td colspan="7" style="text-align:center;color:#8b8aaa;padding:18px;">No reservations</td></tr>
            </tbody>
          </table>
          <div
            v-if="idx === bookingsByPropertyFromGroup(lastMonthGroup, lastMonthGroupBookingsByProperty).length - 1"
            class="shared-costs-bar"
          >
            <span class="sc-label">Shared group costs</span>
            <span class="sc-item">Cleaning {{ formatCurrency(lastMonthGroup.shared_cleaning_fee) }} <span class="sc-note">(shared across {{ lastMonthGroup.member_count }} linked)</span></span>
            <span class="sc-item">Laundry {{ formatCurrency(lastMonthGroup.shared_laundry_fee) }} <span class="sc-note">(shared across {{ lastMonthGroup.member_count }} linked)</span></span>
          </div>
        </div>
      </template>

      <!-- LEGACY This Month Section (hidden — replaced by month tabs above) -->
      <div v-if="false" class="section-card">
        <div class="section-header">
          <div class="section-title-container">
            <h2>This Month - {{ getCurrentMonth() }}</h2>
            <div class="property-selector">
              <label for="currentProperty">Property:</label>
              <select 
                id="currentProperty"
                v-model="selectedCurrentProperty" 
                @change="onCurrentPropertyChange"
                class="property-select-small"
                :disabled="loading"
              >
                <option v-for="property in properties" :key="property.id" :value="property.id">
                  {{ property.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading current month data...</p>
        </div>
        
        <div v-else class="properties-section">
          <div v-for="(reservations, unitName) in groupedCurrentMonth" :key="unitName" class="property-group">
            <div class="property-header">
              <h3>{{ unitName }}</h3>
              <div class="property-stats">
                <span class="stat">{{ reservations.length }} bookings</span>
                <span class="stat">{{ getTotalNights(reservations) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(reservations)) }}</span>
              </div>

              <div class="property-actions">
                <button @click="downloadPropertyExcel(unitName, 'current')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="downloadReportPropertyExcel(unitName, 'current')" class="btn btn-sm btn-report-excel">
                  Report Excel
                </button>
                <button @click="openReportModal(unitName, 'current')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Source</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Total Amount</th>
                    <th>Services</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in reservations" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment || reservation.is_segment }">
                <td class="guest-name">
                  {{ reservation.guest_name || reservation.guestName }}
                <span v-if="(reservation.isSegment || reservation.is_segment) && (reservation.segmentInfo?.total || reservation.segment_info?.total) > 1" class="segment-badge" :title="getSegmentTooltip(reservation)">
  SPLIT {{ reservation.segmentInfo?.part || reservation.segment_info?.part }}/{{ reservation.segmentInfo?.total || reservation.segment_info?.total }}
</span>
                </td>
                                  <td>
                      <span class="source-badge">{{ reservation.channel_name || reservation.otaChannelName || 'Direct' }}</span>
                    </td>
                    <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                    <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                    <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_services_price || reservation.totalServicesPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="Object.keys(groupedCurrentMonth).length === 0" class="empty-state">
            <p>No reservations found for this month</p>
          </div>
        </div>
      </div>

      <!-- LEGACY Last Month Section (hidden — replaced by month tabs above) -->
      <div v-if="false" class="section-card">
        <div class="section-header">
          <div class="section-title-container">
            <h2>Last Month - {{ getLastMonth() }}</h2>
            <div class="property-selector">
              <label for="lastMonthProperty">Property:</label>
              <select 
                id="lastMonthProperty"
                v-model="selectedLastMonthProperty" 
                @change="onLastMonthPropertyChange"
                class="property-select-small"
                :disabled="loadingLastMonth"
              >
                <option v-for="property in properties" :key="property.id" :value="property.id">
                  {{ property.name }}
                </option>
              </select>
            </div>
          </div>
        </div>
        
        <div v-if="loadingLastMonth" class="loading-state">
          <div class="spinner"></div>
          <p>Loading last month data...</p>
        </div>
        
        <div v-else class="properties-section">
          <div v-for="(reservations, unitName) in groupedLastMonth" :key="unitName" class="property-group">
            <div class="property-header">
              <h3>{{ unitName }}</h3>
              <div class="property-stats">
                <span class="stat">{{ reservations.length }} bookings</span>
                <span class="stat">{{ getTotalNights(reservations) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(reservations)) }}</span>
              </div>
              <div class="property-actions">
                <button @click="downloadReportPropertyExcel(unitName, 'lastmonth')" class="btn btn-sm btn-report-excel">
                  Report Excel
                </button>
                <button @click="downloadPropertyExcel(unitName, 'lastmonth')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="openReportModal(unitName, 'lastmonth')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
            
            <div class="table-container">
              <table class="data-table">
                <thead>
                  <tr>
                    <th>Guest Name</th>
                    <th>Source</th>
                    <th>Check-in</th>
                    <th>Check-out</th>
                    <th>Nights</th>
                    <th>Total Amount</th>
                    <th>Services</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="reservation in reservations" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment || reservation.is_segment }">
  <td class="guest-name">
    {{ reservation.guest_name || reservation.guestName }}
    <span v-if="(reservation.isSegment || reservation.is_segment)" class="segment-badge" :title="getSegmentTooltip(reservation)">
      SPLIT {{ reservation.segmentInfo?.part || reservation.segment_info?.part }}/{{ reservation.segmentInfo?.total || reservation.segment_info?.total }}
    </span>
  </td>
                    <td>
                      <span class="source-badge">{{ reservation.channel_name || reservation.otaChannelName || 'Direct' }}</span>
                    </td>
                    <td>{{ formatDate(reservation.arrival_date || reservation.arrivalDate) }}</td>
                    <td>{{ formatDate(reservation.departure_date || reservation.departureDate) }}</td>
                    <td class="text-center">{{ reservation.total_nights || reservation.totalNights }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_reservation_price || reservation.totalReservationPrice) }}</td>
                    <td class="amount">{{ formatCurrency(reservation.total_services_price || reservation.totalServicesPrice) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-if="Object.keys(groupedLastMonth).length === 0" class="empty-state">
            <p>No reservations found for last month</p>
          </div>
        </div>
      </div>

      <!-- History Section -->
      <div class="section-card">
        <div class="section-header">
          <h2>Reservations History</h2>
        </div>
        
        <div class="filters-section">
          <div class="filter-group">
            <label>Property:</label>
            <select v-model="selectedProperty" class="filter-select">
              <option value="">All Properties</option>
              <option v-for="property in properties" :key="'hp'+property.id" :value="property.id">
                {{ property.name }}
              </option>
              <option v-if="groups.length" disabled>─── Groups ───</option>
              <option v-for="group in groups" :key="'hg'+group.id" :value="'group:'+group.id">
                🔗 {{ group.name }} ({{ group.member_count }} linked)
              </option>
            </select>
          </div>
          
          <div class="filter-group">
            <label>Month:</label>
            <select v-model="selectedMonth" class="filter-select">
              <option value="">Select a month</option>
              <option v-for="month in availableMonths" :key="month.value" :value="month.value">
                {{ month.label }}
              </option>
            </select>
          </div>
          
          <div class="filter-actions">
            <button @click="applyFilters" class="btn btn-search">Search</button>
            <button @click="clearFilters" class="btn btn-outline">Clear Filters</button>
          </div>
        </div>

        <!-- Historical Reports Section - Only show when results are available -->
        <div v-if="searchResults.length > 0 && appliedFilters.month" class="historical-reports-section">
          <div class="property-group">
            <div class="property-header">
              <h3>{{ getSelectedPropertyName() || 'All Properties' }}</h3>
              <div class="property-stats">
                <span class="stat">{{ searchResults.length }} bookings</span>
                <span class="stat">{{ getTotalNights(searchResults) }} nights</span>
                <span class="stat revenue">{{ formatCurrency(getTotalRevenue(searchResults)) }}</span>
              </div>

              <div class="property-actions">
                <button @click="downloadPropertyExcel(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-excel">
                  Excel
                </button>
                <button @click="downloadReportPropertyExcel(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-report-excel">
                  Report Excel
                </button>
                <button @click="openReportModal(getSelectedPropertyName() || 'All_Properties', 'historical')" class="btn btn-sm btn-report">
                  View Report
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="loadingHistory" class="loading-state">
          <div class="spinner"></div>
          <p>Loading reservation history...</p>
        </div>
        
        <div v-else class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Guest Name</th>
                <th>Source</th>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Nights</th>
                <th>Total Amount</th>
                <th>Services</th>
                <th>Booked At</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="reservation in paginatedHistory" :key="reservation.id" :class="{ 'segment-row': reservation.isSegment }">
                <td class="property-name">{{ reservation.unitName }}</td>
                <td class="guest-name">
                  {{ reservation.guestName }}
                  <span v-if="reservation.isSegment && reservation.segmentInfo.total > 1" class="segment-badge" :title="getSegmentTooltip(reservation)">
                    SPLIT {{ reservation.segmentInfo.part }}/{{ reservation.segmentInfo.total }}
                  </span>
                </td>
                <td>
                  <span class="source-badge">{{ reservation.otaChannelName || 'Direct' }}</span>
                </td>
                <td>{{ formatDate(reservation.arrivalDate) }}</td>
                <td>{{ formatDate(reservation.departureDate) }}</td>
                <td class="text-center">{{ reservation.totalNights }}</td>
                <td class="amount">{{ formatCurrency(reservation.totalReservationPrice) }}</td>
                <td class="amount">{{ formatCurrency(reservation.totalServicesPrice) }}</td>
                <td>{{ formatDate(reservation.bookedAt) }}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="pagination" v-if="searchResults.length > itemsPerPage">
            <button @click="prevPage" :disabled="currentPage === 1" class="pagination-btn">
              Previous
            </button>
            <span class="pagination-info">
              Page {{ currentPage }} of {{ totalPages }} ({{ searchResults.length }} total)
            </span>
            <button @click="nextPage" :disabled="currentPage === totalPages" class="pagination-btn">
              Next
            </button>
          </div>
          
          <div v-if="searchResults.length === 0 && (appliedFilters.property || appliedFilters.month)" class="empty-state">
            <p>No reservations found for the selected filters</p>
          </div>

          <div v-if="searchResults.length === 0 && !appliedFilters.property && !appliedFilters.month" class="empty-state">
            <p>Use the search filters above to find reservations</p>
          </div>
        </div>
      </div>
      </div>
    </main>

    <!-- GROUP Report Modal — strictement même structure que .report-modal (single property), sans settings-panel -->
    <div v-if="showGroupReportModal" class="report-modal-overlay" @click="closeGroupReportModal">
      <div class="report-modal" @click.stop>
        <!-- Modal Header (même DOM que single-property) -->
        <div class="modal-header">
          <div class="modal-title">
            <h2>{{ groupReportModalData.group ? groupReportModalData.group.name : '' }} — {{ groupReportModalData.monthLabel }} Report</h2>
            <div class="modal-subtitle">
              <span
                class="status-pill"
                :class="(groupReportModalData.period === 'current' ? currentGroupLockStatus : lastGroupLockStatus) && (groupReportModalData.period === 'current' ? currentGroupLockStatus : lastGroupLockStatus).locked ? 'saved' : 'unsaved'"
              >
                <span class="status-dot"></span>
                <template v-if="(groupReportModalData.period === 'current' ? currentGroupLockStatus : lastGroupLockStatus) && (groupReportModalData.period === 'current' ? currentGroupLockStatus : lastGroupLockStatus).locked">Locked</template>
                <template v-else>Draft</template>
              </span>
              <span class="group-modal-props" v-if="groupReportModalData.group">
                {{ groupReportModalData.group.member_count }} linked · {{ groupMemberNames(groupReportModalData.group) }}
              </span>
            </div>
          </div>
          <button @click="closeGroupReportModal" class="close-btn" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="modal-content">
          <!-- Settings Panel (Left Side) — mirrors the single-property sidebar -->
          <div class="settings-panel">
            <div class="report-sidebar">

              <!-- Scope note (mirror single) -->
              <div class="info-note">
                Changes made here affect this group report only and do not update your property settings.
              </div>

              <!-- ── CONFIGURATION ── -->
              <div class="divider-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>
                Configuration
              </div>

              <!-- Management fee (group level) -->
              <div class="section">
                <p class="section-title">Management fee</p>
                <div style="display:flex;gap:6px;margin-bottom:12px;">
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: groupModalSettings.managementFeeBase !== 'grossFixed' }"
                    @click="groupModalSettings.managementFeeBase = 'net'; markGroupAsModified()"
                  >Percentage</button>
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: groupModalSettings.managementFeeBase === 'grossFixed' }"
                    @click="groupModalSettings.managementFeeBase = 'grossFixed'; markGroupAsModified()"
                  >Fixed amount</button>
                </div>
                <div v-if="groupModalSettings.managementFeeBase !== 'grossFixed'">
                  <p class="field-label">Fee rate</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <input
                      type="number"
                      v-model.number="groupModalSettings.managementFeeRate"
                      @input="markGroupAsModified()"
                      step="0.1" min="0" max="100"
                    >
                    <span>%</span>
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">Applied to the combined report for this group.</p>
                </div>
                <div v-else>
                  <p class="field-label">Fixed amount</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model.number="groupModalSettings.managementFeeFixedAmount"
                      @input="markGroupAsModified()"
                      step="0.01" min="0"
                    >
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">Fixed fee for the combined report — overrides the percentage rate.</p>
                </div>
              </div>

              <!-- ── INCOME ── -->
              <div class="divider-label income">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                Income
              </div>

              <!-- Guest extras (group-level) -->
              <div class="section">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
                  <p class="section-title" style="margin-bottom:0;color:#3B6D11;">Guest extras</p>
                  <button class="add-btn blue" type="button" @click="openGroupGuestExtraModal">+ Add extra</button>
                </div>
                <p style="font-size:11px;color:var(--text-muted);margin-bottom:10px;">Additional revenue charged to guests — pet fee, travel cot, early check-in etc.</p>
                <p v-if="groupGuestExtras.length === 0" class="empty-note">No guest extras added yet.</p>
                <div v-else>
                  <div v-for="extra in groupGuestExtras" :key="extra.id" class="extras-item">
                    <span class="name">{{ extra.description }}</span>
                    <span class="amt">£{{ parseFloat(extra.amount).toFixed(2) }}</span>
                    <button type="button" @click="deleteGroupExpense(extra.id)" aria-label="Remove">×</button>
                  </div>
                </div>
                <div v-if="groupGuestExtras.length" class="total-row total-income">
                  <span>Total guest extras</span>
                  <strong>£{{ totalGroupGuestExtras.toFixed(2) }}</strong>
                </div>
              </div>

              <!-- ── EXPENSES ── -->
              <div class="divider-label expense">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                Expenses
              </div>

              <!-- Platform commission (group level) -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:10px;">Platform commission</p>
                <div style="display:flex;gap:6px;margin-bottom:14px;">
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: groupModalSettings.platformFeeMode === 'rentlio' }"
                    @click="groupModalSettings.platformFeeMode = 'rentlio'; markGroupAsModified()"
                  >Channel manager</button>
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: groupModalSettings.platformFeeMode === 'custom' }"
                    @click="groupModalSettings.platformFeeMode = 'custom'; markGroupAsModified()"
                  >Settings rate</button>
                </div>
                <p v-if="groupModalSettings.platformFeeMode === 'rentlio'" style="font-size:11px;color:#007a63;margin-bottom:0;">Commission pulled per booking — Genius tier rates and promotions applied automatically.</p>
                <div v-else>
                  <div class="platform-row" style="border-bottom-color:#f0eef8;">
                    <span class="platform-name">Booking.com</span>
                    <div style="display:flex;align-items:center;gap:5px;margin-left:auto;">
                      <input
                        type="number"
                        v-model.number="groupModalSettings.bookingComCommissionRate"
                        @input="groupModalSettings.applyCustomBookingCom = true; markGroupAsModified()"
                        step="0.01" min="0"
                        style="width:60px;border:1px solid var(--border);border-radius:var(--radius);padding:5px 7px;font-size:13px;font-family:inherit;outline:none;background:var(--bg);text-align:right;"
                      >
                      <span style="font-size:12px;color:var(--text-muted);">%</span>
                    </div>
                  </div>
                  <div class="platform-row" style="border-bottom-color:#f0eef8;">
                    <span class="platform-name">Airbnb</span>
                    <div style="display:flex;align-items:center;gap:5px;margin-left:auto;">
                      <input
                        type="number"
                        v-model.number="groupModalSettings.airbnbCommissionRate"
                        @input="groupModalSettings.applyCustomAirbnb = true; markGroupAsModified()"
                        step="0.01" min="0"
                        style="width:60px;border:1px solid var(--border);border-radius:var(--radius);padding:5px 7px;font-size:13px;font-family:inherit;outline:none;background:var(--bg);text-align:right;"
                      >
                      <span style="font-size:12px;color:var(--text-muted);">%</span>
                    </div>
                  </div>
                  <div class="platform-row" style="border-bottom-color:#f0eef8;">
                    <span class="platform-name" style="color:var(--text-muted);font-style:italic;">All others</span>
                    <div style="display:flex;align-items:center;gap:5px;margin-left:auto;">
                      <input
                        type="number"
                        v-model.number="groupModalSettings.otherPlatformCommissionRate"
                        @input="groupModalSettings.applyCustomOther = true; markGroupAsModified()"
                        step="0.01" min="0"
                        style="width:60px;border:1px solid var(--border);border-radius:var(--radius);padding:5px 7px;font-size:13px;font-family:inherit;outline:none;background:var(--bg);text-align:right;"
                      >
                      <span style="font-size:12px;color:var(--text-muted);">%</span>
                    </div>
                  </div>
                </div>
                <div class="platform-row" style="border-bottom:none;margin-top:6px;">
                  <span class="platform-name" style="color:var(--text-muted);">Direct bookings</span>
                  <span class="source-badge" style="margin-left:auto;background:var(--bg);color:var(--text-muted);border:1px solid var(--border);padding:2px 8px;border-radius:20px;font-size:10px;">Set in Settings</span>
                </div>
              </div>

              <!-- Card fees (group level) -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:10px;">Card fees</p>
                <div style="display:flex;gap:6px;margin-bottom:12px;">
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: groupModalSettings.cardFeeMode === 'rentlio' }"
                    @click="groupModalSettings.cardFeeMode = 'rentlio'; markGroupAsModified()"
                  >Channel manager</button>
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: groupModalSettings.cardFeeMode === 'custom' }"
                    @click="groupModalSettings.cardFeeMode = 'custom'; markGroupAsModified()"
                  >Settings rate</button>
                </div>
                <div v-if="groupModalSettings.cardFeeMode === 'custom'">
                  <p class="field-label">Rate</p>
                  <div class="input-wrap" style="max-width:120px;margin-bottom:8px;">
                    <input
                      type="number"
                      v-model.number="groupModalSettings.customCardFeeRate"
                      @input="markGroupAsModified()"
                      step="0.01" min="0" max="10"
                    >
                    <span>%</span>
                  </div>
                  <p class="field-label">Fixed fee per transaction</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model.number="groupModalSettings.customCardFeeFixed"
                      @input="markGroupAsModified()"
                      step="0.01" min="0"
                    >
                  </div>
                </div>
                <p v-else style="font-size:11px;color:var(--text-muted);margin-top:8px;">Card processing fees pulled from your channel manager per booking.</p>
              </div>

              <!-- Cleaning (shared across group) -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:12px;">Cleaning</p>
                <div>
                  <div class="radio-opt" :class="{ active: groupModalSettings.sharedCleaningMode === 'per_booking' }" @click="groupModalSettings.sharedCleaningMode = 'per_booking'; markGroupAsModified()">
                    <div class="radio-dot"></div><span>Per changeover (sum across linked)</span>
                  </div>
                  <div class="radio-opt" :class="{ active: groupModalSettings.sharedCleaningMode === 'monthly_flat' }" @click="groupModalSettings.sharedCleaningMode = 'monthly_flat'; markGroupAsModified()">
                    <div class="radio-dot"></div><span>Monthly fixed fee</span>
                  </div>
                </div>
                <div v-if="groupModalSettings.sharedCleaningMode === 'monthly_flat'" style="margin-top:10px;">
                  <p class="field-label">Monthly amount (whole group)</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model.number="groupModalSettings.sharedCleaningFee"
                      @input="markGroupAsModified()"
                      step="0.01" min="0"
                    >
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">Flat fee charged once for the entire group this month.</p>
                </div>
                <p v-else style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">Each linked property's per-changeover cleaning cost is summed.</p>
              </div>

              <!-- Laundry & linen (shared across group) -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:12px;">Laundry &amp; linen supply</p>
                <div>
                  <div class="radio-opt" :class="{ active: groupModalSettings.sharedLaundryMode === 'per_booking' }" @click="groupModalSettings.sharedLaundryMode = 'per_booking'; markGroupAsModified()">
                    <div class="radio-dot"></div><span>Per changeover (sum across linked)</span>
                  </div>
                  <div class="radio-opt" :class="{ active: groupModalSettings.sharedLaundryMode === 'monthly_flat' }" @click="groupModalSettings.sharedLaundryMode = 'monthly_flat'; markGroupAsModified()">
                    <div class="radio-dot"></div><span>Monthly fixed fee</span>
                  </div>
                </div>
                <div v-if="groupModalSettings.sharedLaundryMode === 'monthly_flat'" style="margin-top:10px;">
                  <p class="field-label">Monthly amount (whole group)</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model.number="groupModalSettings.sharedLaundryFee"
                      @input="markGroupAsModified()"
                      step="0.01" min="0"
                    >
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">Flat laundry fee charged once for the entire group this month.</p>
                </div>
                <p v-else style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">Each linked property's per-changeover laundry cost is summed.</p>
              </div>

              <!-- Consumables (group level) -->
              <div class="section">
                <div class="cons-toggle-row">
                  <div>
                    <p class="section-title" style="margin-bottom:2px;color:#854F0B;">Consumables</p>
                    <p style="font-size:11px;color:var(--text-muted);">Override the monthly consumables amount for the whole group.</p>
                  </div>
                  <label class="toggle">
                    <input type="checkbox" v-model="groupModalSettings.useCustomConsumables" @change="markGroupAsModified()">
                    <div class="toggle-track"></div>
                    <div class="toggle-thumb"></div>
                  </label>
                </div>
                <div v-if="groupModalSettings.useCustomConsumables" style="margin-top:12px;">
                  <p class="field-label">Monthly amount</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model.number="groupModalSettings.customConsumablesAmount"
                      @input="markGroupAsModified()"
                      step="0.01" min="0"
                    >
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">For one-off or receipt-based consumable costs add them in Expenses below.</p>
                </div>
                <p v-else style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">Using default group amount (£{{ groupModalSettings.consumables }}).</p>
              </div>

              <!-- Expenses (group-level, with attachments) -->
              <div class="section">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
                  <p class="section-title" style="margin-bottom:0;color:#854F0B;">Expenses</p>
                  <button class="add-btn green" type="button" @click="openGroupExpenseModal">+ Add expense</button>
                </div>
                <p style="font-size:11px;color:var(--text-muted);margin-bottom:10px;">Maintenance, cleaning and other costs — set the category per item. Attach receipts as needed.</p>
                <p v-if="nonGuestExtraGroupExpenses.length === 0" class="empty-note">No items added yet.</p>
                <div v-else>
                  <div v-for="expense in nonGuestExtraGroupExpenses" :key="expense.id" class="expense-item">
                    <div style="flex:1;display:flex;flex-direction:column;gap:3px;min-width:0;">
                      <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                        <span class="name">{{ expense.description }}</span>
                        <span class="expense-cat-badge" :class="expense.expense_category || 'maintenance'">{{ expenseCategoryLabel(expense.expense_category) }}</span>
                      </div>
                      <div v-if="(expense.files && expense.files.length) || expense.receipt_url" style="display:flex;flex-wrap:wrap;gap:4px;">
                        <a v-if="expense.receipt_url" :href="expense.receipt_url" target="_blank" rel="noopener" class="expense-file-chip">📎 {{ expense.receipt_filename || 'receipt' }}</a>
                        <a v-for="f in (expense.files || [])" :key="f.id" :href="f.url" target="_blank" rel="noopener" class="expense-file-chip">📎 {{ f.filename }}</a>
                      </div>
                    </div>
                    <span class="amt">£{{ parseFloat(expense.amount).toFixed(2) }}</span>
                    <button type="button" @click="deleteGroupExpense(expense.id)" aria-label="Remove">×</button>
                  </div>
                </div>
                <div v-if="nonGuestExtraGroupExpenses.length" class="total-row total-neutral">
                  <span>Total expenses</span>
                  <strong>£{{ totalNonGuestExtraGroupExpenses.toFixed(2) }}</strong>
                </div>
              </div>

              <!-- Save button (mirror single footer) -->
              <div class="section" style="position:sticky;bottom:0;background:#fff;padding-top:10px;border-top:1px solid var(--border);">
                <button
                  type="button"
                  class="btn btn-lock-report"
                  style="width:100%;"
                  :disabled="savingGroupSettings || !groupSettingsModified"
                  @click="saveGroupSettings"
                >
                  {{ savingGroupSettings
                       ? 'Saving…'
                       : (groupSettingsModified ? 'Save settings' : 'All changes saved') }}
                </button>
                <p style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;text-align:center;">Save to keep these settings when you reopen the modal. Approving the report also saves.</p>
              </div>
            </div>
          </div>

          <!-- Report Preview -->
          <div class="preview-panel">
            <div class="preview-header">
              <div class="preview-actions">
                <button @click="downloadGroupReportPDF" :disabled="groupDownloadingPDF || groupReportModalData.generating" class="btn btn-pdf">
                  <template v-if="groupDownloadingPDF">Preparing…</template>
                  <template v-else>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download PDF
                  </template>
                </button>
                <button
                  v-if="(groupReportModalData.period === 'current' ? currentGroupLockStatus : lastGroupLockStatus)
                    && (groupReportModalData.period === 'current' ? currentGroupLockStatus : lastGroupLockStatus).locked"
                  @click="toggleGroupLock(groupReportModalData.period)"
                  :disabled="lockingGroup"
                  class="btn btn-unlock"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/></svg>
                  Unlock
                </button>
                <button
                  v-else
                  @click="toggleGroupLock(groupReportModalData.period)"
                  :disabled="lockingGroup"
                  class="btn btn-lock-report"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Approve Report
                </button>
              </div>
            </div>

            <div class="preview-content" style="background: #ffffff; padding: 10px; overflow-y: auto; overflow-x: hidden;">
              <div v-if="groupReportModalData.generating" class="group-report-loading">
                <div class="spinner"></div>
                <p>Generating combined report…</p>
              </div>
              <div v-else-if="groupReportModalData.previewHtml" class="pdf-preview-wrapper" style="
                width: 210mm;
                min-height: 297mm;
                background: white;
                margin: 0 auto;
                box-shadow: 0 4px 20px rgba(0,0,0,0.15);
                transform-origin: top center;
                transform: scale(1.0);
                border: 1px solid #e0e0e0;
                position: relative;
              ">
                <div v-if="groupPreviewLoading" style="position:absolute;top:8px;right:8px;background:rgba(91,63,200,0.92);color:#fff;font-size:11px;font-weight:600;padding:4px 10px;border-radius:12px;z-index:5;">Refreshing…</div>
                <div v-html="groupReportModalData.previewHtml" style="width:100%;height:100%;"></div>
              </div>
              <div v-else class="group-report-loading">
                <p>No preview available.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Report Modal -->
    <div v-if="showReportModal" class="report-modal-overlay" @click="closeReportModal">
      <div class="report-modal" @click.stop>
        <!-- Modal Header -->
        <div class="modal-header">
          <div class="modal-title">
            <h2>{{ modalData.propertyName }} — {{ modalData.periodLabel }} Report</h2>
            <div class="modal-subtitle">
              <span class="status-pill" :class="hasUnsavedChanges ? 'unsaved' : 'saved'">
                <span class="status-dot"></span>
                {{ hasUnsavedChanges ? 'Unsaved changes' : 'All changes saved' }}
              </span>
            </div>
          </div>
          <button @click="closeReportModal" class="close-btn" aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="modal-content">
          <!-- Settings Panel (Left Side) -->
          <div class="settings-panel">
            <div class="report-sidebar">

              <!-- Scope note (W1 Refonte #10) -->
              <div class="info-note">
                Changes made here affect this report only and do not update your property settings.
              </div>

              <!-- ── CONFIGURATION ── -->
              <div class="divider-label">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/></svg>
                Configuration
              </div>

              <div class="section">
                <p class="section-title">Management fee</p>
                <div style="display:flex;gap:6px;margin-bottom:12px;">
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: modalSettings.managementFeeBase !== 'grossFixed' }"
                    @click="modalSettings.managementFeeBase = 'net'; markAsModified(); calculateMetrics()"
                  >Percentage</button>
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: modalSettings.managementFeeBase === 'grossFixed' }"
                    @click="modalSettings.managementFeeBase = 'grossFixed'; markAsModified(); calculateMetrics()"
                  >Fixed amount</button>
                </div>
                <div v-if="modalSettings.managementFeeBase !== 'grossFixed'">
                  <p class="field-label">Fee rate</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <input
                      type="number"
                      v-model="modalSettings.managementFeeRate"
                      @input="handleNumericInput('managementFeeRate', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('managementFeeRate', 0)"
                      step="0.1" min="0" max="100"
                    >
                    <span>%</span>
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">Override for this month only — property default applies otherwise.</p>
                </div>
                <div v-else>
                  <p class="field-label">
                    Fixed amount
                    <span v-if="hasMonthSpecificFee" style="font-size:10px;font-weight:600;color:var(--accent);background:var(--accent-dim);border:1px solid var(--accent-border);padding:1px 6px;border-radius:10px;margin-left:5px;">Month-specific</span>
                  </p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.managementFeeFixedAmount"
                      @input="handleNumericInput('managementFeeFixedAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('managementFeeFixedAmount', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">Fixed fee for this month — overrides the percentage rate.</p>
                </div>
              </div>

              <!-- ── INCOME (W1 Refonte #10) ── -->
              <div class="divider-label income">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                Income
              </div>

              <!-- Guest extras -->
              <div class="section">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:4px;">
                  <p class="section-title" style="margin-bottom:0;color:#3B6D11;">Guest extras</p>
                  <button class="add-btn blue" type="button" @click="openGuestExtraModal">+ Add extra</button>
                </div>
                <p style="font-size:11px;color:var(--text-muted);margin-bottom:10px;">Additional revenue charged to guests — pet fee, travel cot, early check-in etc.</p>
                <p v-if="guestExtras.length === 0" class="empty-note">No guest extras added yet.</p>
                <div v-else>
                  <div v-for="extra in guestExtras" :key="extra.id" class="extras-item">
                    <span class="name">{{ extra.description }}</span>
                    <span class="amt">£{{ parseFloat(extra.amount).toFixed(2) }}</span>
                    <button type="button" @click="deleteExpense(extra.id)" aria-label="Remove">×</button>
                  </div>
                </div>
                <div v-if="guestExtras.length" class="total-row total-income">
                  <span>Total guest extras</span>
                  <strong>£{{ totalGuestExtras.toFixed(2) }}</strong>
                </div>
              </div>

              <!-- ── EXPENSES ── -->
              <div class="divider-label expense">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                Expenses
              </div>

              <!-- Platform commission -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:10px;">Platform commission</p>
                <div style="display:flex;gap:6px;margin-bottom:14px;">
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: modalSettings.platformFeeMode === 'rentlio' }"
                    @click="modalSettings.platformFeeMode = 'rentlio'; markAsModified(); calculateMetrics()"
                  >Channel manager</button>
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: modalSettings.platformFeeMode === 'custom' }"
                    @click="modalSettings.platformFeeMode = 'custom'; markAsModified(); calculateMetrics()"
                  >Settings rate</button>
                </div>
                <p v-if="modalSettings.platformFeeMode === 'rentlio'" style="font-size:11px;color:#007a63;margin-bottom:0;">Commission pulled per booking — Genius tier rates and promotions applied automatically.</p>
                <div v-else>
                  <div class="platform-row" style="border-bottom-color:#f0eef8;">
                    <span class="platform-name">Booking.com</span>
                    <div style="display:flex;align-items:center;gap:5px;margin-left:auto;">
                      <input
                        type="number"
                        v-model="modalSettings.bookingComCommissionRate"
                        @input="modalSettings.applyCustomBookingCom = true; markAsModified(); calculateMetrics()"
                        step="0.01" min="0"
                        style="width:60px;border:1px solid var(--border);border-radius:var(--radius);padding:5px 7px;font-size:13px;font-family:inherit;outline:none;background:var(--bg);text-align:right;"
                      >
                      <span style="font-size:12px;color:var(--text-muted);">%</span>
                    </div>
                  </div>
                  <div class="platform-row" style="border-bottom-color:#f0eef8;">
                    <span class="platform-name">Airbnb</span>
                    <div style="display:flex;align-items:center;gap:5px;margin-left:auto;">
                      <input
                        type="number"
                        v-model="modalSettings.airbnbCommissionRate"
                        @input="modalSettings.applyCustomAirbnb = true; markAsModified(); calculateMetrics()"
                        step="0.01" min="0"
                        style="width:60px;border:1px solid var(--border);border-radius:var(--radius);padding:5px 7px;font-size:13px;font-family:inherit;outline:none;background:var(--bg);text-align:right;"
                      >
                      <span style="font-size:12px;color:var(--text-muted);">%</span>
                    </div>
                  </div>
                  <div class="platform-row" style="border-bottom-color:#f0eef8;">
                    <span class="platform-name" style="color:var(--text-muted);font-style:italic;">All others</span>
                    <div style="display:flex;align-items:center;gap:5px;margin-left:auto;">
                      <input
                        type="number"
                        v-model="modalSettings.otherPlatformCommissionRate"
                        @input="modalSettings.applyCustomOther = true; markAsModified(); calculateMetrics()"
                        step="0.01" min="0"
                        style="width:60px;border:1px solid var(--border);border-radius:var(--radius);padding:5px 7px;font-size:13px;font-family:inherit;outline:none;background:var(--bg);text-align:right;"
                      >
                      <span style="font-size:12px;color:var(--text-muted);">%</span>
                    </div>
                  </div>
                </div>

                <!-- Direct bookings row (W1 Refonte #10 — pinned at bottom) -->
                <div class="platform-row" style="border-bottom:none;margin-top:6px;">
                  <span class="platform-name" style="color:var(--text-muted);">Direct bookings</span>
                  <span class="source-badge" style="margin-left:auto;background:var(--bg);color:var(--text-muted);border:1px solid var(--border);padding:2px 8px;border-radius:20px;font-size:10px;">Set in Settings</span>
                </div>
              </div>

              <!-- Card fees -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:10px;">Card fees</p>
                <div style="display:flex;gap:6px;margin-bottom:12px;">
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: modalSettings.cardFeeMode === 'rentlio' }"
                    @click="modalSettings.cardFeeMode = 'rentlio'; markAsModified(); calculateMetrics()"
                  >Channel manager</button>
                  <button
                    type="button"
                    class="source-selector"
                    :class="{ active: modalSettings.cardFeeMode === 'custom' }"
                    @click="modalSettings.cardFeeMode = 'custom'; markAsModified(); calculateMetrics()"
                  >Settings rate</button>
                </div>
                <div v-if="modalSettings.cardFeeMode === 'custom'">
                  <p class="field-label">Rate</p>
                  <div class="input-wrap" style="max-width:120px;margin-bottom:8px;">
                    <input
                      type="number"
                      v-model="modalSettings.customCardFeeRate"
                      @input="handleNumericInput('customCardFeeRate', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customCardFeeRate', 0)"
                      step="0.01" min="0" max="10"
                    >
                    <span>%</span>
                  </div>
                  <p class="field-label">Fixed fee per transaction</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.customCardFeeFixed"
                      @input="handleNumericInput('customCardFeeFixed', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customCardFeeFixed', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                </div>
                <p v-else style="font-size:11px;color:var(--text-muted);margin-top:8px;">Card processing fees pulled from your channel manager per booking.</p>
              </div>

              <!-- Cleaning -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:12px;">Cleaning</p>
                <div>
                  <div class="radio-opt" :class="{ active: modalSettings.cleaningMode === 'rentlio' }" @click="modalSettings.cleaningMode = 'rentlio'; markAsModified(); calculateMetrics()">
                    <div class="radio-dot"></div><span>Use channel manager</span>
                  </div>
                  <div class="radio-opt" :class="{ active: modalSettings.cleaningMode === 'fixed' }" @click="modalSettings.cleaningMode = 'fixed'; markAsModified(); calculateMetrics()">
                    <div class="radio-dot"></div><span>Fixed monthly amount</span>
                  </div>
                  <div class="radio-opt" :class="{ active: modalSettings.cleaningMode === 'per_booking' }" @click="modalSettings.cleaningMode = 'per_booking'; markAsModified(); calculateMetrics()">
                    <div class="radio-dot"></div><span>Per changeover</span>
                  </div>
                </div>
                <div v-if="modalSettings.cleaningMode === 'fixed'" style="margin-top:10px;">
                  <p class="field-label">Monthly amount</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.cleaningFee"
                      @input="handleNumericInput('cleaningFee', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('cleaningFee', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                </div>
                <div v-if="modalSettings.cleaningMode === 'per_booking'" style="margin-top:10px;">
                  <p class="field-label">Cost per changeover</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.cleaningPerBooking"
                      @input="handleNumericInput('cleaningPerBooking', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('cleaningPerBooking', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                  <p class="field-label" style="margin-top:8px;">Number of changeovers</p>
                  <div style="display:flex;align-items:center;gap:6px;">
                    <button type="button" @click="decrementCleaningBookings" :disabled="cleaningBookingCount <= 0" style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);width:28px;height:28px;font-size:14px;color:var(--text);cursor:pointer;font-family:inherit;">−</button>
                    <input
                      type="number"
                      v-model.number="cleaningBookingCount"
                      @input="onCleaningBookingCountChange"
                      min="0"
                      style="width:60px;text-align:center;background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);padding:5px 8px;font-size:13px;font-family:inherit;outline:none;"
                    >
                    <button type="button" @click="incrementCleaningBookings" style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);width:28px;height:28px;font-size:14px;color:var(--text);cursor:pointer;font-family:inherit;">+</button>
                    <span style="font-size:11px;color:var(--text-muted);margin-left:6px;">auto: {{ autoDetectedBookingCount }}</span>
                  </div>
                </div>
                <div class="vat-toggle-row">
                  <span class="vat-toggle-label">VAT on cleaning</span>
                  <div class="vat-toggle-pill" :class="{ on: modalSettings.cleaningVatApplicable }" style="cursor:default;">
                    <div class="vat-dot"></div>{{ modalSettings.cleaningVatApplicable ? 'VAT applied' : 'No VAT' }}
                  </div>
                </div>
                <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">VAT treatment is set in Settings and applied here automatically.</p>
              </div>

              <!-- Laundry & linen supply -->
              <div class="section">
                <p class="section-title" style="color:#854F0B;margin-bottom:12px;">Laundry &amp; linen supply</p>
                <div>
                  <div class="radio-opt" :class="{ active: modalSettings.laundryCalculationMode === 'fixed' }" @click="modalSettings.laundryCalculationMode = 'fixed'; markAsModified(); calculateMetrics()">
                    <div class="radio-dot"></div><span>Fixed monthly amount</span>
                  </div>
                  <div class="radio-opt" :class="{ active: modalSettings.laundryCalculationMode === 'per_booking' }" @click="modalSettings.laundryCalculationMode = 'per_booking'; markAsModified(); calculateMetrics()">
                    <div class="radio-dot"></div><span>Per changeover</span>
                  </div>
                </div>
                <div v-if="modalSettings.laundryCalculationMode === 'fixed'" style="margin-top:10px;">
                  <p class="field-label">Monthly amount</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.laundry"
                      @input="handleNumericInput('laundry', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('laundry', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                </div>
                <div v-if="modalSettings.laundryCalculationMode === 'per_booking'" style="margin-top:10px;">
                  <p class="field-label">Cost per changeover</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.laundryPerBooking"
                      @input="handleNumericInput('laundryPerBooking', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('laundryPerBooking', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                  <p class="field-label" style="margin-top:8px;">Number of changeovers</p>
                  <div style="display:flex;align-items:center;gap:6px;">
                    <button type="button" @click="decrementLaundryBookings" :disabled="laundryBookingCount <= 0" style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);width:28px;height:28px;font-size:14px;color:var(--text);cursor:pointer;font-family:inherit;">−</button>
                    <input
                      type="number"
                      v-model.number="laundryBookingCount"
                      @input="onLaundryBookingCountChange"
                      min="0"
                      style="width:60px;text-align:center;background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);padding:5px 8px;font-size:13px;font-family:inherit;outline:none;"
                    >
                    <button type="button" @click="incrementLaundryBookings" style="background:var(--bg);border:1px solid var(--border);border-radius:var(--radius);width:28px;height:28px;font-size:14px;color:var(--text);cursor:pointer;font-family:inherit;">+</button>
                    <span style="font-size:11px;color:var(--text-muted);margin-left:6px;">auto: {{ autoDetectedBookingCount }}</span>
                  </div>
                </div>
                <div class="vat-toggle-row">
                  <span class="vat-toggle-label">VAT on laundry</span>
                  <div class="vat-toggle-pill" :class="{ on: modalSettings.laundryVatApplicable }" style="cursor:default;">
                    <div class="vat-dot"></div>{{ modalSettings.laundryVatApplicable ? 'VAT applied' : 'No VAT' }}
                  </div>
                </div>
                <p style="font-size:11px;color:var(--text-muted);margin-top:5px;line-height:1.4;">VAT treatment is set in Settings and applied here automatically.</p>
              </div>

              <!-- Consumables -->
              <div class="section">
                <div class="cons-toggle-row">
                  <div>
                    <p class="section-title" style="margin-bottom:2px;color:#854F0B;">Consumables</p>
                    <p style="font-size:11px;color:var(--text-muted);">Override the monthly consumables amount for this month.</p>
                  </div>
                  <label class="toggle">
                    <input type="checkbox" v-model="modalSettings.useCustomConsumables" @change="markAsModified(); calculateMetrics()">
                    <div class="toggle-track"></div>
                    <div class="toggle-thumb"></div>
                  </label>
                </div>
                <div v-if="modalSettings.useCustomConsumables" style="margin-top:12px;">
                  <p class="field-label">Monthly amount</p>
                  <div class="input-wrap" style="max-width:120px;">
                    <span>£</span>
                    <input
                      type="number"
                      v-model="modalSettings.customConsumablesAmount"
                      @input="handleNumericInput('customConsumablesAmount', $event); markAsModified(); calculateMetrics()"
                      @blur="ensureMinimumValue('customConsumablesAmount', 0)"
                      step="0.01" min="0"
                    >
                  </div>
                  <p style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">For one-off or receipt-based consumable costs add them in Expenses below.</p>
                </div>
                <p v-else style="font-size:11px;color:var(--text-muted);margin-top:6px;line-height:1.4;">Using default amount (£{{ modalSettings.consumables }}) from property settings.</p>
              </div>

              <!-- Expenses (Maintenance + Cleaning + Other — guest extras shown above in Income) -->
              <div class="section">
                <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;">
                  <p class="section-title" style="margin-bottom:0;color:#854F0B;">Expenses</p>
                  <button class="add-btn blue" type="button" @click="openExpenseModal">+ Add expense</button>
                </div>
                <p style="font-size:11px;color:var(--text-muted);margin-bottom:10px;">Maintenance, guest extras and other costs — set the category per item.</p>
                <p v-if="nonGuestExtraExpenses.length === 0" class="empty-note">No items added yet.</p>
                <div v-else>
                  <div v-for="expense in nonGuestExtraExpenses" :key="expense.id" class="expense-item">
                    <div style="flex:1;display:flex;flex-direction:column;gap:3px;min-width:0;">
                      <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                        <span class="name">{{ expense.description }}</span>
                        <span class="expense-cat-badge" :class="expense.expense_category || 'maintenance'">{{ expenseCategoryLabel(expense.expense_category) }}</span>
                      </div>
                      <div v-if="(expense.files && expense.files.length) || expense.receipt_url" style="display:flex;flex-wrap:wrap;gap:4px;">
                        <a v-if="expense.receipt_url" :href="expense.receipt_url" target="_blank" rel="noopener" class="expense-file-chip">📎 {{ expense.receipt_filename || 'receipt' }}</a>
                        <a v-for="f in (expense.files || [])" :key="f.id" :href="f.url" target="_blank" rel="noopener" class="expense-file-chip">📎 {{ f.filename }}</a>
                      </div>
                    </div>
                    <span class="amt">£{{ parseFloat(expense.amount).toFixed(2) }}</span>
                    <button type="button" @click="deleteExpense(expense.id)" aria-label="Remove">×</button>
                  </div>
                </div>
                <div v-if="nonGuestExtraExpenses.length" class="total-row total-neutral">
                  <span>Total expenses</span>
                  <strong>£{{ totalNonGuestExtraExpenses.toFixed(2) }}</strong>
                </div>
              </div>

              <div class="footer-btns">
                <button type="button" class="btn-reset" @click="resetToSaved" :disabled="!hasUnsavedChanges">Reset to saved</button>
                <button type="button" class="btn-save" @click="saveSettings" :disabled="!hasUnsavedChanges || savingSettings">
                  <span v-if="savingSettings">Saving…</span>
                  <span v-else>Save settings</span>
                </button>
              </div>
              <div v-if="modalSettings.managementFeeBase === 'grossFixed' && hasFixedAmountChanged" style="padding:0 10px 8px;">
                <button type="button" class="btn-save-this-month" @click="saveForThisMonthOnly" :disabled="!hasFixedAmountChanged || savingSettings">
                  <span v-if="savingSettings">Saving…</span>
                  <span v-else>Save for this month only</span>
                </button>
              </div>

            </div>
          </div>

          <!-- Report Preview (Right Side) -->
          <div class="preview-panel">
            <div class="preview-header">
              <div class="preview-actions">
                <div class="test-send-group">
                  <div class="test-input-wrap">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    <input
                      v-model="testEmail"
                      type="email"
                      placeholder="Send test to..."
                      class="test-email-input"
                      :disabled="testSending"
                    />
                  </div>
                  <button
                    @click="testSendReport"
                    :disabled="testSending || !testEmail"
                    class="btn btn-test-send"
                    title="Send a test email of this report (no lock, no save)"
                  >
                    <template v-if="testSending">Sending…</template>
                    <template v-else>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                      Send test
                    </template>
                  </button>
                </div>
                <button @click="downloadPDF" :disabled="generatingPDF" class="btn btn-pdf">
                  <template v-if="generatingPDF">Generating…</template>
                  <template v-else>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download PDF
                  </template>
                </button>
                <button @click="sendReportByEmailFromModal" class="btn btn-lock-report">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Approve Report
                </button>
              </div>
            </div>

<div class="preview-content" style="background: #ffffff; padding: 10px; overflow-y: auto; overflow-x: hidden;">
  <div class="pdf-preview-wrapper" style="
    width: 210mm; 
    min-height: 297mm; 
    background: white; 
    margin: 0 auto; 
    box-shadow: 0 4px 20px rgba(0,0,0,0.15);
    transform-origin: top center;
    transform: scale(1.3);
    border: 1px solid #e0e0e0;
  ">
    <div v-html="previewHTML" style="width: 100%; height: 100%;"></div>
  </div>
</div>
</div>
        </div>
      </div>
    </div>

    <!-- Professional Contacts Management Modal -->
    <div v-if="showContactsModal" class="contacts-modal-overlay" @click="closeContactsModal">
      <div class="contacts-modal-container" @click.stop>
        <!-- Header -->
        <div class="contacts-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <div class="icon-circle">
                <span class="contacts-icon">👥</span>
              </div>
            </div>
            <div class="header-text">
              <h2>Email Contacts</h2>
              <p>Manage your email recipients for reports</p>
            </div>
          </div>
          <button @click="closeContactsModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="contacts-modal-body">
          <!-- Add New Contact Section -->
          <div class="add-contact-card">
            <div class="card-header">
              <h3>➕ Add New Contact</h3>
              <p>Create a new email contact for sending reports</p>
            </div>
            <div class="card-content">
              <div class="add-contact-form">
                <div class="input-group">
                  <div class="input-container">
                    <label class="input-label">👤 Contact Name</label>
                    <input 
                      v-model="newContact.name" 
                      placeholder="Enter full name..." 
                      class="professional-input"
                      type="text"
                    >
                  </div>
                  <div class="input-container">
                    <label class="input-label">📧 Email Address</label>
                    <input 
                      v-model="newContact.email" 
                      placeholder="Enter email address..." 
                      class="professional-input"
                      type="email"
                    >
                  </div>
                </div>
                <button 
                  @click="addContact" 
                  :disabled="!newContact.name || !newContact.email"
                  class="add-contact-btn"
                >
                  <span class="btn-icon">✨</span>
                  <span>Add Contact</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Contacts List Section -->
          <div class="contacts-list-card">
            <div class="card-header">
              <div class="header-with-count">
                <h3>📋 Saved Contacts</h3>
                <span class="contact-count">{{ contacts.length }} contact{{ contacts.length !== 1 ? 's' : '' }}</span>
              </div>
            </div>
            
            <div class="card-content">
              <!-- Empty State -->
              <div v-if="contacts.length === 0" class="empty-contacts-state">
                <div class="empty-icon">📭</div>
                <h4>No contacts yet</h4>
                <p>Add your first email contact above to start sending reports</p>
              </div>
              
              <!-- Contacts Grid -->
              <div v-else class="contacts-grid">
                <div 
                  v-for="contact in contacts" 
                  :key="contact.id" 
                  class="contact-item"
                  :class="{ 'editing': editingContact?.id === contact.id }"
                >
                  <!-- Contact Avatar -->
                  <div class="contact-avatar">
                    {{ contact.name.charAt(0).toUpperCase() }}
                  </div>
                  
                  <!-- Contact Info / Edit Form -->
                  <div class="contact-details">
                    <div v-if="editingContact?.id === contact.id" class="edit-form">
                      <input 
                        v-model="editingContact.name" 
                        class="edit-input"
                        placeholder="Contact name..."
                      >
                      <input 
                        v-model="editingContact.email" 
                        class="edit-input"
                        type="email"
                        placeholder="Email address..."
                      >
                    </div>
                    <div v-else class="contact-info">
                      <div class="contact-name">{{ contact.name }}</div>
                      <div class="contact-email">{{ contact.email }}</div>
                      <div class="contact-meta">
                        Added {{ formatContactDate(contact.createdAt) }}
                      </div>
                    </div>
                  </div>
                  
                  <!-- Actions -->
                  <div class="contact-actions">
                    <div v-if="editingContact?.id === contact.id" class="edit-actions">
                      <button @click="saveEditContact" class="action-btn save-btn">
                        <span>💾</span>
                      </button>
                      <button @click="editingContact = null" class="action-btn cancel-btn">
                        <span>❌</span>
                      </button>
                    </div>
                    <div v-else class="view-actions">
                      <button @click="editContact(contact)" class="action-btn edit-btn">
                        <span>✏️</span>
                      </button>
                      <button @click="deleteContact(contact.id)" class="action-btn delete-btn">
                        <span>🗑️</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="contacts-modal-footer">
          <div class="footer-info">
            <span class="info-text">
              <span class="info-icon">💾</span>
              Contacts are saved locally in your browser
            </span>
          </div>
          <div class="footer-actions">
            <button @click="closeContactsModal" class="close-modal-btn">
              <span class="btn-icon">✓</span>
              <span>Done</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Professional Email Sending Modal -->
    <div v-if="showEmailModal" class="email-modal-overlay" @click="closeEmailModal">
      <div class="email-modal-container" @click.stop>
        <!-- Header -->
        <div class="email-modal-header">
          <div class="header-content">
            <div class="header-icon">
              <div class="icon-circle">
                <span class="email-icon">📧</span>
              </div>
            </div>
            <div class="header-text">
              <h2>Send Report via Email</h2>
              <p>Share your property report with stakeholders</p>
            </div>
          </div>
          <button @click="closeEmailModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="email-modal-body">
          <!-- Report Preview Card -->
          <div class="report-preview-card">
            <div class="card-header">
              <h3>📊 Report Summary</h3>
            </div>
            <div class="card-content">
              <div class="report-details">
                <div class="detail-item">
                  <div class="detail-icon">🏨</div>
                  <div class="detail-content">
                    <span class="detail-label">Property</span>
                    <span class="detail-value">{{ emailData.propertyName }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">📅</div>
                  <div class="detail-content">
                    <span class="detail-label">Period</span>
                    <span class="detail-value">{{ emailData.period }}</span>
                  </div>
                </div>
                <div class="detail-item">
                  <div class="detail-icon">📋</div>
                  <div class="detail-content">
                    <span class="detail-label">Report Type</span>
                    <span class="detail-value">{{ emailData.reportType }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Recipient Selection -->
          <div class="recipient-section">
            <div class="section-title">
              <h3>👤 Select Recipient</h3>
              <p>Choose who will receive this report</p>
            </div>
            
            <div class="recipient-selector">
              <div class="contact-dropdown">
                <select v-model="emailData.contact" class="professional-select">
                  <option :value="null" disabled>Choose a contact...</option>
                  <option v-for="contact in contacts" :key="contact.id" :value="contact">
                    {{ contact.name }} • {{ contact.email }}
                  </option>
                </select>
              </div>
              
              <button @click="openContactsModal" class="manage-contacts-btn">
                <span class="btn-icon">👥</span>
                <span>Manage Contacts</span>
              </button>
            </div>

            <!-- Selected Contact Preview -->
            <div v-if="emailData.contact" class="selected-contact-preview">
              <div class="contact-card">
                <div class="contact-avatar">
                  {{ emailData.contact.name.charAt(0).toUpperCase() }}
                </div>
                <div class="contact-info">
                  <div class="contact-name">{{ emailData.contact.name }}</div>
                  <div class="contact-email">{{ emailData.contact.email }}</div>
                </div>
                <div class="contact-status">
                  <span class="status-badge">✓ Selected</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Email Options -->
          <div class="email-options-section">
            <div class="section-title">
              <h3>⚙️ Email Options</h3>
            </div>
            
            <div class="options-grid">
              <div class="option-card">
                <div class="option-header">
                  <span class="option-icon">📎</span>
                  <span class="option-title">Include Attachments</span>
                </div>
                <div class="option-content">
                  <label class="checkbox-container">
                    <input type="checkbox" v-model="emailOptions.includeExcel" checked>
                    <span class="checkmark"></span>
                    Excel Report
                  </label>
                  <label class="checkbox-container">
                    <input type="checkbox" v-model="emailOptions.includePDF">
                    <span class="checkmark"></span>
                    PDF Report
                  </label>
                </div>
              </div>

              <div class="option-card">
                <div class="option-header">
                  <span class="option-icon">💬</span>
                  <span class="option-title">Message</span>
                </div>
                <div class="option-content">
                  <textarea 
                    v-model="emailOptions.message" 
                    class="message-textarea"
                    placeholder="Add a personal message (optional)..."
                    rows="3"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="email-modal-footer">
          <div class="footer-info">
            <span class="info-text">
              <span class="info-icon">🔒</span>
              Your data is sent securely
            </span>
          </div>
          <div class="footer-actions">
            <button @click="closeEmailModal" class="cancel-btn">
              Cancel
            </button>
            <button 
              @click="sendReportByEmail" 
              :disabled="!emailData.contact || sendingEmail"
              :class="['send-btn', { 'disabled': !emailData.contact || sendingEmail }]"
            >
              <span v-if="sendingEmail">📧 Sending...</span>
              <span v-else>Send Report</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Sent Popup -->
    <div v-if="showEmailSentPopup" class="email-sent-popup-overlay">
      <div class="email-sent-popup">
        <div class="popup-icon">
          <div class="success-checkmark">
            <div class="check-icon">
              <span class="icon-line line-tip"></span>
              <span class="icon-line line-long"></span>
              <div class="icon-circle"></div>
              <div class="icon-fix"></div>
            </div>
          </div>
        </div>

        <div class="popup-content">
          <h2>Email Sent Successfully!</h2>
          <p>Your report has been sent to <strong>{{ emailData.contact?.name }}</strong></p>
          <p class="delivery-info">The email will arrive in the recipient's inbox within the next 5 minutes.</p>
        </div>

        <button @click="closeEmailSentPopup" class="popup-close-btn">
          Got it
        </button>
      </div>
    </div>

    <!-- Lock Report Modal (Milestone 2 - Xero Integration) -->
    <div v-if="showLockModal" class="lock-modal-overlay" @click="closeLockModal">
      <div class="lock-modal-container" @click.stop>
        <!-- Header -->
        <div class="lock-modal-header">
          <div class="header-icon">
            <div class="icon-circle">
              <span>✓</span>
            </div>
          </div>
          <div class="header-text">
            <h2>Approve Report for Month-End</h2>
            <p>Prepare this report for sending via the Month-End screen</p>
          </div>
          <button @click="closeLockModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="lock-modal-body">
          <!-- Report Summary Card -->
          <div class="report-summary-card">
            <div class="summary-row">
              <span class="summary-label">🏨 Property:</span>
              <span class="summary-value">{{ lockModalData.propertyName }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">📅 Period:</span>
              <span class="summary-value">{{ lockModalData.monthYear }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">👤 Owner:</span>
              <span class="summary-value">{{ lockModalData.ownerName }}</span>
            </div>
            <div class="summary-row">
              <span class="summary-label">📧 Email:</span>
              <span class="summary-value email-highlight">{{ lockModalData.ownerEmail }}</span>
            </div>
          </div>

          <!-- Important Notice -->
          <div class="warning-box">
            <div class="warning-icon">⚠️</div>
            <div class="warning-content">
              <h3>Important:</h3>
              <ul>
                <li>This report will be <strong>approved</strong> and cannot be regenerated</li>
                <li>It will appear in the <strong>Month-End screen</strong> as "Ready to send"</li>
                <li>The email will be sent to <strong>{{ lockModalData.ownerEmail }}</strong></li>
                <li>To change this email, go to <strong>Property Settings → Owner Information</strong></li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="lock-modal-footer">
          <button @click="closeLockModal" class="cancel-btn">
            Cancel
          </button>
          <button @click="confirmLockReport" class="confirm-btn">
            ✓ Confirm & Approve Report
          </button>
        </div>
      </div>
    </div>

    <!-- Notification Modal (replaces alerts) -->
    <div v-if="showNotificationModal" class="notification-modal-overlay" @click="closeNotificationModal">
      <div class="notification-modal-container" @click.stop>
        <!-- Header with Icon -->
        <div class="notification-modal-header" :class="notificationData.type">
          <div class="notification-icon-circle" :class="notificationData.type">
            <span class="notification-icon">{{ notificationData.icon }}</span>
          </div>
          <button @click="closeNotificationModal" class="modal-close-btn">
            <span>&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="notification-modal-body">
          <h2 class="notification-title">{{ notificationData.title }}</h2>
          <p class="notification-message" v-html="notificationData.message"></p>

          <!-- Details (optional) -->
          <div v-if="notificationData.details" class="notification-details">
            <div v-for="(value, key) in notificationData.details" :key="key" class="detail-row">
              <span class="detail-label">{{ key }}:</span>
              <span class="detail-value">{{ value }}</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="notification-modal-footer">
          <button @click="closeNotificationModal" class="notification-btn" :class="notificationData.type">
            OK
          </button>
        </div>
      </div>
    </div>

    <!-- Expense Modal -->
    <div v-if="showExpenseModal" class="expense-modal-overlay" @click="closeExpenseModal">
      <div class="expense-modal" @click.stop>
        <div class="expense-modal-header">
          <h3>Add expense</h3>
          <button @click="closeExpenseModal" class="close-btn">×</button>
        </div>

        <div class="expense-modal-body">
          <div class="form-group">
            <label class="form-label">Description *</label>
            <input
              v-model="newExpense.description"
              type="text"
              class="form-input"
              placeholder="e.g. Plumber repair"
              maxlength="255"
            >
          </div>

          <!-- VAT Treatment Toggle -->
          <div class="form-group">
            <label class="form-label">VAT Treatment</label>
            <div class="vat-toggle-group">
              <label class="vat-toggle-option" :class="{ active: !newExpense.is_vat_applicable }">
                <input type="radio" :value="false" v-model="newExpense.is_vat_applicable" style="display:none;">
                No VAT &mdash; Agent / Disbursement
              </label>
              <label class="vat-toggle-option" :class="{ active: newExpense.is_vat_applicable }">
                <input type="radio" :value="true" v-model="newExpense.is_vat_applicable" style="display:none;">
                VAT Applicable &mdash; Supplied Service
              </label>
            </div>
            <p class="form-hint">Select "VAT Applicable" if this cost is supplied by your company and VAT must be charged to the owner.</p>
          </div>

          <!-- Amount entry (gross si pas de VAT ou gross avec VAT incluse) -->
          <div class="form-group" v-if="!newExpense.is_vat_applicable || newExpense.vat_treatment === 'gross'">
            <label class="form-label">Amount (£) *</label>
            <div class="input-group">
              <span class="input-prefix">£</span>
              <input
                v-model="newExpense.amount"
                type="number"
                class="form-input"
                placeholder="0.00"
                step="0.01"
                min="0"
              >
            </div>
            <p class="form-hint" v-if="newExpense.is_vat_applicable && newExpense.vat_treatment === 'gross'">Gross amount including VAT — the system will extract the net and VAT automatically.</p>
          </div>

          <!-- Method selector si VAT applicable -->
          <div class="form-group" v-if="newExpense.is_vat_applicable">
            <label class="form-label">Amount Entry Method</label>
            <select v-model="newExpense.vat_treatment" class="form-input">
              <option value="gross">Gross Amount (VAT included)</option>
              <option value="net_plus_vat">Net + VAT Separated</option>
            </select>
          </div>

          <!-- Net + VAT séparés -->
          <div v-if="newExpense.is_vat_applicable && newExpense.vat_treatment === 'net_plus_vat'" class="form-row-two">
            <div class="form-group">
              <label class="form-label">Net Amount (£) *</label>
              <div class="input-group">
                <span class="input-prefix">£</span>
                <input
                  v-model="newExpense.net_amount"
                  type="number"
                  class="form-input"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                >
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">VAT Amount (£) *</label>
              <div class="input-group">
                <span class="input-prefix">£</span>
                <input
                  v-model="newExpense.vat_amount"
                  type="number"
                  class="form-input"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                >
              </div>
            </div>
          </div>

          <!-- Expense Category -->
          <div class="form-group">
            <label class="form-label">Expense Category *</label>
            <select v-model="newExpense.expense_category" class="form-input">
              <option value="maintenance">Maintenance</option>
              <option value="guest_extra">Guest extra</option>
              <option value="cleaning">Cleaning</option>
              <option value="other">Other</option>
            </select>
            <p class="form-hint">
              "Guest extra" = revenue charged to the guest. "Cleaning" = no VAT (agent disbursement). "Maintenance" or "Other" = VAT applies if the company supplies the service.
            </p>
          </div>

          <div class="form-group">
            <label class="form-label">Receipts / files (optional, multiple allowed)</label>
            <input
              @change="handleReceiptUpload"
              type="file"
              class="form-input-file"
              accept="image/*,application/pdf"
              ref="receiptFileInput"
              multiple
            >
            <div v-if="newExpense.pending_files && newExpense.pending_files.length" class="file-selected-list">
              <div v-for="(f, idx) in newExpense.pending_files" :key="idx" class="file-selected-row">
                <span>📎 {{ f.name }}</span>
                <button type="button" class="btn-delete-mini" @click="removePendingFile(idx)" title="Remove">×</button>
              </div>
            </div>
            <p class="form-hint">Add one or more invoices/receipts for the same expense. Useful for maintenance where multiple jobs add up over the month.</p>
          </div>
        </div>

        <div class="expense-modal-footer">
          <button @click="closeExpenseModal" class="btn btn-outline">Cancel</button>
          <button
            @click="saveExpense"
            :disabled="!newExpense.description || (!newExpense.amount && !(newExpense.is_vat_applicable && newExpense.vat_treatment === 'net_plus_vat' && newExpense.net_amount)) || savingExpense"
            class="btn btn-primary"
          >
            <span v-if="savingExpense">Saving...</span>
            <span v-else>Add Expense</span>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import * as XLSX from 'xlsx';
import SETTINGS from '@/config/settings.js';
import formulas from '@/config/formulas.js';
import Sidebar from '@/components/Sidebar.vue';
import Topbar from '@/components/Topbar.vue';
import propertyGroups from '@/services/propertyGroups.js';

export default {
  name: 'ReservationsView',
  components: {
    Sidebar,
    Topbar
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el._clickOutside = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el._clickOutside);
      },
      unmounted(el) {
        document.removeEventListener('click', el._clickOutside);
      },
    },
  },
  data() {
    return {
      // Données existantes
      isCollapsed: false,
      loading: false,
      loadingLastMonth: false,
      loadingHistory: false,
      currentMonthReservations: [],
      lastMonthReservations: [],
      historyReservations: [],
      properties: [],
      groups: [],
      activeMonth: 'current',
      selectedCurrentProperty: '',
      selectedLastMonthProperty: '',
      currentGroupBookingsByProperty: {},
      lastMonthGroupBookingsByProperty: {},
      currentDropdownOpen: false,
      lastDropdownOpen: false,
      currentGroupLockStatus: null,
      lastGroupLockStatus: null,
      lockingGroup: false,
      showGroupReportModal: false,
      groupReportModalData: {
        period: null,
        group: null,
        monthLabel: '',
        pdfUrl: null,
        previewHtml: '',
        generating: false,
        membersData: {},
      },
      groupPreviewLoading: false,
      groupPreviewDebounceTimer: null,
      groupDownloadingPDF: false,
      groupModalSettings: {
        sharedCleaningMode: 'per_booking',
        sharedLaundryMode: 'per_booking',
        sharedCleaningFee: 0,
        sharedLaundryFee: 0,
        // Group-level fee overrides (mirror of modalSettings for single).
        managementFeeRate: 16,
        managementFeeBase: 'net',
        managementFeeFixedAmount: 500,
        platformFeeMode: 'rentlio',
        applyCustomBookingCom: false,
        bookingComCommissionRate: 15,
        applyCustomAirbnb: false,
        airbnbCommissionRate: 3,
        applyCustomOther: false,
        otherPlatformCommissionRate: 10,
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.3,
        consumables: 20,
        useCustomConsumables: false,
        customConsumablesAmount: 0,
      },
      groupExpenses: [],
      groupSettingsModified: false,
      savingGroupSettings: false,
      // Scope for the shared expense modal: 'property' (default) or 'group'.
      expenseScope: 'property',
      selectedProperty: '',
      selectedMonth: '',
      appliedFilters: {
        property: '',
        month: ''
      },
      searchResultsMonth: '', // Store the month that searchResults correspond to
      searchResults: [],
      currentPage: 1,
      itemsPerPage: 20,
      baseUrl: SETTINGS.API.baseUrl,

      // Modal state
      showReportModal: false,
      modalData: {
        propertyName: '',
        periodLabel: '',
        reservations: [],
        realPropertyId: null // AJOUTER cette ligne

      },
      modalSettings: {
        managementFeeRate: 16,
        managementFeeBase: 'net',
        managementFeeFixedAmount: 500,
        vatRate: 20,
        vatBusinessModel: 'agent',
        maintenance: 0,
        useCustomMaintenance: false,
        customMaintenanceAmount: 0,
        consumables: 20,
        useCustomConsumables: false,
        customConsumablesAmount: 0,
        laundry: 0,
        laundryCalculationMode: 'fixed',
        laundryPerBooking: 15,
        laundryVatApplicable: true,
        otherExpenses: 0,
        useCustomOtherExpenses: false,
        customOtherExpensesAmount: 0,
        cleaningMode: 'rentlio',
        bookingCleaningFee: 150,
        otherCleaningFee: 120,
        cleaningFee: 0,
        cleaningPerBooking: 150,
        cleaningVatApplicable: false,
        cardFeeMode: 'rentlio',
        customCardFeeRate: 2.9,
        customCardFeeFixed: 0.30,
        platformFeeMode: 'rentlio',
        applyCustomBookingCom: false,
        bookingComCommissionRate: 15.00,
        applyCustomAirbnb: false,
        airbnbCommissionRate: 3.00,
        applyCustomOther: false,
        otherPlatformCommissionRate: 10.00
      },
      originalSettings: {},
      allPropertySettings: {}, // Stocke tous les settings récupérés de l'API
      calculatedMetrics: {},
      previewHTML: '',
      testEmail: '',
      testSending: false,
      hasUnsavedChanges: false,
      savingSettings: false,
      generatingPDF: false,
      hasMonthSpecificFee: false, // Indique si le fixed fee actuel est spécifique au mois

      // Email contacts management
      contacts: [],
      showContactsModal: false,
      showEmailModal: false,
      showEmailSentPopup: false,

      // Lock Report Modal (Milestone 2 - Xero Integration)
      showLockModal: false,
      lockModalData: {
        propertyName: '',
        monthYear: '',
        ownerName: '',
        ownerEmail: '',
        propertyId: null
      },
      selectedContact: null,
      newContact: {
        name: '',
        email: ''
      },
      editingContact: null,
      emailData: {
        contact: null,
        reportType: '',
        propertyName: '',
        period: ''
      },
      emailOptions: {
        includeExcel: false,
        includePDF: true,
        message: ''
      },
      sendingEmail: false,
      savedReportData: null, // Sauvegarde des données du rapport pour l'envoi d'email

      // Notification Modal (replaces alerts)
      showNotificationModal: false,
      notificationData: {
        type: 'success', // 'success', 'error', 'warning'
        icon: '✅',
        title: '',
        message: '',
        details: null
      },

      // Property Expenses (Other Maintenance)
      propertyExpenses: [],
      showExpenseModal: false,
      newExpense: {
        description: '',
        amount: 0,
        expense_category: 'maintenance',
        vat_treatment: 'gross',
        net_amount: 0,
        vat_rate: 20,
        vat_amount: 0,
        is_vat_applicable: false,
        receipt_file: null,
        receipt_filename: ''
      },
      savingExpense: false,

      // Laundry per-booking counter
      laundryBookingCount: 0,
      autoDetectedBookingCount: 0,

      // Cleaning per-booking counter
      cleaningBookingCount: 0
    };
  },

 computed: {
  currentSelection() {
    return this.parseSelection(this.selectedCurrentProperty);
  },
  lastMonthSelection() {
    return this.parseSelection(this.selectedLastMonthProperty);
  },
  historySelection() {
    return this.parseSelection(this.selectedProperty);
  },
  currentGroup() {
    if (this.currentSelection.type !== 'group') return null;
    return this.groups.find(g => g.id === this.currentSelection.id) || null;
  },
  lastMonthGroup() {
    if (this.lastMonthSelection.type !== 'group') return null;
    return this.groups.find(g => g.id === this.lastMonthSelection.id) || null;
  },
  currentGroupSummary() {
    return this.buildGroupSummary(this.currentGroup, this.currentGroupBookingsByProperty);
  },
  lastMonthGroupSummary() {
    return this.buildGroupSummary(this.lastMonthGroup, this.lastMonthGroupBookingsByProperty);
  },
  groupedCurrentMonth() {
    const property = this.properties.find(p => p.id.toString() === this.selectedCurrentProperty.toString());
    const displayName = property ? property.name : 'Unknown Property';
    return { [displayName]: this.currentMonthReservations };
  },

  groupedLastMonth() {
    const property = this.properties.find(p => p.id.toString() === this.selectedLastMonthProperty.toString());
    const displayName = property ? property.name : 'Unknown Property';
    return { [displayName]: this.lastMonthReservations };
  },
  
  availableMonths() {
    const months = [];
    const now = new Date();

    // Ajouter les 12 derniers mois
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);

      // FIX: Utiliser getFullYear() et getMonth() au lieu de toISOString() pour éviter les problèmes de timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0'); // getMonth() est 0-indexed
      const value = `${year}-${month}`; // Format YYYY-MM

      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

      let type = 'historical';
      if (i === 0) type = 'current';
      else if (i === 1) type = 'lastmonth';

      months.push({
        value: value,
        label: label,
        type: type
      });
    }

    return months;
  },

  paginatedHistory() {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.searchResults.slice(start, end);
  },

  totalPages() {
    return Math.ceil(this.searchResults.length / this.itemsPerPage);
  },

  hasFixedAmountChanged() {
    // Vérifie si seul le fixed amount a changé (pas les autres paramètres)
    if (!this.hasUnsavedChanges || this.modalSettings.managementFeeBase !== 'grossFixed') {
      return false;
    }
    // Compare uniquement le fixed amount avec l'original
    return parseFloat(this.modalSettings.managementFeeFixedAmount) !== parseFloat(this.originalSettings.managementFeeFixedAmount);
  },

  totalAdditionalExpenses() {
    return this.propertyExpenses.reduce((sum, expense) => {
      return sum + parseFloat(expense.amount || 0);
    }, 0);
  },

  // W1 Refonte #10 — Guest extras (Income) section
  guestExtras() {
    return this.propertyExpenses.filter(e => e.expense_category === 'guest_extra');
  },

  nonGuestExtraExpenses() {
    return this.propertyExpenses.filter(e => e.expense_category !== 'guest_extra');
  },

  totalGuestExtras() {
    return this.guestExtras.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  },

  totalNonGuestExtraExpenses() {
    return this.nonGuestExtraExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  },

  groupGuestExtras() {
    return this.groupExpenses.filter(e => e.expense_category === 'guest_extra');
  },
  nonGuestExtraGroupExpenses() {
    return this.groupExpenses.filter(e => e.expense_category !== 'guest_extra');
  },
  totalGroupGuestExtras() {
    return this.groupGuestExtras.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  },
  totalNonGuestExtraGroupExpenses() {
    return this.nonGuestExtraGroupExpenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  },
  totalGroupExpenses() {
    return this.groupExpenses.reduce((sum, expense) => {
      return sum + parseFloat(expense.amount || 0);
    }, 0);
  },

  calculatedVatAmount() {
    if (this.newExpense.vat_treatment === 'net_plus_vat') {
      const netAmount = parseFloat(this.newExpense.net_amount) || 0;
      const vatRate = parseFloat(this.newExpense.vat_rate) || 0;
      return (netAmount * (vatRate / 100)).toFixed(2);
    }
    return '0.00';
  },

  calculatedTotalAmount() {
    if (this.newExpense.vat_treatment === 'net_plus_vat') {
      const netAmount = parseFloat(this.newExpense.net_amount) || 0;
      const vatAmount = parseFloat(this.calculatedVatAmount) || 0;
      return (netAmount + vatAmount).toFixed(2);
    }
    return '0.00';
  }
},

  methods: {
    // Méthodes existantes
    toggleSidebar() {
      this.isCollapsed = !this.isCollapsed;
    },
    getSegmentTooltip(reservation) {
  if (!reservation.isSegment || !reservation.segmentInfo) {
    return '';
  }
  
  const info = reservation.segmentInfo;
  return `Multi-month reservation split - Part ${info.part} of ${info.total}\n` +
         `Original reservation: ${info.originalTotalNights} nights, £${info.originalTotalPrice}\n` +
         `Original ID: ${info.originalId}`;
},
    
    logout() {
      localStorage.removeItem('token');
      this.$router.push('/login');
    },
    
    formatCurrency(amount) {
      return SETTINGS.formatCurrency(amount || 0);
    },
    
    formatDate(timestamp) {
      return SETTINGS.formatDate(timestamp);
    },
    
    getCurrentMonth() {
      return new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },
    
    getLastMonth() {
      const lastMonth = new Date();
      lastMonth.setMonth(lastMonth.getMonth() - 1);
      return lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    },

    getCurrentDate() {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${days[now.getDay()]}, ${now.getDate()} ${months[now.getMonth()]} ${now.getFullYear()}`;
    },

    getCurrentMonthYear() {
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const now = new Date();
      return `${months[now.getMonth()]} ${now.getFullYear()}`;
    },


    getTotalNights(reservations) {
      return reservations.reduce((total, r) => total + r.totalNights, 0);
    },
    
    getTotalRevenue(reservations) {
      return reservations.reduce((total, r) => total + r.totalReservationPrice, 0);
    },

    getSelectedPropertyName() {
      if (!this.selectedProperty) {
        return null;
      }
      const property = this.properties.find(p => p.id.toString() === this.selectedProperty.toString());
      return property ? property.name : null;
    },

    getSelectedMonthLabel() {
      if (!this.selectedMonth) {
        return 'Historical Month';
      }
      const selectedMonthData = this.availableMonths.find(m => m.value === this.selectedMonth);
      return selectedMonthData ? selectedMonthData.label : 'Historical Month';
    },


    // Modal methods


    closeReportModal() {
      if (this.hasUnsavedChanges) {
        if (!confirm('You have unsaved changes. Are you sure you want to close?')) {
          return;
        }
      }

      this.showReportModal = false;
      this.modalData = {};
      this.modalSettings = {};
      this.originalSettings = {};
      this.calculatedMetrics = {};
      this.previewHTML = '';
      this.hasUnsavedChanges = false;
    },

    markAsModified() {
      this.hasUnsavedChanges = true;
    },

    // Laundry booking counter methods
    incrementLaundryBookings() {
      this.laundryBookingCount++;
      this.markAsModified();
      this.calculateMetrics();
    },

    decrementLaundryBookings() {
      if (this.laundryBookingCount > 0) {
        this.laundryBookingCount--;
        this.markAsModified();
        this.calculateMetrics();
      }
    },

    onLaundryBookingCountChange() {
      // Ensure it's a valid number
      if (isNaN(this.laundryBookingCount) || this.laundryBookingCount < 0) {
        this.laundryBookingCount = 0;
      }
      this.markAsModified();
      this.calculateMetrics();
    },

    // Cleaning booking counter methods
    incrementCleaningBookings() {
      this.cleaningBookingCount++;
      this.markAsModified();
      this.calculateMetrics();
    },

    decrementCleaningBookings() {
      if (this.cleaningBookingCount > 0) {
        this.cleaningBookingCount--;
        this.markAsModified();
        this.calculateMetrics();
      }
    },

    onCleaningBookingCountChange() {
      // Ensure it's a valid number
      if (isNaN(this.cleaningBookingCount) || this.cleaningBookingCount < 0) {
        this.cleaningBookingCount = 0;
      }
      this.markAsModified();
      this.calculateMetrics();
    },

    autoDetectBookingCount() {
      // Count changeovers (checkouts) inside the report month, excluding
      // extensions and counting split bookings only on their last segment.
      // Mirrors the monthly cleaning filter at calculateMetrics:2545-2552.
      const reservations = this.modalData.reservations || [];

      // Derive report month/year from periodLabel (same logic as calculateMetrics).
      let reportMonth, reportYear;
      const now = new Date();
      if (this.modalData.periodLabel === 'Current Month') {
        reportMonth = now.getMonth() + 1;
        reportYear = now.getFullYear();
      } else if (this.modalData.periodLabel === 'Last Month') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        reportMonth = lastMonth.getMonth() + 1;
        reportYear = lastMonth.getFullYear();
      } else {
        const monthMap = { 'January':1,'February':2,'March':3,'April':4,'May':5,'June':6,'July':7,'August':8,'September':9,'October':10,'November':11,'December':12 };
        const parts = (this.modalData.periodLabel || '').split(' ');
        reportMonth = monthMap[parts[0]] || (now.getMonth() + 1);
        reportYear = parseInt(parts[1]) || now.getFullYear();
      }

      const count = reservations.filter(r => {
        const guestName = (r.guestName || r.guest_name || '').toLowerCase();
        if (guestName.includes('extension')) return false;

        const isLastSegment = !(r.isSegment || r.is_segment) ||
          ((r.segmentInfo?.part === r.segmentInfo?.total) ||
           (r.segment_info?.part === r.segment_info?.total));
        if (!isLastSegment) return false;

        const depTimestamp = r.departureDate || r.departure_date;
        if (!depTimestamp) return false;
        const d = new Date(depTimestamp * 1000);
        return (d.getMonth() + 1) === reportMonth && d.getFullYear() === reportYear;
      }).length;

      this.autoDetectedBookingCount = count;
      this.laundryBookingCount = count;
      this.cleaningBookingCount = count;
    },

    calculateMetrics() {
      if (!this.modalData.reservations || this.modalData.reservations.length === 0) {
        return;
      }

      const reservations = this.modalData.reservations;
      const settings = this.modalSettings;

      // Determine report month/year for checkout-based cleaning calculation
      let reportMonth, reportYear;
      const now = new Date();
      if (this.modalData.periodLabel === 'Current Month') {
        reportMonth = now.getMonth() + 1;
        reportYear = now.getFullYear();
      } else if (this.modalData.periodLabel === 'Last Month') {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        reportMonth = lastMonth.getMonth() + 1;
        reportYear = lastMonth.getFullYear();
      } else {
        const monthMap = { 'January':1,'February':2,'March':3,'April':4,'May':5,'June':6,'July':7,'August':8,'September':9,'October':10,'November':11,'December':12 };
        const parts = (this.modalData.periodLabel || '').split(' ');
        reportMonth = monthMap[parts[0]] || now.getMonth() + 1;
        reportYear = parseInt(parts[1]) || now.getFullYear();
      }

      let totalRevenue = 0;
      let totalNights = 0;
      let totalPlatformFees = 0;
      let totalCardFees = 0;
      let totalOutgoings = 0;

      reservations.forEach(reservation => {
        const amount = parseFloat(reservation.totalReservationPrice || 0);
        const fees = this.calculateCustomCommission(reservation, settings);
        
        let cardFee = 0;
        if (settings.cardFeeMode === 'rentlio') {
          cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
        } else {
          cardFee = (reservation.otaChannelName === 'Booking.com') ? 
            (amount * settings.customCardFeeRate / 100) : 0;
        }
        
        let outgoings = 0;
        const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');

        if (isExtension) {
          outgoings = 0;
        } else {
          // Cleaning is counted in the month of CHECKOUT (departureDate), not checkin
          const depTimestamp = reservation.departureDate || reservation.departure_date || 0;
          const depDate = depTimestamp ? new Date(depTimestamp * 1000) : null;
          const checkoutInReportMonth = depDate && (depDate.getMonth() + 1) === reportMonth && depDate.getFullYear() === reportYear;

          if (settings.cleaningMode === 'rentlio') {
            outgoings = checkoutInReportMonth ? parseFloat(reservation.totalServicesPrice || 0) : 0;
          } else if (settings.cleaningMode === 'monthly') {
            // Custom Rates: Appliquer les frais uniquement au dernier segment ET si checkout dans le mois
            const isLastSegment = !reservation.isSegment ||
              (reservation.segmentInfo?.part === reservation.segmentInfo?.total);

            if (isLastSegment && checkoutInReportMonth) {
              const platform = reservation.otaChannelName || '';
              if (platform === 'Booking.com') {
                outgoings = settings.bookingCleaningFee;
              } else {
                outgoings = settings.otherCleaningFee;
              }
            } else {
              outgoings = 0;
            }
          } else if (settings.cleaningMode === 'fixed') {
            outgoings = 0;
          } else if (settings.cleaningMode === 'per_booking') {
            outgoings = 0;
          }
        }

        totalRevenue += amount;
       totalNights += parseInt(reservation.totalNights || 0);
       totalPlatformFees += fees;
       totalCardFees += cardFee;
       totalOutgoings += outgoings;
     });

     // Calculate cleaning as supplied service (like laundry) when in fixed/per_booking mode
     // Otherwise it stays as a cost in totalOutgoings (rentlio/monthly modes)
     let cleaningSupplied = 0;
     if (settings.cleaningMode === 'fixed') {
       cleaningSupplied = parseFloat(settings.cleaningFee || 0);
       // Supplied service - will be added to subTotal with VAT
     } else if (settings.cleaningMode === 'per_booking') {
       const cleaningPerBooking = parseFloat(settings.cleaningPerBooking || 0);
       cleaningSupplied = this.cleaningBookingCount * cleaningPerBooking;
       // Supplied service - will be added to subTotal with VAT
     }
     // Note: For 'rentlio' and 'monthly' modes, cleaning is already calculated
     // in the booking loop and included in totalOutgoings as a cost

     // Route cleaning to disbursement or supplied based on classification (independent of mode):
     // - Principal model: cleaning is ALWAYS supplied (VAT always applies)
     // - Agent model: supplied only if cleaningVatApplicable toggle is ON
     // totalOutgoings (rentlio/monthly) and cleaningSupplied (fixed/per_booking) are mutually
     // exclusive — their sum equals the actual cleaning amount regardless of mode.
     const cleaningIsSupplied = settings.vatBusinessModel === 'principal'
       || settings.cleaningVatApplicable;
     const cleaningRaw = totalOutgoings + cleaningSupplied;
     totalOutgoings   = !cleaningIsSupplied ? cleaningRaw : 0;
     cleaningSupplied =  cleaningIsSupplied ? cleaningRaw : 0;

     const netIncome = totalRevenue - totalPlatformFees - totalCardFees;

     // Laundry as disbursement: deduct from gross profit when VAT toggle is OFF
     // (when ON, it's a supplied service and goes to Sub Total instead)
     let laundryDisbursement = 0;
     if (!settings.laundryVatApplicable) {
       if (settings.laundryCalculationMode === 'per_booking') {
         laundryDisbursement = this.laundryBookingCount * parseFloat(settings.laundryPerBooking || 0);
       } else {
         laundryDisbursement = parseFloat(settings.laundry || 0);
       }
     }

     const grossProfit = netIncome - totalOutgoings - laundryDisbursement;

     let managementFees = 0;
      if (settings.managementFeeBase === 'revenue') {
        managementFees = totalRevenue * (settings.managementFeeRate / 100);
      } else if (settings.managementFeeBase === 'gross') {
        managementFees = grossProfit * (settings.managementFeeRate / 100);
      } else if (settings.managementFeeBase === 'grossFixed') {
        managementFees = parseFloat(settings.managementFeeFixedAmount || 0);
      } else {
        managementFees = netIncome * (settings.managementFeeRate / 100);
      }

     const maintenance = settings.useCustomMaintenance
       ? parseFloat(settings.customMaintenanceAmount || 0)
       : parseFloat(settings.maintenance || 0);
     const consumables = settings.useCustomConsumables
       ? parseFloat(settings.customConsumablesAmount || 0)
       : parseFloat(settings.consumables || 0);
     // Calculate laundry based on mode
     let laundry = 0;
     if (settings.laundryCalculationMode === 'per_booking') {
       const laundryPerBooking = parseFloat(settings.laundryPerBooking || 0);
       laundry = this.laundryBookingCount * laundryPerBooking;
     } else {
       // Fixed mode
       laundry = parseFloat(settings.laundry || 0);
     }
     const otherExpenses = settings.useCustomOtherExpenses
       ? parseFloat(settings.customOtherExpensesAmount || 0)
       : parseFloat(settings.otherExpenses || 0);

     // Calculate VAT using formulas.js (handles business model and VAT toggles)
     const vatBreakdown = formulas.calculateVATBreakdown({
       vatRate: settings.vatRate,
       businessModel: settings.vatBusinessModel,
       managementFees: managementFees,
       consumables: consumables,
       laundry: laundry,
       laundryVatApplicable: settings.laundryVatApplicable,
       cleaning: cleaningSupplied,
       cleaningVatApplicable: settings.cleaningVatApplicable,
       expenses: []  // Expenses handled separately
     });

     const vat = vatBreakdown.total_vat;
     const subTotal = vatBreakdown.total_taxable;

     // Ajouter le total des dépenses supplémentaires (Other Maintenance)
     const additionalExpenses = this.totalAdditionalExpenses;

     const settlementAmount = grossProfit - subTotal - vat - maintenance - otherExpenses - additionalExpenses;

     this.calculatedMetrics = {
       totalRevenue,
       totalNights,
       totalPlatformFees,
       totalCardFees,
       totalOutgoings,
       netIncome,
       grossProfit,
       managementFees,
       vat,
       maintenance,
       consumables,
       laundry,
       cleaning: cleaningSupplied,
       otherExpenses,
       additionalExpenses,
       settlementAmount,
       averagePerNight: reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0
     };

     this.generatePreviewHTML();
   },

generatePreviewHTML() {
  if (!this.modalData.reservations || !this.calculatedMetrics) {
    return;
  }

  const reservations = this.modalData.reservations;
  const calculations = this.calculatedMetrics;
  const settings = this.modalSettings;

  const currentDate = new Date();

  // Determine report month/year for checkout-based cleaning
  let reportMonth, reportYear;
  if (this.modalData.periodLabel === 'Current Month') {
    reportMonth = currentDate.getMonth() + 1;
    reportYear = currentDate.getFullYear();
  } else if (this.modalData.periodLabel === 'Last Month') {
    const lm = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    reportMonth = lm.getMonth() + 1;
    reportYear = lm.getFullYear();
  } else {
    const monthMap = { 'January':1,'February':2,'March':3,'April':4,'May':5,'June':6,'July':7,'August':8,'September':9,'October':10,'November':11,'December':12 };
    const parts = (this.modalData.periodLabel || '').split(' ');
    reportMonth = monthMap[parts[0]] || currentDate.getMonth() + 1;
    reportYear = parseInt(parts[1]) || currentDate.getFullYear();
  }
  // Use the actual periodLabel instead of calculating dates
  // periodLabel already contains the correct month/year (e.g., "December 2025")
  let monthYear;
  if (this.modalData.periodLabel === 'Current Month') {
    monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else if (this.modalData.periodLabel === 'Last Month') {
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    monthYear = lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else {
    // For historical months, use the periodLabel directly (e.g., "December 2025")
    monthYear = this.modalData.periodLabel;
  }

  const bookingsRows = reservations.map(reservation => {
    const amount = parseFloat(reservation.totalReservationPrice || 0);
    const fees = this.calculateCustomCommission(reservation, this.modalSettings);

    let cardFee = 0;
    if (settings.cardFeeMode === 'rentlio') {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
    } else {
      cardFee = (reservation.otaChannelName === 'Booking.com') ?
        (amount * settings.customCardFeeRate / 100) : 0;
    }

    let outgoings = 0;
    const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');

    if (isExtension) {
      outgoings = 0;
    } else {
      const depTs = reservation.departureDate || reservation.departure_date || 0;
      const depDt = depTs ? new Date(depTs * 1000) : null;
      const checkoutInMonth = depDt && (depDt.getMonth() + 1) === reportMonth && depDt.getFullYear() === reportYear;

      if (settings.cleaningMode === 'rentlio') {
        outgoings = checkoutInMonth ? parseFloat(reservation.totalServicesPrice || 0) : 0;
      } else {
        const isLastSegment = !reservation.isSegment ||
          (reservation.segmentInfo?.part === reservation.segmentInfo?.total);

        if (isLastSegment && checkoutInMonth) {
          const platform = reservation.otaChannelName || '';
          if (platform === 'Booking.com') {
            outgoings = settings.bookingCleaningFee || 150;
          } else {
            outgoings = settings.otherCleaningFee || 120;
          }
        } else {
          outgoings = 0;
        }
      }
    }

    const netIncome = amount - fees - cardFee;
    const grossProfit = netIncome - outgoings;

    return `
      <tr>
        <td style="text-align: left;">${reservation.guestName || ''}</td>
        <td style="text-align: left;">${reservation.otaChannelName || 'Direct'}</td>
        <td style="text-align: center;">${reservation.totalNights || 0}</td>
        <td style="text-align: right;">£${amount.toFixed(2)}</td>
        <td style="text-align: right;">£${fees > 0 ? fees.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${cardFee > 0 ? cardFee.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${netIncome.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const totalNights = reservations.reduce((sum, r) => sum + (r.totalNights || 0), 0);
  const totalAmount = parseFloat(calculations.totalRevenue.toFixed(2));
  const totalFees = calculations.totalPlatformFees;
  const totalCardFees = calculations.totalCardFees;
  const totalNetIncome = calculations.netIncome;
  const totalOutgoings = calculations.totalOutgoings;
  const totalGrossProfit = calculations.grossProfit;
  const avgPerNight = reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0;

  let managementFeeLabel = '';
  if (settings.managementFeeBase === 'revenue') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Total Revenue)`;
  } else if (settings.managementFeeBase === 'gross') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Gross Profit)`;
  } else if (settings.managementFeeBase === 'grossFixed') {
    managementFeeLabel = `MANAGEMENT FEES (Fixed Amount)`;
  } else {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Net Income)`;
  }

  // Sub Total: uses VAT breakdown total_taxable (respects toggles)
  const vatSubTotal = calculations.managementFees + calculations.consumables
    + (settings.laundryVatApplicable ? calculations.laundry : 0)
    + (settings.cleaningVatApplicable ? calculations.cleaning : 0);

  // HTML MATCHING NEW PDF TEMPLATE DESIGN
  this.previewHTML = `
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">
    <div style="
      font-family: 'Inter', sans-serif;
      background: #fdfcff;
      color: #2a1f45;
      font-size: 9pt;
      line-height: 1.5;
      width: 210mm;
      min-height: 297mm;
      padding: 0;
      position: relative;
      box-sizing: border-box;
    ">

      <!-- HEADER BAND -->
      <div style="background: #f9f8fc; padding: 10mm 15mm 6mm 15mm;">
        <div style="font-family: 'Inter', sans-serif; font-size: 28pt; font-weight: 400; line-height: 1.1; margin-bottom: 6px; color: #2a1f45;">Monthly Report</div>
        <div style="font-family: 'Inter', sans-serif; font-size: 18pt; font-weight: 600; margin-bottom: 4px; color: #2a1f45;">${this.modalData.propertyName}</div>
        <div style="font-size: 10px; color: #8a80a0; margin-bottom: 14px;">${this.modalData.propertyAddress || ''}</div>
        <div style="display: inline-block; background: #ede9f8; border-radius: 4px; padding: 3px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45;">${monthYear}${this.modalData.ownerName ? '&nbsp;&nbsp;|&nbsp;&nbsp;' + this.modalData.ownerName : ''}</div>
      </div>

      <!-- BODY -->
      <div style="padding: 10mm 15mm 12mm 15mm;">

        <!-- BOOKING SUMMARY -->
        <div style="margin-bottom: 14px;">
          <div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45; margin-bottom: 6px;">Booking Summary</div>
          <div style="border: 1.5px solid #e0d8f2; border-radius: 8px; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 7pt; table-layout: fixed;">
              <thead>
                <tr style="background: #f9f8fc;">
                  <th style="text-align: left; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 22%;">Guest</th>
                  <th style="text-align: left; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 14%;">Source</th>
                  <th style="text-align: right; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 9%;">Nights</th>
                  <th style="text-align: right; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 13%;">Amount</th>
                  <th style="text-align: right; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 13%;">Platform Fees</th>
                  <th style="text-align: right; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 11%;">Card Fee</th>
                  <th style="text-align: right; padding: 5px 6px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 14%;">Net Income</th>
                </tr>
              </thead>
              <tbody>
                ${reservations.map(reservation => {
                  const amt = parseFloat(reservation.totalReservationPrice || 0);
                  const feeAmt = this.calculateCustomCommission(reservation, this.modalSettings);
                  let cardFeeAmt = 0;
                  if (settings.cardFeeMode === 'rentlio') {
                    cardFeeAmt = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
                  } else {
                    cardFeeAmt = (reservation.otaChannelName === 'Booking.com') ? (amt * settings.customCardFeeRate / 100) : 0;
                  }
                  const netAmt = amt - feeAmt - cardFeeAmt;
                  const cardFeeDisplay = cardFeeAmt > 0 ? `£${cardFeeAmt.toFixed(2)}` : '—';
                  return `<tr>
                    <td style="text-align: left; padding: 4px 6px; font-size: 7pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">${reservation.guestName || ''}</td>
                    <td style="text-align: left; padding: 4px 6px; font-size: 7pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">${reservation.otaChannelName || 'Direct'}</td>
                    <td style="text-align: right; padding: 4px 6px; font-size: 7pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">${reservation.totalNights || 0}</td>
                    <td style="text-align: right; padding: 4px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${amt.toFixed(2)}</td>
                    <td style="text-align: right; padding: 4px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${feeAmt.toFixed(2)}</td>
                    <td style="text-align: right; padding: 4px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">${cardFeeDisplay}</td>
                    <td style="text-align: right; padding: 4px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${netAmt.toFixed(2)}</td>
                  </tr>`;
                }).join('')}
                <tr style="background: #f9f8fc;">
                  <td style="text-align: left; padding: 5px 6px; font-size: 7pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">Totals</td>
                  <td style="padding: 5px 6px; border-top: 1.5px solid #e0d8f2;"></td>
                  <td style="text-align: right; padding: 5px 6px; font-size: 7pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">${totalNights}</td>
                  <td style="text-align: right; padding: 5px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${totalAmount.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${totalFees.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${totalCardFees.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 6px; font-family: 'Inter', sans-serif; font-size: 7pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${totalNetIncome.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- INCOME & DIRECT EXPENSES -->
        <div style="margin-bottom: 14px;">
          <div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45; margin-bottom: 6px;">Income &amp; Direct Expenses</div>
          <div style="border: 1.5px solid #e0d8f2; border-radius: 8px; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <thead>
                <tr style="background: #f9f8fc;">
                  <th style="text-align: left; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2;">Item</th>
                  <th style="text-align: right; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 28%;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; font-weight: 700; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Guest Payments</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.totalRevenue.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Platform Fees</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${calculations.totalPlatformFees.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Card Processing Fees</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${calculations.totalCardFees.toFixed(2)}</td>
                </tr>
                ${!settings.cleaningVatApplicable && calculations.totalOutgoings > 0 ? `
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Cleaning Disbursement</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${calculations.totalOutgoings.toFixed(2)}</td>
                </tr>` : ''}
                ${!settings.laundryVatApplicable && calculations.laundry > 0 ? `
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Laundry &amp; Linen (Disbursement)</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${calculations.laundry.toFixed(2)}</td>
                </tr>` : ''}
                <tr style="background: #f9f8fc;">
                  <td style="text-align: left; padding: 6px 10px; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">Gross Profit</td>
                  <td style="text-align: right; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${calculations.grossProfit.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- MANAGEMENT FEES & SUPPLIED SERVICES -->
        <div style="margin-bottom: 14px;">
          <div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45; margin-bottom: 6px;">Management Fees &amp; Supplied Services</div>
          <div style="border: 1.5px solid #e0d8f2; border-radius: 8px; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <thead>
                <tr style="background: #f9f8fc;">
                  <th style="text-align: left; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2;">Charge</th>
                  <th style="text-align: right; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 28%;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; border-bottom: 1px solid #f0edf8;">
                    <div style="font-size: 8pt; font-weight: 700; color: #2a1f45;">${managementFeeLabel}</div>
                  </td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.managementFees.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Consumables &amp; Guest Supplies</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.consumables.toFixed(2)}</td>
                </tr>
                ${settings.laundryVatApplicable && calculations.laundry > 0 ? `
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Laundry &amp; Linen Supply</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.laundry.toFixed(2)}</td>
                </tr>` : ''}
                ${settings.cleaningVatApplicable && calculations.cleaning > 0 ? `
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Cleaning (Supplied Service)</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.cleaning.toFixed(2)}</td>
                </tr>` : ''}
                <tr style="background: #f9f8fc;">
                  <td style="text-align: left; padding: 6px 10px; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">Sub-total</td>
                  <td style="text-align: right; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${vatSubTotal.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- VAT SUMMARY -->
        <div style="margin-bottom: 14px;">
          <div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45; margin-bottom: 6px;">VAT Summary</div>
          <div style="border: 1.5px solid #e0d8f2; border-radius: 8px; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <thead>
                <tr style="background: #f9f8fc;">
                  <th style="text-align: left; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2;">Item</th>
                  <th style="text-align: right; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 20%;">Net</th>
                  <th style="text-align: right; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 20%;">VAT</th>
                  <th style="text-align: right; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 20%;">Gross</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Management Fees</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.managementFees.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.managementFees * (settings.vatRate || 20) / 100).toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.managementFees * (1 + (settings.vatRate || 20) / 100)).toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Consumables &amp; Guest Supplies</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.consumables.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.consumables * (settings.vatRate || 20) / 100).toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.consumables * (1 + (settings.vatRate || 20) / 100)).toFixed(2)}</td>
                </tr>
                ${settings.laundryVatApplicable && calculations.laundry > 0 ? `
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Laundry &amp; Linen Supply</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.laundry.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.laundry * (settings.vatRate || 20) / 100).toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.laundry * (1 + (settings.vatRate || 20) / 100)).toFixed(2)}</td>
                </tr>` : ''}
                ${settings.cleaningVatApplicable && calculations.cleaning > 0 ? `
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Cleaning (Supplied Service)</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.cleaning.toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.cleaning * (settings.vatRate || 20) / 100).toFixed(2)}</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${(calculations.cleaning * (1 + (settings.vatRate || 20) / 100)).toFixed(2)}</td>
                </tr>` : ''}
                <tr style="background: #f9f8fc;">
                  <td style="text-align: left; padding: 6px 10px; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">Total VAT</td>
                  <td style="text-align: right; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${vatSubTotal.toFixed(2)}</td>
                  <td style="text-align: right; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${calculations.vat.toFixed(2)}</td>
                  <td style="text-align: right; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${(vatSubTotal + calculations.vat).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style="font-size: 7.5pt; font-style: italic; color: #8a80a0; margin-top: 5px;">VAT applies only to supplied services.</div>
        </div>

        <!-- MAINTENANCE & OTHER COSTS -->
        <div style="margin-bottom: 14px;">
          <div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45; margin-bottom: 6px;">Maintenance &amp; Other Costs</div>
          <div style="border: 1.5px solid #e0d8f2; border-radius: 8px; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <thead>
                <tr style="background: #f9f8fc;">
                  <th style="text-align: left; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2;">Item</th>
                  <th style="text-align: right; padding: 5px 10px; font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #8a80a0; border-bottom: 1px solid #e0d8f2; width: 28%;">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Maintenance</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">${calculations.maintenance > 0 ? `£${calculations.maintenance.toFixed(2)}` : '—'}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Other Expenses</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">${calculations.additionalExpenses > 0 ? `£${calculations.additionalExpenses.toFixed(2)}` : '—'}</td>
                </tr>
                <tr style="background: #f9f8fc;">
                  <td style="text-align: left; padding: 6px 10px; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">Total</td>
                  <td style="text-align: right; padding: 6px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${(calculations.maintenance + calculations.additionalExpenses).toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- SETTLEMENT SUMMARY -->
        <div style="margin-bottom: 20px;">
          <div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; color: #2a1f45; margin-bottom: 6px;">Settlement Summary</div>
          <div style="border: 1.5px solid #e0d8f2; border-radius: 8px; overflow: hidden;">
            <table style="width: 100%; border-collapse: collapse; font-size: 8pt;">
              <tbody>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Gross Profit</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">£${calculations.grossProfit.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Less Supplied Services</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${vatSubTotal.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Less VAT</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${calculations.vat.toFixed(2)}</td>
                </tr>
                <tr>
                  <td style="text-align: left; padding: 5px 10px; font-size: 8pt; color: #2a1f45; border-bottom: 1px solid #f0edf8;">Less Maintenance &amp; Other Costs</td>
                  <td style="text-align: right; padding: 5px 10px; font-family: 'Inter', sans-serif; font-size: 8pt; color: #5a4f72; border-bottom: 1px solid #f0edf8;">−£${(calculations.maintenance + calculations.additionalExpenses).toFixed(2)}</td>
                </tr>
                <tr style="background: #f9f8fc;">
                  <td style="text-align: left; padding: 8px 10px; font-size: 9pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">Settlement to Owner</td>
                  <td style="text-align: right; padding: 8px 10px; font-family: 'Inter', sans-serif; font-size: 9pt; font-weight: 700; color: #2a1f45; border-top: 1.5px solid #e0d8f2;">£${calculations.settlementAmount.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- FOOTER BRAND BAND -->
        <div style="background: linear-gradient(90deg, #3b5bdb 0%, #5b3fc8 50%, #7c30b8 100%); padding: 9px 36px; display: flex; align-items: center; justify-content: center; gap: 6px; border-radius: 0 0 14px 14px;">
          <svg viewBox="0 0 24 16" height="9" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2 12 Q5 2, 8 12 Q11 22, 14 12 Q17 2, 20 12" stroke="rgba(255,255,255,0.6)" stroke-width="2.2" stroke-linecap="round" fill="none"/>
          </svg>
          <span style="font-size: 7.5px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; color: rgba(255,255,255,.75);">Powered by Invoxis</span>
        </div>

      </div><!-- end body -->
    </div>
  `;
},

   resetToSaved() {
     if (!confirm('Reset all settings to saved values?')) {
       return;
     }

     this.modalSettings = { ...this.originalSettings };
     this.hasUnsavedChanges = false;
     this.calculateMetrics();
   },

async saveSettings() {
  this.savingSettings = true;

  try {
    const propertyId = this.modalData.realPropertyId;

    if (!propertyId) {
      throw new Error('ID de propriété manquant');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    // Map frontend field names to backend (camelCase to snake_case)
    // Ensure all numeric values are properly converted to numbers
    const payload = {
      property_id: propertyId,
      management_fee_rate: parseFloat(this.modalSettings.managementFeeRate) || 0,
      management_fee_base: this.modalSettings.managementFeeBase,
      management_fee_fixed_amount: parseFloat(this.modalSettings.managementFeeFixedAmount) || 0,
      vat_rate: parseFloat(this.modalSettings.vatRate) || 0,
      maintenance: parseFloat(this.modalSettings.maintenance) || 0,
      consumables: parseFloat(this.modalSettings.consumables) || 0,
      laundry_fee: parseFloat(this.modalSettings.laundry) || 0,
      laundry_calculation_mode: this.modalSettings.laundryCalculationMode,
      laundry_per_booking: parseFloat(this.modalSettings.laundryPerBooking) || 0,
      laundry_vat_applicable: this.modalSettings.laundryVatApplicable,
      other_expenses: parseFloat(this.modalSettings.otherExpenses) || 0,
      cleaning_mode: this.modalSettings.cleaningMode,
      booking_cleaning_fee: parseFloat(this.modalSettings.bookingCleaningFee) || 0,
      other_cleaning_fee: parseFloat(this.modalSettings.otherCleaningFee) || 0,
      cleaning_fee: parseFloat(this.modalSettings.cleaningFee) || 0,
      cleaning_per_booking: parseFloat(this.modalSettings.cleaningPerBooking) || 0,
      card_fee_mode: this.modalSettings.cardFeeMode,
      custom_card_fee_rate: parseFloat(this.modalSettings.customCardFeeRate) || 0,
      custom_card_fee_fixed: parseFloat(this.modalSettings.customCardFeeFixed) || 0,
      platform_fee_mode: this.modalSettings.platformFeeMode,
      apply_custom_booking_com: this.modalSettings.applyCustomBookingCom,
      booking_com_commission_rate: parseFloat(this.modalSettings.bookingComCommissionRate) || 0,
      apply_custom_airbnb: this.modalSettings.applyCustomAirbnb,
      airbnb_commission_rate: parseFloat(this.modalSettings.airbnbCommissionRate) || 0,
      apply_custom_other: this.modalSettings.applyCustomOther,
      other_platform_commission_rate: parseFloat(this.modalSettings.otherPlatformCommissionRate) || 0
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/settings/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      // Reload settings from backend to ensure cache is synchronized with database
      await this.reloadPropertySettingsFromDB(propertyId);

      // Save monthly laundry booking count override if in per_booking mode
      if (this.modalSettings.laundryCalculationMode === 'per_booking') {
        const periodLabel = this.modalData.periodLabel || '';
        const monthMap = {
          'January': 1, 'February': 2, 'March': 3, 'April': 4,
          'May': 5, 'June': 6, 'July': 7, 'August': 8,
          'September': 9, 'October': 10, 'November': 11, 'December': 12
        };

        let month, year;

        if (periodLabel === 'Current Month') {
          const now = new Date();
          month = now.getMonth() + 1;
          year = now.getFullYear();
        } else if (periodLabel === 'Last Month') {
          const now = new Date();
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          month = lastMonth.getMonth() + 1;
          year = lastMonth.getFullYear();
        } else {
          const [monthName, yearStr] = periodLabel.split(' ');
          month = monthMap[monthName];
          year = parseInt(yearStr);
        }

        if (month && year) {
          await this.saveMonthlyLaundryOverride(propertyId, year, month);
        }
      }

      // Save monthly cleaning booking count override if in per_booking mode
      if (this.modalSettings.cleaningMode === 'per_booking') {
        const periodLabel = this.modalData.periodLabel || '';
        const monthMap = {
          'January': 1, 'February': 2, 'March': 3, 'April': 4,
          'May': 5, 'June': 6, 'July': 7, 'August': 8,
          'September': 9, 'October': 10, 'November': 11, 'December': 12
        };

        let month, year;

        if (periodLabel === 'Current Month') {
          const now = new Date();
          month = now.getMonth() + 1;
          year = now.getFullYear();
        } else if (periodLabel === 'Last Month') {
          const now = new Date();
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          month = lastMonth.getMonth() + 1;
          year = lastMonth.getFullYear();
        } else {
          const [monthName, yearStr] = periodLabel.split(' ');
          month = monthMap[monthName];
          year = parseInt(yearStr);
        }

        if (month && year) {
          await this.saveMonthlyCleaningOverride(propertyId, year, month);
        }
      }

      this.hasUnsavedChanges = false;

      alert(`Settings saved for ${this.modalData.propertyName}!`);
    } else {
      throw new Error(response.data.message || 'Save failed');
    }

  } catch (error) {
    console.error('Error saving settings:', error);
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      this.$router.push('/login');
    } else {
      alert('Save failed. Please try again.');
    }
  } finally {
    this.savingSettings = false;
  }
},

// Vérifier s'il existe un montant fixe spécifique pour ce mois
async checkMonthSpecificFee(propertyId, periodLabel) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    // Parse year and month from periodLabel
    const [monthName, yearStr] = periodLabel.split(' ');
    const monthMap = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4,
      'May': 5, 'June': 6, 'July': 7, 'August': 8,
      'September': 9, 'October': 10, 'November': 11, 'December': 12,
      'Current': new Date().getMonth() + 1,
      'Last': new Date().getMonth() === 0 ? 12 : new Date().getMonth()
    };

    let month, year;

    if (periodLabel === 'Current Month') {
      const now = new Date();
      month = now.getMonth() + 1;
      year = now.getFullYear();
    } else if (periodLabel === 'Last Month') {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      month = lastMonth.getMonth() + 1;
      year = lastMonth.getFullYear();
    } else if (monthName && yearStr) {
      month = monthMap[monthName];
      year = parseInt(yearStr);
    }

    if (!month || !year || !propertyId) {
      console.warn('Could not parse month/year from period label:', periodLabel);
      return;
    }

    // Appeler l'API pour vérifier s'il existe un montant spécifique
    const response = await axios.get(
      `${this.baseUrl}/api/pms/report-fixed-fee/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success && response.data.data && response.data.data.fixed_amount !== null) {
      // Un montant spécifique existe pour ce mois
      this.modalSettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
      this.originalSettings.managementFeeFixedAmount = parseFloat(response.data.data.fixed_amount);
      this.hasMonthSpecificFee = true;
      console.log(`Month-specific fee loaded: £${response.data.data.fixed_amount} for ${monthName} ${year}`);
    } else {
      // Pas de montant spécifique, utiliser le défaut
      this.hasMonthSpecificFee = false;
    }

  } catch (error) {
    console.error('Error checking month-specific fee:', error);
    // Ne pas bloquer l'ouverture du modal en cas d'erreur
    this.hasMonthSpecificFee = false;
  }
},

async saveForThisMonthOnly() {
  this.savingSettings = true;

  try {
    const propertyId = this.modalData.realPropertyId;
    const periodLabel = this.modalData.periodLabel; // Ex: "November 2025"

    if (!propertyId || !periodLabel) {
      throw new Error('Missing property ID or period');
    }

    // Parse year and month from periodLabel (format: "Month YYYY")
    const [monthName, yearStr] = periodLabel.split(' ');
    const monthMap = {
      'January': 1, 'February': 2, 'March': 3, 'April': 4,
      'May': 5, 'June': 6, 'July': 7, 'August': 8,
      'September': 9, 'October': 10, 'November': 11, 'December': 12
    };
    const month = monthMap[monthName];
    const year = parseInt(yearStr);

    if (!month || !year) {
      throw new Error('Could not parse month/year from period label');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    // Payload pour sauvegarder le fixed fee pour ce mois uniquement
    const payload = {
      property_id: propertyId,
      year: year,
      month: month,
      fixed_amount: parseFloat(this.modalSettings.managementFeeFixedAmount) || 0
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/report-fixed-fee/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      this.hasMonthSpecificFee = true;
      this.originalSettings.managementFeeFixedAmount = this.modalSettings.managementFeeFixedAmount;
      this.hasUnsavedChanges = false;

      alert(`Fixed Management Fee of £${this.modalSettings.managementFeeFixedAmount} saved for ${monthName} ${year} only!\n\nOther months will continue to use the default setting.`);
    } else {
      throw new Error(response.data.message || 'Save failed');
    }

  } catch (error) {
    console.error('Error saving month-specific fixed fee:', error);
    if (error.response && error.response.status === 401) {
      localStorage.removeItem('token');
      this.$router.push('/login');
    } else {
      alert('Failed to save for this month only. Please try again.');
    }
  } finally {
    this.savingSettings = false;
  }
},

// ========== Property Expenses Methods ==========

async loadPropertyExpenses(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.get(
      `${this.baseUrl}/api/pms/property-expenses/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success) {
      this.propertyExpenses = response.data.data || [];
      console.log(`Loaded ${this.propertyExpenses.length} expenses for ${year}-${month}`);
    }
  } catch (error) {
    console.error('Error loading property expenses:', error);
    this.propertyExpenses = [];
  }
},

async loadMonthlyLaundryOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.get(
      `${this.baseUrl}/api/pms/monthly-laundry-override/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success && response.data.data) {
      // Override exists for this month
      const override = response.data.data;
      this.laundryBookingCount = override.booking_count;
      this.autoDetectedBookingCount = override.auto_detected_count;
      console.log(`Loaded laundry override: ${override.booking_count} bookings (auto-detected: ${override.auto_detected_count})`);
    } else {
      // No override, use auto-detected value (already set by autoDetectBookingCount)
      console.log(`No laundry override found for ${year}-${month}, using auto-detected count`);
    }
  } catch (error) {
    console.error('Error loading monthly laundry override:', error);
    // Keep auto-detected value on error
  }
},

async saveMonthlyLaundryOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const payload = {
      property_id: propertyId,
      year: year,
      month: month,
      booking_count: this.laundryBookingCount,
      auto_detected_count: this.autoDetectedBookingCount
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/monthly-laundry-override/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      console.log(`Saved laundry override: ${this.laundryBookingCount} bookings for ${year}-${month}`);
    }
  } catch (error) {
    console.error('Error saving monthly laundry override:', error);
  }
},

async loadMonthlyCleaningOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.get(
      `${this.baseUrl}/api/pms/monthly-cleaning-override/`,
      {
        params: { property_id: propertyId, year: year, month: month },
        headers
      }
    );

    if (response.data.success && response.data.data) {
      // Override exists for this month
      const override = response.data.data;
      this.cleaningBookingCount = override.booking_count;
      console.log(`Loaded cleaning override: ${override.booking_count} bookings (auto-detected: ${override.auto_detected_count})`);
    } else {
      // No override, use auto-detected value (already set by autoDetectBookingCount)
      console.log(`No cleaning override found for ${year}-${month}, using auto-detected count`);
    }
  } catch (error) {
    console.error('Error loading monthly cleaning override:', error);
    // Keep auto-detected value on error
  }
},

async saveMonthlyCleaningOverride(propertyId, year, month) {
  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    const payload = {
      property_id: propertyId,
      year: year,
      month: month,
      booking_count: this.cleaningBookingCount,
      auto_detected_count: this.autoDetectedBookingCount
    };

    const response = await axios.post(
      `${this.baseUrl}/api/pms/monthly-cleaning-override/`,
      payload,
      { headers }
    );

    if (response.data.success) {
      console.log(`Saved cleaning override: ${this.cleaningBookingCount} bookings for ${year}-${month}`);
    }
  } catch (error) {
    console.error('Error saving monthly cleaning override:', error);
  }
},

openExpenseModal() {
  this.showExpenseModal = true;
  this.newExpense = {
    description: '',
    amount: 0,
    expense_category: 'maintenance',
    is_vat_applicable: false,
    vat_treatment: 'gross',
    net_amount: 0,
    vat_amount: 0,
    vat_rate: 20,
    receipt_file: null,
    receipt_filename: '',
    pending_files: []
  };
},

// W1 Refonte #10 — Pre-fill expense_category=guest_extra when adding from Income section
openGuestExtraModal() {
  this.showExpenseModal = true;
  this.newExpense = {
    description: '',
    amount: 0,
    expense_category: 'guest_extra',
    is_vat_applicable: false,
    vat_treatment: 'gross',
    net_amount: 0,
    vat_amount: 0,
    vat_rate: 20,
    receipt_file: null,
    receipt_filename: '',
    pending_files: []
  };
},

closeExpenseModal() {
  this.showExpenseModal = false;
  this.expenseScope = 'property';
  this.newExpense = {
    description: '',
    amount: 0,
    expense_category: 'maintenance',
    is_vat_applicable: false,
    vat_treatment: 'gross',
    net_amount: 0,
    vat_amount: 0,
    vat_rate: 20,
    receipt_file: null,
    receipt_filename: '',
    pending_files: []
  };
  // Reset file input
  if (this.$refs.receiptFileInput) {
    this.$refs.receiptFileInput.value = '';
  }
},

handleReceiptUpload(event) {
  // Multiple files allowed — accumulate every selection into pending_files
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  if (!this.newExpense.pending_files) {
    this.newExpense.pending_files = [];
  }
  this.newExpense.pending_files.push(...files);
  // Reset the input so picking the same file again triggers @change
  if (this.$refs.receiptFileInput) {
    this.$refs.receiptFileInput.value = '';
  }
},

removePendingFile(idx) {
  if (!this.newExpense.pending_files) return;
  this.newExpense.pending_files.splice(idx, 1);
},

expenseCategoryLabel(cat) {
  const map = {
    maintenance: 'Maintenance',
    guest_extra: 'Guest extra',
    cleaning: 'Cleaning',
    other: 'Other',
  };
  return map[cat] || 'Maintenance';
},

async saveExpense() {
  const hasAmount = this.newExpense.amount ||
    (this.newExpense.is_vat_applicable && this.newExpense.vat_treatment === 'net_plus_vat' && this.newExpense.net_amount);

  if (!this.newExpense.description || !hasAmount) {
    alert('Please fill in description and amount');
    return;
  }

  const isGroupScope = this.expenseScope === 'group';

  this.savingExpense = true;

  try {
    let year, month, propertyId, groupId;

    if (isGroupScope) {
      const g = this.groupReportModalData.group;
      const monthDef = this.monthForPeriod(this.groupReportModalData.period);
      if (!g || !monthDef) {
        throw new Error('Missing group or period');
      }
      groupId = g.id;
      [year, month] = monthDef.value.split('-').map(Number);
    } else {
      propertyId = this.modalData.realPropertyId;
      const periodLabel = this.modalData.periodLabel;

      if (!propertyId || !periodLabel) {
        throw new Error('Missing property ID or period');
      }

      // Parse year and month from periodLabel
      const [monthName, yearStr] = periodLabel.split(' ');
      const monthMap = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4,
        'May': 5, 'June': 6, 'July': 7, 'August': 8,
        'September': 9, 'October': 10, 'November': 11, 'December': 12,
      };

      if (periodLabel === 'Current Month') {
        const now = new Date();
        month = now.getMonth() + 1;
        year = now.getFullYear();
      } else if (periodLabel === 'Last Month') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        month = lastMonth.getMonth() + 1;
        year = lastMonth.getFullYear();
      } else if (monthName && yearStr) {
        month = monthMap[monthName];
        year = parseInt(yearStr);
      }

      if (!month || !year) {
        throw new Error('Could not parse month/year from period label');
      }
    }

    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    // Calculer le montant total selon le mode VAT
    let totalAmount = this.newExpense.amount;
    if (this.newExpense.is_vat_applicable && this.newExpense.vat_treatment === 'net_plus_vat') {
      totalAmount = parseFloat(this.newExpense.net_amount || 0) + parseFloat(this.newExpense.vat_amount || 0);
    }

    // Créer FormData pour l'upload de fichier
    const formData = new FormData();
    if (isGroupScope) {
      formData.append('property_group', groupId);
    } else {
      formData.append('property_id', propertyId);
    }
    formData.append('year', year);
    formData.append('month', month);
    formData.append('description', this.newExpense.description);
    formData.append('amount', totalAmount);
    formData.append('is_vat_applicable', this.newExpense.is_vat_applicable ? 'true' : 'false');
    formData.append('vat_treatment', this.newExpense.vat_treatment || 'gross');
    formData.append('vat_rate', this.newExpense.vat_rate || 20);
    formData.append('expense_category', this.newExpense.expense_category || 'maintenance');

    if (this.newExpense.is_vat_applicable && this.newExpense.vat_treatment === 'net_plus_vat') {
      formData.append('net_amount', this.newExpense.net_amount || 0);
      formData.append('vat_amount', this.newExpense.vat_amount || 0);
    }

    const response = await axios.post(
      `${this.baseUrl}/api/pms/property-expenses/create/`,
      formData,
      {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    if (response.data.success) {
      const expenseId = response.data.data && response.data.data.id;
      const pending = Array.isArray(this.newExpense.pending_files) ? this.newExpense.pending_files : [];

      if (expenseId && pending.length) {
        const filesData = new FormData();
        for (const f of pending) {
          filesData.append('files', f);
        }
        try {
          await axios.post(
            `${this.baseUrl}/api/pms/property-expenses/${expenseId}/files/`,
            filesData,
            { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
          );
        } catch (e) {
          console.error('Failed to upload attached files:', e);
          alert('Expense saved but one or more files failed to upload.');
        }
      }

      if (isGroupScope) {
        await this.loadGroupExpenses(groupId, year, month);
        if (this.showGroupReportModal) {
          this.scheduleGroupPreviewRefresh(0);
        }
      } else {
        await this.loadPropertyExpenses(propertyId, year, month);
        this.calculateMetrics();
      }

      // Fermer le modal
      this.closeExpenseModal();
    } else {
      throw new Error(response.data.message || 'Failed to save expense');
    }

  } catch (error) {
    console.error('Error saving expense:', error);
    alert('Failed to save expense. Please try again.');
  } finally {
    this.savingExpense = false;
  }
},

async deleteExpense(expenseId) {
  if (!confirm('Are you sure you want to delete this expense?')) {
    return;
  }

  try {
    const token = localStorage.getItem('token');
    if (!token) {
      this.$router.push('/login');
      return;
    }

    const headers = { 'Authorization': `Bearer ${token}` };

    const response = await axios.delete(
      `${this.baseUrl}/api/pms/property-expenses/${expenseId}/`,
      { headers }
    );

    if (response.data.success) {
      // Recharger la liste
      const propertyId = this.modalData.realPropertyId;
      const periodLabel = this.modalData.periodLabel;

      // Parse year and month
      const [monthName, yearStr] = periodLabel.split(' ');
      const monthMap = {
        'January': 1, 'February': 2, 'March': 3, 'April': 4,
        'May': 5, 'June': 6, 'July': 7, 'August': 8,
        'September': 9, 'October': 10, 'November': 11, 'December': 12
      };

      let month, year;
      if (periodLabel === 'Current Month') {
        const now = new Date();
        month = now.getMonth() + 1;
        year = now.getFullYear();
      } else if (periodLabel === 'Last Month') {
        const now = new Date();
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        month = lastMonth.getMonth() + 1;
        year = lastMonth.getFullYear();
      } else if (monthName && yearStr) {
        month = monthMap[monthName];
        year = parseInt(yearStr);
      }

      if (month && year) {
        await this.loadPropertyExpenses(propertyId, year, month);
        this.calculateMetrics();
      }
    }
  } catch (error) {
    console.error('Error deleting expense:', error);
    alert('Failed to delete expense. Please try again.');
  }
},

   async downloadPDF() {
     this.generatingPDF = true;

     try {
       console.log('Preparing data for backend PDF generation with VAT system...');

       // Use the new prepareBookingsForBackend() function
       const reportData = this.prepareBookingsForBackend();

       // Add send_email flag (false for download)
       reportData.send_email = false;

       // Save monthly laundry override if in per_booking mode
       if (this.modalSettings.laundryCalculationMode === 'per_booking' && this.modalData.realPropertyId) {
         const periodLabel = this.modalData.periodLabel;
         const monthMap = {
           'January': 1, 'February': 2, 'March': 3, 'April': 4,
           'May': 5, 'June': 6, 'July': 7, 'August': 8,
           'September': 9, 'October': 10, 'November': 11, 'December': 12
         };

         let month, year;
         if (periodLabel === 'Current Month') {
           const now = new Date();
           month = now.getMonth() + 1;
           year = now.getFullYear();
         } else if (periodLabel === 'Last Month') {
           const now = new Date();
           const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
           month = lastMonth.getMonth() + 1;
           year = lastMonth.getFullYear();
         } else {
           const [monthName, yearStr] = periodLabel.split(' ');
           month = monthMap[monthName];
           year = parseInt(yearStr);
         }

         if (month && year) {
           await this.saveMonthlyLaundryOverride(this.modalData.realPropertyId, year, month);
         }
       }

       console.log('Sending request to /api/pms/generate-report-vat/');

       // Check if token exists
       const token = localStorage.getItem('token');
       if (!token) {
         alert('Votre session a expiré. Veuillez vous reconnecter.');
         this.$router.push('/login');
         return;
       }

       const response = await axios.post(`${this.baseUrl}/api/pms/generate-report-vat/`, reportData, {
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });

       if (response.data.success && response.data.data.pdf_base64) {
         console.log('PDF received from backend, downloading...');

         // Decode base64 and create blob
         const pdfBase64 = response.data.data.pdf_base64;
         const byteCharacters = atob(pdfBase64);
         const byteNumbers = new Array(byteCharacters.length);
         for (let i = 0; i < byteCharacters.length; i++) {
           byteNumbers[i] = byteCharacters.charCodeAt(i);
         }
         const byteArray = new Uint8Array(byteNumbers);
         const blob = new Blob([byteArray], { type: 'application/pdf' });

         // Create download link
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = response.data.data.filename || `${this.modalData.propertyName}_${this.modalData.periodLabel}_Report.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);

         console.log('PDF downloaded successfully');
       } else {
         throw new Error(response.data.message || 'Failed to generate PDF');
       }

     } catch (error) {
       console.error('Error generating PDF:', error);

       // Handle 401 Unauthorized error
       if (error.response?.status === 401) {
         alert('Votre session a expiré. Veuillez vous reconnecter.');
         this.$router.push('/login');
         return;
       }

       const errorMessage = error.response?.data?.message || error.message || 'Failed to generate PDF. Please try again.';
       alert(errorMessage);
     } finally {
       this.generatingPDF = false;
     }
   },

   generatePDFHTML() {
  const currentDate = new Date();

  // Determine report month/year for checkout-based cleaning
  let reportMonth, reportYear;
  if (this.modalData.periodLabel === 'Current Month') {
    reportMonth = currentDate.getMonth() + 1;
    reportYear = currentDate.getFullYear();
  } else if (this.modalData.periodLabel === 'Last Month') {
    const lm = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    reportMonth = lm.getMonth() + 1;
    reportYear = lm.getFullYear();
  } else {
    const monthMap = { 'January':1,'February':2,'March':3,'April':4,'May':5,'June':6,'July':7,'August':8,'September':9,'October':10,'November':11,'December':12 };
    const parts = (this.modalData.periodLabel || '').split(' ');
    reportMonth = monthMap[parts[0]] || currentDate.getMonth() + 1;
    reportYear = parseInt(parts[1]) || currentDate.getFullYear();
  }

  let monthYear;
  if (this.modalData.periodLabel === 'Current Month') {
    monthYear = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else if (this.modalData.periodLabel === 'Last Month') {
    const lastMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1);
    monthYear = lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  } else {
    // For historical months, use the periodLabel directly (e.g., "December 2025")
    monthYear = this.modalData.periodLabel;
  }

  const calculations = this.calculatedMetrics;
  const settings = this.modalSettings;
  const reservations = this.modalData.reservations;

  // Générer les lignes du tableau EXACTEMENT comme dans l'original
  const bookingsRows = reservations.map(reservation => {
    const amount = parseFloat(reservation.totalReservationPrice || 0);
    const fees = this.calculateCustomCommission(reservation, this.modalSettings);

    let cardFee = 0;
    if (settings.cardFeeMode === 'rentlio') {
      cardFee = (reservation.otaChannelName === 'Booking.com') ? parseFloat(reservation.cardFee || 0) : 0;
    } else {
      cardFee = (reservation.otaChannelName === 'Booking.com') ?
        (amount * settings.customCardFeeRate / 100) : 0;
    }

    let outgoings = 0;
    const isExtension = (reservation.guestName || '').toLowerCase().includes('extension');

    if (isExtension) {
      outgoings = 0;
    } else {
      const depTs = reservation.departureDate || reservation.departure_date || 0;
      const depDt = depTs ? new Date(depTs * 1000) : null;
      const checkoutInMonth = depDt && (depDt.getMonth() + 1) === reportMonth && depDt.getFullYear() === reportYear;

      if (settings.cleaningMode === 'rentlio') {
        outgoings = checkoutInMonth ? parseFloat(reservation.totalServicesPrice || 0) : 0;
      } else {
        const isLastSegment = !reservation.isSegment ||
          (reservation.segmentInfo?.part === reservation.segmentInfo?.total);

        if (isLastSegment && checkoutInMonth) {
          const platform = reservation.otaChannelName || '';
          if (platform === 'Booking.com') {
            outgoings = settings.bookingCleaningFee || 150;
          } else {
            outgoings = settings.otherCleaningFee || 120;
          }
        } else {
          outgoings = 0;
        }
      }
    }

    const netIncome = amount - fees - cardFee;
    const grossProfit = netIncome - outgoings;

    return `
      <tr>
        <td style="text-align: left;">${reservation.guestName || ''}</td>
        <td style="text-align: left;">${reservation.otaChannelName || 'Direct'}</td>
        <td style="text-align: center;">${reservation.totalNights || 0}</td>
        <td style="text-align: right;">£${amount.toFixed(2)}</td>
        <td style="text-align: right;">£${fees > 0 ? fees.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${cardFee > 0 ? cardFee.toFixed(2) : '0'}</td>
        <td style="text-align: right;">£${netIncome.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const totalNights = reservations.reduce((sum, r) => sum + (r.totalNights || 0), 0);
  const totalAmount = parseFloat(calculations.totalRevenue.toFixed(2));
  const totalFees = calculations.totalPlatformFees;
  const totalCardFees = calculations.totalCardFees;
  const totalNetIncome = calculations.netIncome;
  const totalOutgoings = calculations.totalOutgoings;
  const totalGrossProfit = calculations.grossProfit;
  const avgPerNight = reservations.length > 0 ? reservations.reduce((sum, r) => sum + (r.totalReservationPrice / r.totalNights), 0) / reservations.length : 0;

  let managementFeeLabel = '';
  if (settings.managementFeeBase === 'revenue') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Total Revenue)`;
  } else if (settings.managementFeeBase === 'gross') {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Gross Profit)`;
  } else if (settings.managementFeeBase === 'grossFixed') {
    managementFeeLabel = `MANAGEMENT FEES (Fixed Amount)`;
  } else {
    managementFeeLabel = `MANAGEMENT FEES (${settings.managementFeeRate}% on Net Income)`;
  }

  // UTILISER EXACTEMENT LES MÊMES STYLES CSS QUE L'ORIGINAL
  return `
<!DOCTYPE html>
<html lang="en">
<head>
   <meta charset="UTF-8">
   <title>Monthly Report</title>
   <style>
       @page {
           size: A4;
           margin: 0;
       }
       
       * {
           margin: 0;
           padding: 0;
           box-sizing: border-box;
       }
       
       body {
           font-family: 'Playfair Display', 'Times New Roman', serif;
           background: #f8f9f9;
           color: #2c3e50;
           font-size: 12px;
           line-height: 1.4;
           width: 210mm;
           height: 297mm;
           padding: 15mm;
           position: relative;
       }
       
       .page-container {
           width: 100%;
           min-height: 267mm;
           display: flex;
           flex-direction: column;
           padding: 20px;
           box-shadow: 0 2px 4px rgba(0,0,0,0.1);
           position: relative;
       }
       
       .report-header {
           margin-bottom: 25px;
       }
       
       .report-title {
           font-size: 38px;
           font-weight: 400;
           color: #2c3e50;
           margin-bottom: 5px;
           letter-spacing: 1px;
       }
       
       .property-name {
           font-size: 22px;
           font-weight: 300;
           color: #34495e;
           margin-bottom: 15px;
           letter-spacing: 0.5px;
       }
       
       .divider {
           width: 100%;
           height: 1px;
           background: #bdc3c7;
           margin: 15px 0 12px 0;
       }
       
       .report-period {
           font-size: 13px;
           font-weight: 400;
           color: #7f8c8d;
           text-transform: uppercase;
           letter-spacing: 2px;
       }
       
       .section-title {
           font-size: 10px;
           font-weight: 600;
           color: #2c3e50;
           text-transform: uppercase;
           letter-spacing: 1px;
           margin: 20px 0 10px 0;
       }
       
       .data-table {
           width: 100%;
           border-collapse: collapse;
           font-size: 8px;
           margin-bottom: 15px;
           font-family: 'Arial', sans-serif;
       }
       
       .data-table th {
           background: #ecf0f1;
           padding: 5px 3px;
           text-align: center;
           font-weight: 600;
           border: 1px solid #bdc3c7;
           font-size: 7px;
           line-height: 1.2;
           color: #2c3e50;
       }
       
       .data-table td {
           padding: 3px 2px;
           border: 1px solid #d5dbdb;
           font-size: 7px;
           vertical-align: middle;
           color: #2c3e50;
       }
       
       .data-table .totals-row {
           background: #f8f9fa;
           font-weight: 700;
           border-top: 2px solid #34495e;
       }
       
       .data-table .totals-row td {
           border: 1px solid #34495e;
           font-weight: 700;
           color: #2c3e50;
       }
       
       .content-sections {
           margin: 15px 0;
       }
       
       .summary-section {
           margin-bottom: 20px;
           margin-top: 20px;
       }
       
       .summary-title {
           font-size: 10px;
           font-weight: 700;
           margin-bottom: 8px;
           text-transform: uppercase;
           color: #2c3e50;
           letter-spacing: 1px;
       }
       
       .summary-table {
           width: 100%;
           border-collapse: collapse;
           font-family: 'Arial', sans-serif;
       }
       
       .summary-table td {
           padding: 2px 0;
           font-size: 8px;
           color: #2c3e50;
           border: none;
       }
       
       .summary-table .label {
           text-align: left;
           width: 70%;
       }
       
       .summary-table .value {
           text-align: right;
           width: 30%;
           font-weight: 600;
       }
       
       .summary-table .total-row {
           border-top: 1px solid #34495e;
           padding-top: 4px;
           margin-top: 4px;
           font-weight: 700;
           font-size: 11px;
       }
       
       .summary-table .total-row td {
           padding-top: 4px;
           border-top: 1px solid #34495e;
       }
       
       .settlement-section {
           margin: 25px 0;
           padding: 12px;
           background: #f8f9fa;
           border: 1px solid #e9ecef;
           text-align: center;
       }
       
       .settlement-title {
           font-size: 11px;
           font-weight: 700;
           margin-bottom: 10px;
           text-transform: uppercase;
           letter-spacing: 1px;
           color: #2c3e50;
       }
       
       .settlement-table {
           width: 100%;
           border-collapse: collapse;
           font-family: 'Arial', sans-serif;
       }
       
       .settlement-table td {
           padding: 3px 0;
           font-size: 11px;
           color: #2c3e50;
           border: none;
       }
       
       .settlement-table .label {
           text-align: left;
           width: 70%;
       }
       
       .settlement-table .value {
           text-align: right;
           width: 30%;
           font-weight: 600;
       }
       
       .settlement-table .final-row {
           border-top: 2px solid #2c3e50;
           padding-top: 6px;
           margin-top: 6px;
           font-weight: 700;
           font-size: 13px;
       }
       
       .settlement-table .final-row td {
           padding-top: 6px;
           border-top: 2px solid #2c3e50;
       }
       
       .footer-section {
           margin-top: auto;
           padding-top: 15px;
           text-align: center;
           border-top: 1px solid #bdc3c7;
       }
       
       .prepared-info {
           margin-bottom: 12px;
       }
       
       .prepared-label {
           font-size: 8px;
           color: #7f8c8d;
           margin-bottom: 2px;
       }
       
       .prepared-company {
           font-size: 10px;
           font-weight: 600;
           color: #2c3e50;
       }
       
       .company-logo {
           margin-bottom: 15px;
       }
       
       .company-name {
           font-size: 16px;
           font-weight: 300;
           letter-spacing: 6px;
           color: #2c3e50;
           margin-bottom: 1px;
       }
       
       .company-tagline {
           font-size: 6px;
           color: #95a5a6;
           text-transform: uppercase;
           letter-spacing: 3px;
       }
       
       .footer-meta {
           display: flex;
           justify-content: space-between;
           align-items: center;
           font-size: 9px;
           color: #7f8c8d;
           margin-top: 10px;
       }
   </style>
</head>
<body>
   <div class="page-container">
       <div class="report-header">
           <h1 class="report-title">Monthly Report</h1>
           <h2 class="property-name">${this.modalData.propertyName}</h2>
           <div class="divider"></div>
           <div class="report-period">${monthYear.toUpperCase()}</div>
       </div>
       
       <div class="section-title">BOOKING OVERVIEW</div>
       
       <table class="data-table">
           <thead>
               <tr>
                   <th>Booking Name</th>
                   <th>Source</th>
                   <th>No. Nights</th>
                   <th>Amount</th>
                   <th>Fees</th>
                   <th>Card Fee</th>
                   <th>Net Income</th>
               </tr>
           </thead>
           <tbody>
               ${bookingsRows}
               <tr class="totals-row">
                   <td style="text-align: left;"><strong>Totals</strong></td>
                   <td></td>
                   <td style="text-align: center;"><strong>${totalNights}</strong></td>
                   <td style="text-align: right;"><strong>£${totalAmount.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalFees.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalCardFees.toFixed(2)}</strong></td>
                   <td style="text-align: right;"><strong>£${totalNetIncome.toFixed(2)}</strong></td>
               </tr>
           </tbody>
       </table>
       
       <div class="content-sections">
           <div class="summary-section">
               <div class="summary-title">INCOME & DIRECT EXPENSES</div>
               <table class="summary-table">
                   <tr>
                       <td class="label">TOTAL GUEST PAYMENTS</td>
                       <td class="value">£${calculations.totalRevenue.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">PLATFORM FEES</td>
                       <td class="value">£${calculations.totalPlatformFees.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CARD PROCESSING FEES</td>
                       <td class="value">£${calculations.totalCardFees.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CLEANING & LAUNDRY</td>
                       <td class="value">£${calculations.totalOutgoings.toFixed(2)}</td>
                   </tr>
                   <tr class="total-row">
                       <td class="label">GROSS PROFIT</td>
                       <td class="value">£${calculations.grossProfit.toFixed(2)}</td>
                   </tr>
               </table>
           </div>
           
       <div class="summary-section">
         <div class="summary-title">MANAGEMENT FEES & OTHER COSTS</div>
         <table class="summary-table">
             <tr>
                 <td class="label">${managementFeeLabel}</td>
                 <td class="value">£${calculations.managementFees.toFixed(2)}</td>
             </tr>
                   <tr>
                       <td class="label">VAT</td>
                       <td class="value">£${calculations.vat.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">CONSUMABLES</td>
                       <td class="value">£${calculations.consumables.toFixed(2)}</td>
                   </tr>
                   <tr>
                       <td class="label">MAINTENANCE</td>
                       <td class="value">£${calculations.maintenance.toFixed(2)}</td>
                   </tr>
                   ${this.propertyExpenses.length > 0 ? `
                   <tr>
                       <td class="label">OTHER EXPENSES</td>
                       <td class="value">£${calculations.additionalExpenses.toFixed(2)}</td>
                   </tr>
                   ${this.propertyExpenses.map(expense => `
                   <tr>
                       <td class="label" style="padding-left: 15px;">- ${expense.description}</td>
                       <td class="value">£${parseFloat(expense.amount).toFixed(2)}</td>
                   </tr>
                   `).join('')}` : ''}
               </table>
           </div>
       </div>
       
       <div class="settlement-section">
           <div class="settlement-title">SETTLEMENT SUMMARY</div>
           <table class="settlement-table">
               <tr>
                   <td class="label">GROSS PROFIT</td>
                   <td class="value">£${calculations.grossProfit.toFixed(2)}</td>
               </tr>
               <tr>
                   <td class="label">LESS MANAGEMENT FEE & OTHER COSTS</td>
                   <td class="value">£${(calculations.managementFees + calculations.vat + calculations.maintenance + calculations.consumables + calculations.additionalExpenses).toFixed(2)}</td>
               </tr>
               <tr class="final-row">
                   <td class="label">SETTLEMENT TO OWNER</td>
                   <td class="value">£${calculations.settlementAmount.toFixed(2)}</td>
               </tr>
           </table>
       </div>
       
       <div class="footer-section" style="background: linear-gradient(90deg, #3b5bdb 0%, #5b3fc8 50%, #7c30b8 100%); padding: 9px 36px; display: flex; align-items: center; justify-content: center; gap: 6px; border-radius: 0 0 14px 14px;">
           <svg viewBox="0 0 24 16" height="9" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path d="M2 12 Q5 2, 8 12 Q11 22, 14 12 Q17 2, 20 12" stroke="rgba(255,255,255,0.6)" stroke-width="2.2" stroke-linecap="round" fill="none"/>
           </svg>
           <span style="font-size: 7.5px; font-weight: 700; letter-spacing: .18em; text-transform: uppercase; color: rgba(255,255,255,.75);">Powered by Invoxis</span>
       </div>
   </div>
</body>
</html>
     `;
},

   // Méthodes helper
// Dans reservationsView.vue, remplacer les méthodes openReportModal() et getPropertySettings() par :

async openReportModal(displayName, period) {
  let data, propertyId;

  if (period === 'current') {
    data = this.groupedCurrentMonth[displayName];
    propertyId = this.selectedCurrentProperty;
  } else if (period === 'lastmonth') {
    data = this.groupedLastMonth[displayName];
    propertyId = this.selectedLastMonthProperty;
  } else if (period === 'historical') {
    data = this.searchResults;
    propertyId = this.selectedProperty || this.getPropertyIdByName(displayName);
  }

  if (!data || data.length === 0) {
    alert('No reservations found for this property and period.');
    return;
  }

  let periodLabel;
  if (period === 'current') {
    periodLabel = 'Current Month';
  } else if (period === 'lastmonth') {
    periodLabel = 'Last Month';
  } else if (period === 'historical') {
    // Use searchResultsMonth which stores the month that the current searchResults correspond to
    // This prevents showing wrong month when appliedFilters.month is outdated
    const monthToUse = this.searchResultsMonth || this.appliedFilters.month;
    const selectedMonthData = this.availableMonths.find(m => m.value === monthToUse);
    periodLabel = selectedMonthData ? selectedMonthData.label : 'Historical Month';
    console.log('🔍 Historical report - Using month:', monthToUse, 'Label:', periodLabel);
  }

  this.modalData = {
    propertyName: displayName,
    periodLabel: periodLabel,
    reservations: [...data],
    realPropertyId: propertyId,
    ownerName: ''
  };

  // Load owner name for this property
  try {
    const token = localStorage.getItem('token');
    const ownerRes = await axios.get(`${this.baseUrl}/api/pms/property-owner/${propertyId}/`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (ownerRes.data.success && ownerRes.data.data) {
      this.modalData.ownerName = ownerRes.data.data.full_legal_name || '';
    }
  } catch (e) { /* owner not configured */ }

  // ALWAYS reload settings from database to ensure they are up-to-date
  await this.reloadPropertySettingsFromDB(propertyId);

  // Now get the fresh settings from cache (just reloaded from DB)
  const savedSettings = this.getPropertySettings(propertyId);

  this.modalSettings = {
    managementFeeRate: parseFloat(savedSettings.managementFeeRate) || 16,
    managementFeeBase: savedSettings.managementFeeBase || 'net',
    managementFeeFixedAmount: parseFloat(savedSettings.managementFeeFixedAmount) || 500,
    vatRate: parseFloat(savedSettings.vatRate) || 20,
    vatBusinessModel: savedSettings.vatBusinessModel || 'agent',
    maintenance: parseFloat(savedSettings.maintenance) || 0,
    useCustomMaintenance: false,
    customMaintenanceAmount: 0,
    consumables: parseFloat(savedSettings.consumables) || 20,
    useCustomConsumables: false,
    customConsumablesAmount: 0,
    laundry: parseFloat(savedSettings.laundry) || 0,
    laundryCalculationMode: savedSettings.laundryCalculationMode || 'fixed',
    laundryPerBooking: parseFloat(savedSettings.laundryPerBooking) || 15,
    laundryVatApplicable: savedSettings.laundryVatApplicable !== undefined ? savedSettings.laundryVatApplicable : true,
    otherExpenses: parseFloat(savedSettings.otherExpenses) || 0,
    useCustomOtherExpenses: false,
    customOtherExpensesAmount: 0,
    cleaningMode: savedSettings.cleaningMode || 'rentlio',
    bookingCleaningFee: parseFloat(savedSettings.bookingCleaningFee) || 150,
    otherCleaningFee: parseFloat(savedSettings.otherCleaningFee) || 120,
    cleaningFee: parseFloat(savedSettings.cleaningFee) || 0,
    cleaningPerBooking: parseFloat(savedSettings.cleaningPerBooking) || 150,
    cleaningVatApplicable: savedSettings.cleaningVatApplicable !== undefined ? savedSettings.cleaningVatApplicable : false,
    cardFeeMode: savedSettings.cardFeeMode || 'rentlio',
    customCardFeeRate: parseFloat(savedSettings.customCardFeeRate) || 2.9,
    customCardFeeFixed: parseFloat(savedSettings.customCardFeeFixed) || 0.30,
    platformFeeMode: savedSettings.platformFeeMode || 'rentlio',
    applyCustomBookingCom: savedSettings.applyCustomBookingCom || false,
    bookingComCommissionRate: parseFloat(savedSettings.bookingComCommissionRate) || 15.00,
    applyCustomAirbnb: savedSettings.applyCustomAirbnb || false,
    airbnbCommissionRate: parseFloat(savedSettings.airbnbCommissionRate) || 3.00,
    applyCustomOther: savedSettings.applyCustomOther || false,
    otherPlatformCommissionRate: parseFloat(savedSettings.otherPlatformCommissionRate) || 10.00
  };

  this.originalSettings = { ...this.modalSettings };
  this.hasUnsavedChanges = false;

  // Vérifier s'il existe un montant fixe spécifique pour ce mois
  await this.checkMonthSpecificFee(propertyId, periodLabel);

  // Charger les dépenses supplémentaires (Other Maintenance) pour ce mois
  const [monthName, yearStr] = periodLabel.split(' ');
  const monthMap = {
    'January': 1, 'February': 2, 'March': 3, 'April': 4,
    'May': 5, 'June': 6, 'July': 7, 'August': 8,
    'September': 9, 'October': 10, 'November': 11, 'December': 12
  };

  let month, year;
  if (periodLabel === 'Current Month') {
    const now = new Date();
    month = now.getMonth() + 1;
    year = now.getFullYear();
  } else if (periodLabel === 'Last Month') {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    month = lastMonth.getMonth() + 1;
    year = lastMonth.getFullYear();
  } else if (monthName && yearStr) {
    month = monthMap[monthName];
    year = parseInt(yearStr);
  }

  if (month && year) {
    await this.loadPropertyExpenses(propertyId, year, month);

    // Auto-detect booking count and load monthly overrides if exist
    this.autoDetectBookingCount();
    await this.loadMonthlyLaundryOverride(propertyId, year, month);
    await this.loadMonthlyCleaningOverride(propertyId, year, month);
  }

  this.calculateMetrics();
  this.generatePreviewHTML();
  this.showReportModal = true;
},

// CORRECTION de getPropertyIdByName() pour garantir la correspondance:
getPropertyIdByName(propertyName) {
  console.log('Searching for property with name:', propertyName);
  console.log('Available properties:', this.properties.map(p => ({ id: p.id, name: p.name })));
  
  const property = this.properties.find(p => p.name === propertyName);
  const propertyId = property ? property.id : null;
  
  console.log('Found property ID:', propertyId, 'for name:', propertyName);
  
  if (!propertyId) {
    console.warn('Property not found! Available names:', this.properties.map(p => p.name));
  }
  
  return propertyId;
},

getPropertyAddress(propertyId) {
  if (!propertyId) return '';

  // Chercher la propriété dans la liste
  const property = this.properties.find(p => p.id == propertyId);
  if (!property) return '';

  // Rentlio retourne address, city, postalCode au premier niveau
  const parts = [];
  if (property.address) parts.push(property.address);
  if (property.city && property.postalCode) {
    parts.push(`${property.city} ${property.postalCode}`);
  } else if (property.city) {
    parts.push(property.city);
  }

  return parts.join(', ');
},

// CORRECTION de getPropertySettings() pour garantir le bon chargement:
// Load all property settings from API
async loadAllPropertySettings() {
  const token = localStorage.getItem('token');
  if (!token) return;

  const headers = { 'Authorization': `Bearer ${token}` };

  try {
    const response = await axios.get(
      `${this.baseUrl}/api/pms/settings/all/`,
      { headers }
    );

    if (response.data.success) {
      // Map backend data (snake_case) to frontend format (camelCase)
      const settingsData = response.data.data;
      this.allPropertySettings = {};

      for (const [propertyId, data] of Object.entries(settingsData)) {
        this.allPropertySettings[propertyId] = {
          managementFeeRate: parseFloat(data.management_fee_rate || 16),
          managementFeeBase: data.management_fee_base || 'net',
          managementFeeFixedAmount: parseFloat(data.management_fee_fixed_amount || 500),
          vatRate: parseFloat(data.vat_rate || 20),
          maintenance: parseFloat(data.maintenance || 0),
          consumables: parseFloat(data.consumables || 20),
          laundry: parseFloat(data.laundry_fee || 0),
          laundryCalculationMode: data.laundry_calculation_mode || 'fixed',
          laundryPerBooking: parseFloat(data.laundry_per_booking || 15),
          laundryVatApplicable: data.laundry_vat_applicable !== undefined ? data.laundry_vat_applicable : true,
          vatBusinessModel: data.vat_business_model || 'agent',
          otherExpenses: parseFloat(data.other_expenses || 0),
          cleaningMode: data.cleaning_mode || 'rentlio',
          bookingCleaningFee: parseFloat(data.booking_cleaning_fee || 150),
          otherCleaningFee: parseFloat(data.other_cleaning_fee || 120),
          cleaningFee: parseFloat(data.cleaning_fee || 0),
          cleaningPerBooking: parseFloat(data.cleaning_per_booking || 150),
          cleaningVatApplicable: data.cleaning_vat_applicable !== undefined ? data.cleaning_vat_applicable : false,
          cardFeeMode: data.card_fee_mode || 'rentlio',
          customCardFeeRate: parseFloat(data.custom_card_fee_rate || 2.9),
          customCardFeeFixed: parseFloat(data.custom_card_fee_fixed || 0.30),
          platformFeeMode: data.platform_fee_mode || 'rentlio',
          applyCustomBookingCom: data.apply_custom_booking_com || false,
          bookingComCommissionRate: parseFloat(data.booking_com_commission_rate || 15.00),
          applyCustomAirbnb: data.apply_custom_airbnb || false,
          airbnbCommissionRate: parseFloat(data.airbnb_commission_rate || 3.00),
          applyCustomOther: data.apply_custom_other || false,
          otherPlatformCommissionRate: parseFloat(data.other_platform_commission_rate || 10.00)
        };
      }

      console.log('Loaded property settings from API:', this.allPropertySettings);
    }
  } catch (error) {
    console.error('Error loading property settings from API:', error);
  }
},

// Reload settings for a specific property from database and update modal
async reloadPropertySettingsFromDB(propertyId) {
  const token = localStorage.getItem('token');
  if (!token) return;

  const headers = { 'Authorization': `Bearer ${token}` };

  try {
    const response = await axios.get(
      `${this.baseUrl}/api/pms/settings/${propertyId}/`,
      { headers }
    );

    if (response.data.success) {
      const data = response.data.data;

      // Update cache with fresh data from database
      this.allPropertySettings[propertyId] = {
        managementFeeRate: parseFloat(data.management_fee_rate || 16),
        managementFeeBase: data.management_fee_base || 'net',
        managementFeeFixedAmount: parseFloat(data.management_fee_fixed_amount || 500),
        vatRate: parseFloat(data.vat_rate || 20),
        maintenance: parseFloat(data.maintenance || 0),
        consumables: parseFloat(data.consumables || 20),
        laundry: parseFloat(data.laundry_fee || 0),
        laundryCalculationMode: data.laundry_calculation_mode || 'fixed',
        laundryPerBooking: parseFloat(data.laundry_per_booking || 15),
        laundryVatApplicable: data.laundry_vat_applicable !== undefined ? data.laundry_vat_applicable : true,
        vatBusinessModel: data.vat_business_model || 'agent',
        otherExpenses: parseFloat(data.other_expenses || 0),
        cleaningMode: data.cleaning_mode || 'rentlio',
        bookingCleaningFee: parseFloat(data.booking_cleaning_fee || 150),
        otherCleaningFee: parseFloat(data.other_cleaning_fee || 120),
        cleaningFee: parseFloat(data.cleaning_fee || 0),
        cleaningPerBooking: parseFloat(data.cleaning_per_booking || 150),
        cleaningVatApplicable: data.cleaning_vat_applicable !== undefined ? data.cleaning_vat_applicable : false,
        cardFeeMode: data.card_fee_mode || 'rentlio',
        customCardFeeRate: parseFloat(data.custom_card_fee_rate || 2.9),
        customCardFeeFixed: parseFloat(data.custom_card_fee_fixed || 0.30),
        platformFeeMode: data.platform_fee_mode || 'rentlio',
        applyCustomBookingCom: data.apply_custom_booking_com || false,
        bookingComCommissionRate: parseFloat(data.booking_com_commission_rate || 15.00),
        applyCustomAirbnb: data.apply_custom_airbnb || false,
        airbnbCommissionRate: parseFloat(data.airbnb_commission_rate || 3.00),
        applyCustomOther: data.apply_custom_other || false,
        otherPlatformCommissionRate: parseFloat(data.other_platform_commission_rate || 10.00)
      };

      // Update modal settings to reflect saved values
      this.modalSettings = { ...this.allPropertySettings[propertyId] };
      this.originalSettings = { ...this.allPropertySettings[propertyId] };

      console.log('✅ Reloaded settings from DB for property', propertyId);
    }
  } catch (error) {
    console.error('❌ Error reloading property settings from DB:', error);
  }
},

getPropertySettings(propertyId) {
  console.log('Getting settings for property ID:', propertyId);

  if (!propertyId) {
    console.log('No property ID, using defaults');
    return this.getDefaultSettings();
  }

  // Use settings from API if available
  if (this.allPropertySettings[propertyId]) {
    console.log('Using API settings for property', propertyId);
    return this.allPropertySettings[propertyId];
  }

  console.log('No settings found for this property, using defaults');
  return this.getDefaultSettings();
},

   getDefaultSettings() {
     return {
       managementFeeRate: 16.0,
       managementFeeBase: 'net',
       vatRate: 20.0,
       vatBusinessModel: 'agent',
       maintenance: 0.0,
       consumables: 20.0,
       laundry: 0.0,
       laundryCalculationMode: 'fixed',
       laundryPerBooking: 15.0,
       laundryVatApplicable: true,
       otherExpenses: 0.0,
       cleaningMode: 'rentlio',
       bookingCleaningFee: 150.0,
       otherCleaningFee: 120.0,
       cleaningFee: 0.0,
       cleaningPerBooking: 150.0,
       cleaningVatApplicable: false,
       cardFeeMode: 'rentlio',
       customCardFeeRate: 2.9,
       customCardFeeFixed: 0.00,
       platformFeeMode: 'rentlio',
       applyCustomBookingCom: false,
       bookingComCommissionRate: 15.00,
       applyCustomAirbnb: false,
       airbnbCommissionRate: 3.00,
       applyCustomOther: false,
       otherPlatformCommissionRate: 10.00
     };
   },

   // Existing Excel methods
   downloadReportPropertyExcel(unitName, period) {
     let data;
     if (period === 'current') {
       data = this.groupedCurrentMonth[unitName];
     } else if (period === 'lastmonth') {
       data = this.groupedLastMonth[unitName];
     } else if (period === 'historical') {
       data = this.searchResults;
     }
     this.generateReportExcel(data, `${unitName}_${period}_report`, unitName);
   },

   generateReportExcel(reservations, filename, propertyName) {
     const propertyId = this.getPropertyIdByName(propertyName);
     const propertySettings = this.getPropertySettings(propertyId);
     
     const data = reservations.map(item => {
       const amount = parseFloat(item.totalReservationPrice || 0);
       const fees = this.calculateCustomCommission(item, this.modalSettings);
       
       let cardFee = 0;
       if (item.otaChannelName == 'Booking.com') {
         if (propertySettings.cardFeeMode === 'rentlio') {
           const totalWithFees = parseFloat(item.totalCost || item.totalReservationPrice || 0);
           const basePrice = parseFloat(item.accommodationCost || 0);
           const vatAmount = parseFloat(item.vatAmount || 0);
           cardFee = Math.max(0, totalWithFees - basePrice - vatAmount);
         } else {
           cardFee = (amount * propertySettings.customCardFeeRate / 100);
         }
       }
       
       let outgoings = 0;
       const isExtension = (item.guestName || '').toLowerCase().includes('extension');

       if (isExtension) {
         outgoings = 0;
       } else {
         if (propertySettings.cleaningMode === 'rentlio') {
           outgoings = parseFloat(item.totalServicesPrice || 0);
         } else {
           // Custom Rates: Appliquer les frais uniquement au dernier segment
           const isLastSegment = !item.isSegment ||
             (item.segmentInfo?.part === item.segmentInfo?.total);

           if (isLastSegment) {
             const platform = item.otaChannelName || '';
             if (platform === 'Booking.com') {
               outgoings = propertySettings.bookingCleaningFee || 150;
             } else {
               outgoings = propertySettings.otherCleaningFee || 120;
             }
           } else {
             // Segment intermédiaire, pas de frais de nettoyage
             outgoings = 0;
           }
         }
       }
       
       const netIncome = amount - fees - cardFee;
       const grossProfit = netIncome - outgoings;
       const perNight = item.totalNights > 0 ? amount / item.totalNights : 0;

       return [
         item.guestName || '',
         item.otaChannelName || 'Direct',
         item.totalNights || 0,
         amount.toFixed(2),
         fees.toFixed(2),
         cardFee.toFixed(2),
         netIncome.toFixed(2),
         outgoings.toFixed(2),
         grossProfit.toFixed(2),
         perNight.toFixed(2)
       ];
     });

     const totalNights = data.reduce((sum, r) => sum + r[2], 0);
     const totalAmount = data.reduce((sum, r) => sum + parseFloat(r[3]), 0);
     const correctPerNight = data.length > 0 ? (data.reduce((sum, r) => sum + parseFloat(r[9]), 0) / data.length).toFixed(2) : '0.00';

     const totals = [
       'Totals',
       '',
       totalNights,
       totalAmount.toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[4]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[5]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[6]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[7]), 0).toFixed(2),
       data.reduce((sum, r) => sum + parseFloat(r[8]), 0).toFixed(2),
       correctPerNight
     ];

     const totalRevenue = parseFloat(totals[3]);
     const totalCardFees = parseFloat(totals[5]);
     const netIncomeTotal = parseFloat(totals[6]);
     const grossProfitTotal = parseFloat(totals[8]);
     
     let managementFee = 0;
     let managementFeeLabel = '';
     
     if (propertySettings.managementFeeBase === 'revenue') {
      managementFee = (totalRevenue * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Total Revenue)`;
    } else if (propertySettings.managementFeeBase === 'gross') {
      managementFee = (grossProfitTotal * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Gross Profit)`;
    } else {
      managementFee = (netIncomeTotal * propertySettings.managementFeeRate) / 100;
      managementFeeLabel = `Management fee (${propertySettings.managementFeeRate}% on Net Income)`;
    }

     const vat = (managementFee * propertySettings.vatRate) / 100;
     const maintenance = propertySettings.maintenance;
     const consumables = propertySettings.consumables;
     const otherExpenses = propertySettings.otherExpenses;
     const toClient = grossProfitTotal - managementFee - vat - maintenance - consumables - otherExpenses;

     const ws = XLSX.utils.aoa_to_sheet([
       [propertyName || 'Property Report'],
       [''],
       ['Booking Name', 'Source', 'No. Nights', 'Amount', 'Fees', 'Card Fee', 'Net Income', 'Outgoings', 'Gross Profit', 'Per Night'],
       ...data,
       totals,
       ['', '', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', 'Profit', '', grossProfitTotal.toFixed(2)],
       ['', '', '', '', '', '', '', '', '', ''],
       ['Days in month', '', 31, '', '', '', '', managementFeeLabel, '', managementFee.toFixed(2)],
       ['Occupancy', '', Math.round((totals[2] / 31) * 100) + '%', '', '', '', '', 'VAT', '', vat.toFixed(2)],
       ['', '', '', '', '', '', '', 'Maintenance', '', maintenance.toFixed(2)],
       ['', '', '', '', '', '', '', 'Consumables', '', consumables.toFixed(2)],
       ['', '', '', '', '', '', '', 'Other Expenses', '', otherExpenses.toFixed(2)],
       ['', '', '', '', '', '', '', '', '', ''],
       ['', '', '', '', '', '', '', 'To Client', '', toClient.toFixed(2)]
     ]);

     ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }];

     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, propertyName || 'Report');
     XLSX.writeFile(wb, `${filename}.xlsx`);
   },

   downloadPropertyExcel(unitName, period) {
     let data;
     if (period === 'current') {
       data = this.groupedCurrentMonth[unitName];
     } else if (period === 'lastmonth') {
       data = this.groupedLastMonth[unitName];
     } else if (period === 'historical') {
       data = this.searchResults;
     }
     this.generateExcelReport(data, `${unitName}_${period}_report`);
   },
   
   generateExcelReport(reservations, filename) {
     const data = reservations.map(item => ({
       'Booking Name': item.guestName,
       'Source': item.otaChannelName || 'Direct',
       'No. Nights': item.totalNights,
       'Amount': parseFloat(item.totalReservationPrice).toFixed(2),
       'Fees': this.calculateCustomCommission(item, this.modalSettings).toFixed(2),
       'Card Fee': parseFloat(item.cardFee || 0).toFixed(2),
       'Gross Income': parseFloat(item.totalReservationPrice).toFixed(2),
       'Outgoings': parseFloat(item.totalServicesPrice).toFixed(2),
       'Net Income': parseFloat(item.accommodationCost).toFixed(2),
       'Per Night': parseFloat(item.pricePerNight).toFixed(2),
       'Arrival Date': this.formatDate(item.arrivalDate),
       'Departure Date': this.formatDate(item.departureDate),
     }));

     const ws = XLSX.utils.json_to_sheet(data);
    
     const totalNightsExcel = reservations.reduce((sum, r) => sum + r.totalNights, 0);
     const totalAmountExcel = reservations.reduce((sum, r) => sum + parseFloat(r.totalReservationPrice), 0);
     const correctPerNightExcel = reservations.length > 0 ? (reservations.reduce((sum, r) => sum + parseFloat(r.pricePerNight), 0) / reservations.length).toFixed(2) : '0.00';

     XLSX.utils.sheet_add_aoa(ws, [
       ["", "", "", "", "", "", "", "", "", "", ""],
       ["Totals", "", 
        totalNightsExcel,
        totalAmountExcel.toFixed(2),
        reservations.reduce((sum, r) => sum + this.calculateCustomCommission(r, this.modalSettings), 0).toFixed(2),
        "",
        totalAmountExcel.toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.totalServicesPrice), 0).toFixed(2),
        reservations.reduce((sum, r) => sum + parseFloat(r.accommodationCost), 0).toFixed(2),
        correctPerNightExcel
       ]
     ], { origin: -1 });

     const wb = XLSX.utils.book_new();
     XLSX.utils.book_append_sheet(wb, ws, 'Report');
     XLSX.writeFile(wb, `${filename}.xlsx`);
   },

   // Méthodes de données existantes
   async fetchData() {
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Get selected PMS configuration from localStorage
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) {
       alert('No PMS configuration selected. Please select a PMS from the Hub.');
       this.$router.push('/');
       return;
     }

     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };

     try {
       const propertiesRes = await axios.get(`${this.baseUrl}/api/pms/properties/`, {
         headers,
         params: { pms_config_id: pmsConfigId }
       });
       this.properties = propertiesRes.data.properties.data;

       // Default to the first property of the current PMS config if nothing
       // is selected yet, or if the previous selection isn't part of this
       // config (avoids showing "Property NNNN — 0 bookings" for a stale id).
       const hasCurrent = this.selectedCurrentProperty
         && this.properties.some(p => p.id.toString() === this.selectedCurrentProperty.toString());
       if (!hasCurrent && this.properties.length > 0) {
         this.selectedCurrentProperty = this.properties[0].id.toString();
       }
       const hasLast = this.selectedLastMonthProperty
         && this.properties.some(p => p.id.toString() === this.selectedLastMonthProperty.toString());
       if (!hasLast && this.properties.length > 0) {
         this.selectedLastMonthProperty = this.properties[0].id.toString();
       }

       this.loading = true;
       const currentRes = await axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: this.selectedCurrentProperty }
       });
       this.currentMonthReservations = Array.isArray(currentRes.data.reservations)
         ? currentRes.data.reservations
         : Object.values(currentRes.data.reservations);
       this.loading = false;

       this.loadingLastMonth = true;
       const lastMonthRes = await axios.get(`${this.baseUrl}/api/pms/reports/lastmonth/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: this.selectedLastMonthProperty }
       });
       this.lastMonthReservations = Array.isArray(lastMonthRes.data.reservations)
         ? lastMonthRes.data.reservations
         : Object.values(lastMonthRes.data.reservations);
       this.loadingLastMonth = false;

     } catch (error) {
       console.error('Error fetching data:', error);
       if (error.response && error.response.status === 401) {
         localStorage.removeItem('token');
         this.$router.push('/login');
       }
     }
   },

   onSelectCurrent(value) {
     this.selectedCurrentProperty = value;
     this.currentDropdownOpen = false;
     this.onCurrentPropertyChange();
   },

   onSelectLastMonth(value) {
     this.selectedLastMonthProperty = value;
     this.lastDropdownOpen = false;
     this.onLastMonthPropertyChange();
   },

   monthForPeriod(period) {
     const targetType = period === 'current' ? 'current' : 'lastmonth';
     return this.availableMonths.find(m => m.type === targetType) || null;
   },

   async refreshGroupLockStatus(period) {
     const group = period === 'current' ? this.currentGroup : this.lastMonthGroup;
     if (!group) {
       if (period === 'current') this.currentGroupLockStatus = null;
       else this.lastGroupLockStatus = null;
       return;
     }
     const monthDef = this.monthForPeriod(period);
     if (!monthDef) return;
     const [year, month] = monthDef.value.split('-').map(Number);
     try {
       const status = await propertyGroups.lockStatus(group.id, year, month);
       if (period === 'current') this.currentGroupLockStatus = status;
       else this.lastGroupLockStatus = status;
     } catch (err) {
       console.error('Lock status fetch failed:', err);
       if (period === 'current') this.currentGroupLockStatus = null;
       else this.lastGroupLockStatus = null;
     }
   },

   async toggleGroupLock(period) {
     const group = period === 'current' ? this.currentGroup : this.lastMonthGroup;
     if (!group) return;
     const status = period === 'current' ? this.currentGroupLockStatus : this.lastGroupLockStatus;
     const monthDef = this.monthForPeriod(period);
     if (!monthDef) return;
     const [year, month] = monthDef.value.split('-').map(Number);
     this.lockingGroup = true;
     try {
       if (status && status.locked) {
         if (!confirm(`Unlock ${group.name} for ${monthDef.label}? Reports will become editable again.`)) {
           this.lockingGroup = false;
           return;
         }
         await propertyGroups.unlock(group.id, year, month);
       } else {
         if (!confirm(`Lock ${group.name} for ${monthDef.label}? Reports will become final.`)) {
           this.lockingGroup = false;
           return;
         }
         // Persist any pending sidebar changes so locked report matches preview.
         if (this.showGroupReportModal && this.groupSettingsModified) {
           await this.saveGroupSettings();
           if (this.groupSettingsModified) {
             // Save failed and surfaced an alert; bail out of the lock too.
             this.lockingGroup = false;
             return;
           }
         }
         await propertyGroups.lock(group.id, year, month);
       }
       await this.refreshGroupLockStatus(period);
     } catch (err) {
       console.error('Lock toggle failed:', err);
       alert('Failed to update lock state: ' + (err.response?.data?.detail || err.message));
     } finally {
       this.lockingGroup = false;
     }
   },

   sourceBadgeClass(channel) {
     const c = (channel || '').toString().toLowerCase();
     if (c.includes('airbnb')) return 'src-airbnb';
     if (c.includes('booking')) return 'src-booking';
     return 'src-direct';
   },

   parseSelection(value) {
     if (typeof value !== 'string' || !value) {
       return { type: 'property', id: value, raw: value };
     }
     if (value.startsWith('group:')) {
       return { type: 'group', id: parseInt(value.slice(6), 10), raw: value };
     }
     if (value.startsWith('prop:')) {
       return { type: 'property', id: value.slice(5), raw: value };
     }
     return { type: 'property', id: value, raw: value };
   },

   async fetchGroups() {
     try {
       const data = await propertyGroups.list();
       this.groups = Array.isArray(data) ? data : [];
     } catch (err) {
       console.error('Error fetching property groups:', err);
       this.groups = [];
     }
   },

   memberPropertyIds(group) {
     if (!group || !Array.isArray(group.memberships)) return [];
     return group.memberships.map(m => m.property_id);
   },

   propertyNameById(propertyId) {
     const prop = this.properties.find(p => p.id.toString() === propertyId.toString());
     return prop ? prop.name : `Property ${propertyId}`;
   },

   bookingsByPropertyFromGroup(group, byPropertyMap) {
     const ids = this.memberPropertyIds(group);
     const safeMap = byPropertyMap || {};
     const result = [];
     for (const pid of ids) {
       result.push({
         propertyId: pid,
         propertyName: this.propertyNameById(pid),
         reservations: safeMap[pid] || [],
       });
     }
     return result;
   },

   buildGroupSummary(group, byPropertyMap) {
     if (!group) return null;
     const ids = this.memberPropertyIds(group);
     const safeMap = byPropertyMap || {};
     let totalBookings = 0;
     let totalNights = 0;
     let totalRevenue = 0;
     for (const pid of ids) {
       const bookings = safeMap[pid] || [];
       totalBookings += bookings.length;
       totalNights += this.getTotalNights(bookings);
       totalRevenue += this.getTotalRevenue(bookings);
     }
     return {
       group,
       totalBookings,
       totalNights,
       totalRevenue,
       memberCount: ids.length,
     };
   },

   async loadGroupBookings(group, period) {
     const token = localStorage.getItem('token');
     if (!token) return {};
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) return {};
     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };
     const endpoint = period === 'current' ? 'current' : 'lastmonth';
     const ids = this.memberPropertyIds(group);
     const byProperty = {};
     await Promise.all(ids.map(async (pid) => {
       try {
         const res = await axios.get(`${this.baseUrl}/api/pms/reports/${endpoint}/`, {
           headers,
           params: { pms_config_id: pmsConfigId, property_id: pid },
         });
         byProperty[pid] = Array.isArray(res.data.reservations)
           ? res.data.reservations
           : Object.values(res.data.reservations);
       } catch (err) {
         console.error(`Error fetching ${endpoint} bookings for property ${pid}:`, err);
         byProperty[pid] = [];
       }
     }));
     return byProperty;
   },

   // --- Group live-preview: compute per-member report_data from live bookings ---
   // Same shape as the backend expects for `members_data` in the preview/generate
   // endpoints. Kept standalone (no modalData mutation) so it can run per member
   // without breaking single-property state.
   buildMemberReportPayload(propertyId, reservations, settings) {
     const s = settings || this.getDefaultSettings();
     const list = Array.isArray(reservations) ? reservations : [];

     const calculateOutgoings = (res) => {
       const isExtension = (res.guestName || '').toLowerCase().includes('extension');
       if (isExtension) return 0;
       if (s.cleaningMode === 'rentlio') {
         return parseFloat(res.totalServicesPrice || 0);
       }
       const isLastSegment = !res.isSegment || (res.segmentInfo?.part === res.segmentInfo?.total);
       if (!isLastSegment) return 0;
       const platform = res.otaChannelName || '';
       if (platform === 'Booking.com') return s.bookingCleaningFee || 150;
       return s.otherCleaningFee || 120;
     };

     const bookings = list.map(res => {
       const amount = parseFloat(res.totalReservationPrice || 0);
       const commission = this.calculateCustomCommission(res, s);
       const cardFee = parseFloat(res.cardFee || 0);
       const outgoings = calculateOutgoings(res);
       const netIncome = amount - commission - cardFee;
       const grossProfit = netIncome - outgoings;
       return {
         guest_name: res.guestName || 'Unknown Guest',
         channel_name: res.otaChannelName || 'Direct',
         total_nights: res.totalNights || 0,
         total_amount: amount,
         channel_commission: commission,
         card_fee: cardFee,
         net_income: netIncome,
         total_services_price: outgoings,
         gross_profit: grossProfit,
         price_per_night: parseFloat(res.pricePerNight || 0),
         net_per_night: (res.totalNights || 0) > 0 ? netIncome / res.totalNights : 0,
       };
     });

     const sum = (key) => bookings.reduce((acc, b) => acc + (b[key] || 0), 0);
     const totalAmount = sum('total_amount');
     const totalPlatformFees = sum('channel_commission');
     const totalCardFees = sum('card_fee');
     const totalNetIncome = sum('net_income');
     const totalOutgoings = sum('total_services_price');
     const totalGrossProfit = sum('gross_profit');

     let managementFees = 0;
     if (s.managementFeeBase === 'revenue') {
       managementFees = totalAmount * (s.managementFeeRate / 100);
     } else if (s.managementFeeBase === 'gross') {
       managementFees = totalGrossProfit * (s.managementFeeRate / 100);
     } else if (s.managementFeeBase === 'grossFixed') {
       managementFees = parseFloat(s.managementFeeFixedAmount || 0);
     } else {
       managementFees = totalNetIncome * (s.managementFeeRate / 100);
     }

     const consumables = parseFloat(s.consumables || 0);
     const laundry = parseFloat(s.laundry || 0);
     const maintenance = parseFloat(s.maintenance || 0);
     const otherExpenses = parseFloat(s.otherExpenses || 0);
     const cleaningSupplied = 0;

     const laundryTaxable = s.laundryVatApplicable ? laundry : 0;
     const cleaningTaxable = s.cleaningVatApplicable ? cleaningSupplied : 0;
     const subTotal = managementFees + consumables + laundryTaxable + cleaningTaxable;
     // VAT matches single-property calc (line 6667): applied on management fees only.
     const vat = managementFees * ((s.vatRate || 20) / 100);
     const settlementAmount = totalGrossProfit - managementFees - vat - maintenance - consumables - otherExpenses - laundry;

     const suppliedServices = { management_fees: managementFees };
     if (consumables > 0) suppliedServices.consumables = consumables;
     if (laundry > 0 && s.laundryVatApplicable) suppliedServices.laundry = laundry;
     if (cleaningSupplied > 0 && s.cleaningVatApplicable) suppliedServices.cleaning = cleaningSupplied;

     const propertyName = this.propertyNameById(propertyId);
     const propertyAddress = this.getPropertyAddress ? this.getPropertyAddress(propertyId) : '';

     return {
       property_id: propertyId,
       property_name: propertyName,
       property_address: propertyAddress,
       owner_name: '',
       bookings,
       expenses: [],
       totals: {
         total_guest_payments: totalAmount,
         platform_fees: totalPlatformFees,
         card_fees: totalCardFees,
         cleaning_fees: totalOutgoings,
         cleaning: cleaningSupplied,
         gross_profit: totalGrossProfit,
         management_fees: managementFees,
         consumables,
         laundry,
         maintenance,
         other_expenses: otherExpenses,
         settlement_amount: settlementAmount,
       },
       vat_breakdown: {
         supplied_services: suppliedServices,
         total_taxable: subTotal,
         total_vat: vat,
         vat_rate: s.vatRate || 20,
         business_model: s.vatBusinessModel || 'agent',
       },
       property_settings: null,
     };
   },

   // Build the members_data dict expected by the backend preview/generate endpoints.
   // Uses cached bookings loaded by loadGroupBookings + per-member settings.
   buildGroupMembersData(group, period) {
     if (!group) return {};
     const map = period === 'current'
       ? this.currentGroupBookingsByProperty
       : this.lastMonthGroupBookingsByProperty;
     const ids = this.memberPropertyIds(group);
     const result = {};
     for (const pid of ids) {
       const reservations = (map || {})[pid] || [];
       const settings = this.getPropertySettings(pid);
       result[pid] = this.buildMemberReportPayload(pid, reservations, settings);
     }
     return result;
   },

   async onCurrentPropertyChange() {
     const sel = this.parseSelection(this.selectedCurrentProperty);
     if (sel.type === 'group') {
       const group = this.groups.find(g => g.id === sel.id);
       if (!group) return;
       this.loading = true;
       this.currentGroupBookingsByProperty = await this.loadGroupBookings(group, 'current');
       this.currentMonthReservations = [];
       this.loading = false;
       this.refreshGroupLockStatus('current');
       return;
     }
     this.currentGroupLockStatus = null;
     const token = localStorage.getItem('token');
     if (!token) return;
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) return;
     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };
     try {
       this.loading = true;
       const currentRes = await axios.get(`${this.baseUrl}/api/pms/reports/current/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: sel.id }
       });
       this.currentMonthReservations = Array.isArray(currentRes.data.reservations)
         ? currentRes.data.reservations
         : Object.values(currentRes.data.reservations);
       this.currentGroupBookingsByProperty = {};
       this.loading = false;
     } catch (error) {
       console.error('Error fetching current month data:', error);
       this.loading = false;
     }
   },

   async onLastMonthPropertyChange() {
     const sel = this.parseSelection(this.selectedLastMonthProperty);
     if (sel.type === 'group') {
       const group = this.groups.find(g => g.id === sel.id);
       if (!group) return;
       this.loadingLastMonth = true;
       this.lastMonthGroupBookingsByProperty = await this.loadGroupBookings(group, 'lastmonth');
       this.lastMonthReservations = [];
       this.loadingLastMonth = false;
       this.refreshGroupLockStatus('last');
       return;
     }
     this.lastGroupLockStatus = null;
     const token = localStorage.getItem('token');
     if (!token) return;
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) return;
     const pmsConfigId = selectedPms.id;
     const headers = { 'Authorization': `Bearer ${token}` };
     try {
       this.loadingLastMonth = true;
       const lastMonthRes = await axios.get(`${this.baseUrl}/api/pms/reports/lastmonth/`, {
         headers,
         params: { pms_config_id: pmsConfigId, property_id: sel.id }
       });
       this.lastMonthReservations = Array.isArray(lastMonthRes.data.reservations)
         ? lastMonthRes.data.reservations
         : Object.values(lastMonthRes.data.reservations);
       this.lastMonthGroupBookingsByProperty = {};
       this.loadingLastMonth = false;
     } catch (error) {
       console.error('Error fetching last month data:', error);
       this.loadingLastMonth = false;
     }
   },

   async applyFilters() {
     if (!this.selectedMonth) {
       alert('Please select a month');
       return;
     }

     if (!this.selectedProperty) {
       alert('Please select a property');
       return;
     }

     this.appliedFilters = {
       property: this.selectedProperty,
       month: this.selectedMonth
     };

     // Store the month for searchResults to ensure openReportModal uses the correct month
     this.searchResultsMonth = this.selectedMonth;

     this.loadingHistory = true;
     this.searchResults = [];

     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Get selected PMS configuration
     const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
     if (!selectedPms || !selectedPms.id) {
       alert('No PMS configuration selected. Please select a PMS from the Hub.');
       return;
     }
     const pmsConfigId = selectedPms.id;

     const headers = { 'Authorization': `Bearer ${token}` };
     
     try {
       console.log(`Fetching reservations for month: ${this.selectedMonth}, property: ${this.selectedProperty}`);

       // Trouver le type de mois sélectionné
       const selectedMonthData = this.availableMonths.find(m => m.value === this.selectedMonth);
       const monthType = selectedMonthData ? selectedMonthData.type : 'historical';

       let response;
       let url;
       
       if (monthType === 'current') {
         // Use the current reports endpoint
         console.log('Using current month report endpoint');
         url = `${this.baseUrl}/api/pms/reports/current/?pms_config_id=${pmsConfigId}&property_id=${this.selectedProperty}`;
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations || [];
       } else if (monthType === 'lastmonth') {
         // Use the last month reports endpoint
         console.log('Using last month report endpoint');
         url = `${this.baseUrl}/api/pms/reports/lastmonth/?pms_config_id=${pmsConfigId}&property_id=${this.selectedProperty}`;
         response = await axios.get(url, { headers });
         this.searchResults = response.data.reservations || [];
       } else {
         // Pour les mois historiques, calculer les dates de début et fin du mois
         console.log('Using generic reservations endpoint for historical month');
         console.log('Selected Property ID:', this.selectedProperty);
         const year = parseInt(this.selectedMonth.substring(0, 4));
         const month = parseInt(this.selectedMonth.substring(5, 7)); // 1-12

         // Calculer le premier et dernier jour du mois
         const firstDay = 1;
         const lastDay = new Date(year, month, 0).getDate(); // Dernier jour du mois

         // Formater les dates en YYYY-MM-DD
         const dateFrom = `${year}-${month.toString().padStart(2, '0')}-01`;
         const dateTo = `${year}-${month.toString().padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;

         url = `${this.baseUrl}/api/pms/reservations/?date_from=${dateFrom}&date_to=${dateTo}&per_page=200&order_by=arrivalDate&order_direction=ASC&property_id=${this.selectedProperty}&pms_config_id=${pmsConfigId}`;
         console.log('Full URL being called:', url);

         response = await axios.get(url, { headers });
         console.log('Response data:', response.data);
         const rawHistoricalResults = response.data.reservations?.data || [];

         // Garder uniquement les segments dont le mois d'arrivée correspond au mois recherché.
         // Sans ce filtre, une réservation multi-mois remonte à la fois comme 1/2 et 2/2
         // dans le même rapport historique (les endpoints current/lastmonth appliquent le
         // même filtre côté backend).
         const targetMonth = `${year}-${month.toString().padStart(2, '0')}`;
         this.searchResults = rawHistoricalResults.filter(r => {
           const arrivalTs = r.arrivalDate;
           if (!arrivalTs) return true;
           const d = new Date(arrivalTs * 1000);
           const ym = `${d.getUTCFullYear()}-${(d.getUTCMonth() + 1).toString().padStart(2, '0')}`;
           return ym === targetMonth;
         });
         console.log('Search results after filtering:', this.searchResults.length, 'reservations');
       }

       this.currentPage = 1;
       console.log(`Final results: ${this.searchResults.length} reservations`);
       console.log('Used URL:', url);
       
     } catch (error) {
       console.error('Error fetching reservations:', error);
       alert('Error fetching reservations. Please try again.');
     } finally {
       this.loadingHistory = false;
     }
   },

   clearFilters() {
     this.selectedProperty = '';
     this.selectedMonth = '';
     this.appliedFilters = {
       property: '',
       month: ''
     };
     this.searchResultsMonth = '';
     this.searchResults = [];
     this.currentPage = 1;
     this.loadingHistory = false;
   },

   prevPage() {
     if (this.currentPage > 1) {
       this.currentPage--;
     }
   },
   
   nextPage() {
     if (this.currentPage < this.totalPages) {
       this.currentPage++;
     }
   },
   
 handleNumericInput(fieldName, event) {
    const value = event.target.value;
    
    // Si le champ est complètement vide, mettre 0
    if (value === '' || value === null || value === undefined) {
      this.modalSettings[fieldName] = 0;
      return;
    }
    
    // Convertir en nombre et s'assurer que c'est valide
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      this.modalSettings[fieldName] = 0;
    } else {
      this.modalSettings[fieldName] = numValue;
    }
  },
 

ensureMinimumValue(fieldName, minimumValue = 0) {
    // Vérifier quand l'utilisateur quitte le champ
    const currentValue = this.modalSettings[fieldName];
    
    if (currentValue === null || currentValue === undefined || currentValue === '' || isNaN(currentValue) || currentValue < minimumValue) {
      this.modalSettings[fieldName] = minimumValue;
      this.markAsModified();
      this.calculateMetrics();
    }
  
},

 // Contacts management methods
 async loadContacts() {
   const token = localStorage.getItem('token');
   if (!token) return;

   try {
     const response = await axios.get(`${this.baseUrl}/api/pms/contacts/`, {
       headers: { 'Authorization': `Bearer ${token}` }
     });

     if (response.data.status === 'success') {
       this.contacts = response.data.data;
     }
   } catch (error) {
     console.error('Error loading contacts:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     }
   }
 },

 async addContact() {
   if (!this.newContact.name || !this.newContact.email) {
     alert('Please fill in both name and email');
     return;
   }

   // Validation du format d'email
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   if (!emailRegex.test(this.newContact.email)) {
     alert('Please enter a valid email address (e.g., user@domain.com)');
     return;
   }

   const token = localStorage.getItem('token');
   if (!token) {
     this.$router.push('/login');
     return;
   }

   try {
     const response = await axios.post(`${this.baseUrl}/api/pms/contacts/`, {
       name: this.newContact.name,
       email: this.newContact.email
     }, {
       headers: { 'Authorization': `Bearer ${token}` }
     });

     if (response.data.status === 'success') {
       // Ajouter le nouveau contact à la liste
       this.contacts.push(response.data.data);
       this.newContact = { name: '', email: '' };
       alert(`Contact "${response.data.data.name}" added successfully!`);
     }
   } catch (error) {
     console.error('Error adding contact:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     } else if (error.response && error.response.data.errors) {
       const errorMsg = error.response.data.errors.email ?
         error.response.data.errors.email[0] :
         'Failed to add contact';
       alert(errorMsg);
     } else {
       alert('Failed to add contact. Please try again.');
     }
   }
 },

 editContact(contact) {
   this.editingContact = { ...contact };
 },

 async saveEditContact() {
   const token = localStorage.getItem('token');
   if (!token) {
     this.$router.push('/login');
     return;
   }

   try {
     const response = await axios.put(
       `${this.baseUrl}/api/pms/contacts/${this.editingContact.id}/`,
       {
         name: this.editingContact.name,
         email: this.editingContact.email
       },
       {
         headers: { 'Authorization': `Bearer ${token}` }
       }
     );

     if (response.data.status === 'success') {
       // Mettre à jour le contact dans la liste
       const index = this.contacts.findIndex(c => c.id === this.editingContact.id);
       if (index !== -1) {
         this.contacts[index] = response.data.data;
       }
       this.editingContact = null;
       alert('Contact updated successfully!');
     }
   } catch (error) {
     console.error('Error updating contact:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     } else if (error.response && error.response.data.errors) {
       const errorMsg = error.response.data.errors.email ?
         error.response.data.errors.email[0] :
         'Failed to update contact';
       alert(errorMsg);
     } else {
       alert('Failed to update contact. Please try again.');
     }
   }
 },

 async deleteContact(contactId) {
   if (!confirm('Are you sure you want to delete this contact?')) {
     return;
   }

   const token = localStorage.getItem('token');
   if (!token) {
     this.$router.push('/login');
     return;
   }

   try {
     const response = await axios.delete(
       `${this.baseUrl}/api/pms/contacts/${contactId}/`,
       {
         headers: { 'Authorization': `Bearer ${token}` }
       }
     );

     if (response.data.status === 'success') {
       // Retirer le contact de la liste
       this.contacts = this.contacts.filter(c => c.id !== contactId);
       alert('Contact deleted successfully!');
     }
   } catch (error) {
     console.error('Error deleting contact:', error);
     if (error.response && error.response.status === 401) {
       this.$router.push('/login');
     } else {
       alert('Failed to delete contact. Please try again.');
     }
   }
 },

 openContactsModal() {
   this.showContactsModal = true;
 },

 closeContactsModal() {
   this.showContactsModal = false;
   this.editingContact = null;
   this.newContact = { name: '', email: '' };
 },

 // Méthodes pour gérer le dernier contact sélectionné
 getLastSelectedContact() {
   const lastContactId = localStorage.getItem('lastSelectedContactId');
   if (lastContactId) {
     return this.contacts.find(c => c.id.toString() === lastContactId);
   }
   return null;
 },

 setLastSelectedContact(contact) {
   if (contact && contact.id) {
     localStorage.setItem('lastSelectedContactId', contact.id.toString());
   }
 },

 
 formatContactDate(dateString) {
   const date = new Date(dateString);
   return date.toLocaleDateString('en-US', { 
     month: 'short', 
     day: 'numeric',
     year: 'numeric'
   });
 },

 async sendReportByEmail() {
   if (!this.emailData.contact) {
     alert('Please select a contact');
     return;
   }

   this.setLastSelectedContact(this.emailData.contact);
   this.sendingEmail = true;
   
   // Afficher immédiatement le popup de confirmation
   this.displayEmailSentPopup();
   
   try {
     const token = localStorage.getItem('token');
     if (!token) {
       this.$router.push('/login');
       return;
     }

     // Préparer le message du rapport mensuel en anglais
     const hasReceipts = this.propertyExpenses.some(expense => expense.receipt_file);
     const receiptsMessage = hasReceipts
       ? '<p style="background: #fff8e1; padding: 10px; border-left: 4px solid #ffd54f; margin: 15px 0;"><strong>📎 Note:</strong> Supporting documents (receipts) for additional maintenance expenses are attached to this email for your reference.</p>'
       : '';

     const message = `
       <h2>Dear ${this.emailData.contact.name},</h2>
       <p>Please find attached your monthly property report for <strong>${this.emailData.propertyName}</strong> for the period <strong>${this.emailData.period}</strong>.</p>

       ${receiptsMessage}

       <div style="background: #f8fafc; padding: 15px; border-radius: 8px; margin: 20px 0;">
         <h3 style="margin: 0 0 10px 0; color: #667eea;">📊 Report Summary</h3>
         <p style="margin: 5px 0;"><strong>Property:</strong> ${this.emailData.propertyName}</p>
         <p style="margin: 5px 0;"><strong>Period:</strong> ${this.emailData.period}</p>
         <p style="margin: 5px 0;"><strong>Report Type:</strong> ${this.emailData.reportType}</p>
       </div>

       <p>The report includes detailed information about:</p>
       <ul>
         <li>Booking overview and revenue analysis</li>
         <li>Income and direct expenses breakdown</li>
         <li>Management fees and operational costs</li>
         <li>Settlement summary</li>
       </ul>

       <p style="margin-top: 20px;">If you have any questions about this report, please don't hesitate to contact us.</p>

       <p>Best regards,<br>
       <strong>Sojourn Executive Team</strong></p>
     `;

     const emailPayload = {
       to: this.emailData.contact.email,
       subject: `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`,
       message: message,
       fromName: "Sojourn Reports"
     };

     // Use NEW VAT-aware endpoint for PDF generation and email sending
     if (this.emailOptions.includePDF) {
       try {
         console.log('Preparing report data for NEW VAT-aware PDF generation...');

         // Use the new prepareBookingsForBackend() function
         const reportData = this.prepareBookingsForBackend();

         // Add email configuration
         reportData.send_email = true;
         reportData.to_email = this.emailData.contact.email;
         reportData.subject = `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`;

         // Build email message
         reportData.email_message = message;

         // Add personal message if provided
         if (this.emailOptions.message && this.emailOptions.message.trim()) {
           reportData.email_message = `
             <div style="background: #e8f4f8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
               <h4 style="margin: 0 0 10px 0; color: #2c5282;">Personal Message:</h4>
               <p style="margin: 0; font-style: italic;">${this.emailOptions.message}</p>
             </div>
             ${reportData.email_message}
           `;
         }

         console.log('Report data prepared, calling /api/pms/generate-report-vat/');

         // Validation de l'email
         const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
         if (!reportData.to_email || !emailRegex.test(reportData.to_email)) {
           throw new Error(`Invalid email address: "${reportData.to_email}"`);
         }

         // Vérifier le token avant l'envoi
         if (!token) {
           throw new Error('Authentication token is missing');
         }

         const response = await axios.post(`${this.baseUrl}/api/pms/generate-report-vat/`, reportData, {
           headers: {
             'Authorization': `Bearer ${token}`,
             'Content-Type': 'application/json'
           },
           timeout: 480000, // 8 minutes timeout
           maxContentLength: 50 * 1024 * 1024, // 50MB max
         });

         console.log('API Response:', response.data);

         if (response.data.success) {
           console.log('✅ Email sent successfully with VAT-aware PDF');
           // Pas d'alert car le popup s'affiche déjà
         } else {
           throw new Error(response.data.message || 'Failed to send email');
         }

       } catch (error) {
         console.error('Error sending email with VAT system:', error);
         throw error;
       }
     } else {
       // Send email without PDF using old endpoint
       const emailPayloadSimple = {
         to: this.emailData.contact.email,
         subject: `Monthly Report - ${this.emailData.propertyName} - ${this.emailData.period}`,
         message: message,
         fromName: "Sojourn Reports"
       };

       // Add personal message if provided
       if (this.emailOptions.message && this.emailOptions.message.trim()) {
         emailPayloadSimple.message = `
           <div style="background: #e8f4f8; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
             <h4 style="margin: 0 0 10px 0; color: #2c5282;">Personal Message:</h4>
             <p style="margin: 0; font-style: italic;">${this.emailOptions.message}</p>
           </div>
           ${emailPayloadSimple.message}
         `;
       }

       // Get selected PMS configuration
       const selectedPms = JSON.parse(localStorage.getItem('selectedPmsConfig'));
       if (!selectedPms || !selectedPms.id) {
         throw new Error('No PMS configuration selected');
       }
       emailPayloadSimple.pms_config_id = selectedPms.id;

       const response = await axios.post(`${this.baseUrl}/api/pms/send-email/`, emailPayloadSimple, {
         headers: {
           'Authorization': `Bearer ${token}`,
           'Content-Type': 'application/json'
         }
       });

       if (!(response.status === 200 && response.data && response.data.status === 'success')) {
         throw new Error(response.data?.message || 'Email sending failed');
       }
     }

     // Success - email sent
     this.sendingEmail = false;
     this.savedReportData = null;
   } catch (error) {
     console.error('Error sending email:', error);
     this.sendingEmail = false;
     this.savedReportData = null; // Nettoyer en cas d'erreur aussi
     let errorMessage = 'Failed to send email. Please try again.';
     
     if (error.response?.data?.message) {
       errorMessage = error.response.data.message;
     } else if (error.message) {
       errorMessage = error.message;
     }
     
     // alert(`❌ Error: ${errorMessage}`);
     console.error('Email sending error:', errorMessage);
   } finally {
     this.sendingEmail = false;
   }
 },

 // Méthodes pour Email Modal avec contacts
 openEmailModal(reportType, propertyName, period) {
   // Recharger les contacts depuis localStorage au cas où ils auraient été modifiés
   this.loadContacts();
   
   // Essayer de pré-sélectionner le dernier contact utilisé
   const lastContact = this.getLastSelectedContact();
   
   // Configurer les données du modal email
   this.emailData = {
     contact: lastContact,
     reportType: reportType,
     propertyName: propertyName,
     period: period
   };
   
   // Réinitialiser les options email
   this.emailOptions = {
     includeExcel: false,
     includePDF: true,
     message: ''
   };
   
   // Ouvrir le modal
   this.showEmailModal = true;
 },

 closeEmailModal() {
   this.showEmailModal = false;
   this.emailData = {
     contact: null,
     reportType: '',
     propertyName: '',
     period: ''
   };
 },

 displayEmailSentPopup() {
   // Fermer le modal d'email
   this.showEmailModal = false;
   
   // Afficher le popup de confirmation
   this.showEmailSentPopup = true;
   
   // Fermer automatiquement après 8 secondes
   setTimeout(() => {
     this.showEmailSentPopup = false;
   }, 8000);
 },

 closeEmailSentPopup() {
   this.showEmailSentPopup = false;
 },

 async generatePDFForEmail(propertyName, period) {
   try {
     // Utiliser la même logique que downloadPDF mais créer un File object
     const jsPDF = (await import('jspdf')).default;
     const html2canvas = (await import('html2canvas')).default;

     // Utiliser EXACTEMENT la même logique que downloadPDF()
     const iframe = document.createElement('iframe');
     iframe.style.cssText = `
       position: absolute !important;
       top: -10000px !important;
       left: -10000px !important;
       width: 210mm !important;
       height: 297mm !important;
       border: none !important;
       opacity: 0 !important;
       pointer-events: none !important;
     `;
     
     document.body.appendChild(iframe);
     
     // Utiliser la même fonction que downloadPDF()
     const pdfHTML = this.generatePDFHTML();
     iframe.contentDocument.open();
     iframe.contentDocument.write(pdfHTML);
     iframe.contentDocument.close();
     
     await new Promise(resolve => {
       iframe.onload = resolve;
       setTimeout(resolve, 500);
     });

     const canvas = await html2canvas(iframe.contentDocument.body, {
       scale: 1,           // Réduit de 2 à 1 pour réduire la taille
       useCORS: true,
       backgroundColor: '#ffffff',
       width: 794,
       height: 1123,
       logging: false
     });

     const pdf = new jsPDF('p', 'mm', 'a4');
     const imgData = canvas.toDataURL('image/jpeg', 0.7);  // JPEG avec compression 70%
     
     const imgWidth = 210;
     const pageHeight = 295;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;
     let heightLeft = imgHeight;
     let position = 0;

     pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
     heightLeft -= pageHeight;

     while (heightLeft >= 0) {
       position = heightLeft - imgHeight;
       pdf.addPage();
       pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;
     }

     document.body.removeChild(iframe);
     
     // Retourner le blob PDF pour l'email (même PDF que downloadPDF())
     return pdf.output('blob');

   } catch (error) {
     console.error('Error generating PDF for email:', error);
     throw error;
   }
 },

 // Nouvelle méthode pour envoyer l'email depuis le modal avec le PDF déjà généré
 sendReportByEmailFromModal() {
   console.log('=== DEBUT sendReportByEmailFromModal ===');
   console.log('modalData AVANT sauvegarde:', this.modalData);
   console.log('Nombre de reservations:', this.modalData?.reservations?.length);

   // IMPORTANT: Sauvegarder les données du rapport AVANT d'ouvrir le modal d'email
   // car le modal de rapport peut se fermer et vider this.modalData
   // FIX: Sauvegarder calculatedMetrics pour envoyer EXACTEMENT les mêmes valeurs que l'aperçu
   this.savedReportData = {
     modalData: JSON.parse(JSON.stringify(this.modalData)),
     modalSettings: JSON.parse(JSON.stringify(this.modalSettings)),
     propertyExpenses: JSON.parse(JSON.stringify(this.propertyExpenses)),
     totalAdditionalExpenses: this.totalAdditionalExpenses,
     calculatedMetrics: JSON.parse(JSON.stringify(this.calculatedMetrics))
   };

   console.log('savedReportData APRES sauvegarde:', this.savedReportData);
   console.log('Nombre de reservations sauvegardées:', this.savedReportData?.modalData?.reservations?.length);
   console.log('calculatedMetrics sauvegardés:', this.savedReportData?.calculatedMetrics);

   // MILESTONE 2: Ouvrir le Lock Report Modal au lieu de l'ancien Email Modal
   this.openLockReportModal();
 },

 // Générer le PDF à partir du contenu actuel du modal (même que downloadPDF)
 async generatePDFFromModal() {
   try {
     // Utiliser exactement la même logique que downloadPDF mais retourner un blob
     const jsPDF = (await import('jspdf')).default;
     const html2canvas = (await import('html2canvas')).default;

     const previewContent = document.querySelector('.pdf-preview-wrapper');
     if (!previewContent) {
       throw new Error('Preview content not found');
     }

     const canvas = await html2canvas(previewContent, {
       scale: 2,
       useCORS: true,
       backgroundColor: '#ffffff',
       logging: false
     });

     const pdf = new jsPDF('p', 'mm', 'a4');
     const imgData = canvas.toDataURL('image/png');
     
     const imgWidth = 210;
     const pageHeight = 295;
     const imgHeight = (canvas.height * imgWidth) / canvas.width;
     let heightLeft = imgHeight;
     let position = 0;

     pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
     heightLeft -= pageHeight;

     while (heightLeft >= 0) {
       position = heightLeft - imgHeight;
       pdf.addPage();
       pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
       heightLeft -= pageHeight;
     }

     return pdf.output('blob');
   } catch (error) {
     console.error('Error generating PDF from modal:', error);
     throw error;
   }
 },

 // ========================================
 // MILESTONE 2: LOCK REPORT MODAL METHODS
 // ========================================

 async openLockReportModal() {
   try {
     const propertyId = this.modalData.realPropertyId;

     if (!propertyId) {
       this.showNotification(
         'error',
         'Property Not Found',
         'Property ID not found. Please try again.'
       );
       return;
     }

     // DEBUG: Vérifier baseUrl
     console.log('🔍 DEBUG baseUrl:', this.baseUrl);
     console.log('🔍 DEBUG propertyId:', propertyId);
     const fullUrl = `${this.baseUrl}/api/pms/property-owner/${propertyId}/`;
     console.log('🔍 DEBUG Full URL:', fullUrl);
     console.log('🔍 DEBUG Token:', localStorage.getItem('token') ? 'EXISTS' : 'MISSING');

     // 1. Récupérer les informations du propriétaire (Property Owner)
     const ownerResponse = await fetch(
       fullUrl,
       {
         headers: {
           'Authorization': `Bearer ${localStorage.getItem('token')}`,
           'Content-Type': 'application/json'
         }
       }
     );

     if (!ownerResponse.ok) {
       if (ownerResponse.status === 404) {
         this.showNotification(
           'warning',
           'Owner Not Configured',
           'No owner configured for this property. Please configure owner information in Property Settings first.'
         );
         // Optionnel: rediriger vers Settings
         // this.$router.push(`/settings?property_id=${propertyId}`);
         return;
       }
       throw new Error(`Failed to fetch owner: ${ownerResponse.status}`);
     }

     const ownerData = await ownerResponse.json();

     if (!ownerData.success || !ownerData.data) {
       this.showNotification(
         'warning',
         'Owner Not Configured',
         'No owner configured for this property. Please configure owner information in Property Settings first.'
       );
       return;
     }

     // 2. Remplir les données du Lock Modal
     this.lockModalData = {
       propertyName: this.modalData.propertyName,
       monthYear: this.modalData.periodLabel,
       ownerName: ownerData.data.full_legal_name,
       ownerEmail: ownerData.data.primary_email,
       propertyId: propertyId
     };

     // 3. Afficher le modal
     this.showLockModal = true;

   } catch (error) {
     console.error('Error opening Lock Report Modal:', error);
     this.showNotification(
       'error',
       'Error Loading Owner Information',
       error.message || 'An unexpected error occurred while loading owner information.'
     );
   }
 },

 closeLockModal() {
   this.showLockModal = false;
   this.lockModalData = {
     propertyName: '',
     monthYear: '',
     ownerName: '',
     ownerEmail: '',
     propertyId: null
   };
 },

 // Notification Modal Methods
 showNotification(type, title, message, details = null) {
   const icons = {
     success: '✓',
     error: '✕',
     warning: '!',
     info: 'i'
   };

   this.notificationData = {
     type: type,
     icon: icons[type] || icons.info,
     title: title,
     message: message,
     details: details
   };

   this.showNotificationModal = true;
 },

 closeNotificationModal() {
   this.showNotificationModal = false;
 },

 async testSendReport() {
   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
   if (!this.testEmail || !emailRegex.test(this.testEmail)) {
     this.showNotification('error', 'Invalid Email', 'Please enter a valid email address.');
     return;
   }

   if (!this.modalData?.reservations?.length || !this.calculatedMetrics) {
     this.showNotification(
       'error',
       'Report Data Not Found',
       'Generate the report first before sending a test email.'
     );
     return;
   }

   // ⚠️ Snapshot IDENTIQUE à sendReportByEmailFromModal (Approve) pour garantir
   // que le PDF de test = le PDF qui sera envoyé au client lors du Lock & Send.
   this.savedReportData = {
     modalData: JSON.parse(JSON.stringify(this.modalData)),
     modalSettings: JSON.parse(JSON.stringify(this.modalSettings)),
     propertyExpenses: JSON.parse(JSON.stringify(this.propertyExpenses)),
     totalAdditionalExpenses: this.totalAdditionalExpenses,
     calculatedMetrics: JSON.parse(JSON.stringify(this.calculatedMetrics))
   };

   this.testSending = true;
   try {
     const reportData = this.prepareBookingsForBackend();
     reportData.test_email = this.testEmail;

     const response = await fetch(`${this.baseUrl}/api/pms/test-send-report/`, {
       method: 'POST',
       headers: {
         'Authorization': `Bearer ${localStorage.getItem('token')}`,
         'Content-Type': 'application/json'
       },
       body: JSON.stringify(reportData)
     });
     const result = await response.json();

     if (!response.ok || !result.success) {
       this.showNotification(
         'error',
         'Test Send Failed',
         result.message || 'Unknown error while sending test email.'
       );
       return;
     }

     this.showNotification(
       'success',
       'Test Email Sent',
       `A test PDF was sent to ${this.testEmail}. Nothing was locked or saved.`
     );
   } catch (error) {
     console.error('Error in testSendReport:', error);
     this.showNotification(
       'error',
       'Test Send Error',
       error.message || 'Unexpected error.'
     );
   } finally {
     this.testSending = false;
   }
 },

 async confirmLockReport() {
   try {
     // 1. Préparer les données du rapport (utiliser savedReportData)
     if (!this.savedReportData || !this.savedReportData.modalData) {
       this.showNotification(
         'error',
         'Report Data Not Found',
         'Report data not found. Please regenerate the report before locking.'
       );
       return;
     }

     const reportData = this.prepareBookingsForBackend();

     // 2. Appeler l'endpoint lock-report
     const response = await fetch(`${this.baseUrl}/api/pms/lock-report/`, {
       method: 'POST',
       headers: {
         'Authorization': `Bearer ${localStorage.getItem('token')}`,
         'Content-Type': 'application/json'
       },
       body: JSON.stringify(reportData)
     });

     const result = await response.json();

     if (!response.ok || !result.success) {
       this.showNotification(
         'error',
         'Failed to Lock Report',
         result.message || 'An unknown error occurred while locking the report. Please try again.'
       );
       return;
     }

     // 3. Succès !
     this.showNotification(
       'success',
       'Report Locked Successfully!',
       'This report will now appear in the Month-End screen as "Ready to send".',
       {
         'Property': result.data.owner_name,
         'Email': result.data.owner_email
       }
     );

     // 4. Fermer les modals
     this.closeLockModal();
     this.closeReportModal();

   } catch (error) {
     console.error('Error confirming lock report:', error);
     this.showNotification(
       'error',
       'Error Locking Report',
       error.message || 'An unexpected error occurred. Please try again.'
     );
   }
 },

 // Fonction pour calculer la commission personnalisée selon les paramètres
 calculateCustomCommission(reservation, settings) {
   const platformName = reservation.otaChannelName || '';
   const amount = parseFloat(reservation.totalReservationPrice || 0);

   // Si pas de settings ou mode custom non activé, utiliser Rentlio
   if (!settings || settings.platformFeeMode !== 'custom') {
     return parseFloat(reservation.channelCommissionNominal || 0);
   }

   // Identifier la plateforme et appliquer le taux si activé
   if (platformName.toLowerCase().includes('booking')) {
     if (settings.applyCustomBookingCom) {
       return amount * (parseFloat(settings.bookingComCommissionRate) / 100);
     }
   } else if (platformName.toLowerCase().includes('airbnb')) {
     if (settings.applyCustomAirbnb) {
       return amount * (parseFloat(settings.airbnbCommissionRate) / 100);
     }
   } else {
     if (settings.applyCustomOther) {
       return amount * (parseFloat(settings.otherPlatformCommissionRate) / 100);
     }
   }

   // Sinon, utiliser les données Rentlio
   return parseFloat(reservation.channelCommissionNominal || 0);
 },

 // Préparer les données pour le nouveau endpoint generate-report-vat
 prepareBookingsForBackend() {
   // FIX: Utiliser les données sauvegardées pour envoyer EXACTEMENT les mêmes valeurs que l'aperçu
   // Sans aucun recalcul qui pourrait créer des différences
   const source = this.savedReportData || {
     modalData: this.modalData,
     modalSettings: this.modalSettings,
     propertyExpenses: this.propertyExpenses,
     totalAdditionalExpenses: this.totalAdditionalExpenses,
     calculatedMetrics: this.calculatedMetrics
   };

   const reservations = source.modalData.reservations || [];
   const propertyId = source.modalData.realPropertyId;
   const propertyName = source.modalData.propertyName;
   const periodLabel = source.modalData.periodLabel;
   const settings = source.modalSettings;
   const metrics = source.calculatedMetrics;

   // Extraire year et month du periodLabel
   let year, month;
   if (periodLabel === 'Current Month') {
     const now = new Date();
     month = now.getMonth() + 1;
     year = now.getFullYear();
   } else if (periodLabel === 'Last Month') {
     const now = new Date();
     const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
     month = lastMonth.getMonth() + 1;
     year = lastMonth.getFullYear();
   } else {
     const [monthName, yearStr] = periodLabel.split(' ');
     const monthMap = {
       'January': 1, 'February': 2, 'March': 3, 'April': 4,
       'May': 5, 'June': 6, 'July': 7, 'August': 8,
       'September': 9, 'October': 10, 'November': 11, 'December': 12
     };
     month = monthMap[monthName];
     year = parseInt(yearStr);
   }

   // Fonction pour calculer cleaning fee selon le mode
   const calculateCleaningFee = (res) => {
     const isExtension = (res.guestName || '').toLowerCase().includes('extension');
     if (isExtension) return 0;

     if (settings.cleaningMode === 'rentlio') {
       return parseFloat(res.totalServicesPrice || 0);
     } else {
       // Custom Rates: Appliquer les frais uniquement au dernier segment
       const isLastSegment = !res.isSegment || (res.segmentInfo?.part === res.segmentInfo?.total);
       if (isLastSegment) {
         const platform = res.otaChannelName || '';
         if (platform === 'Booking.com') {
           return settings.bookingCleaningFee || 0;
         } else {
           return settings.otherCleaningFee || 0;
         }
       } else {
         return 0;
       }
     }
   };

   // Formater les bookings pour le backend avec calculs custom
   const bookings = reservations.map(res => {
     const amount = parseFloat(res.totalReservationPrice || 0);
     const commission = this.calculateCustomCommission(res, settings);
     const totalNights = parseInt(res.totalNights || 1);

     // Calculate card fee - EXACTLY like in preview (line 1866-1872)
     let cardFee = 0;
     if (settings.cardFeeMode === 'rentlio') {
       cardFee = (res.otaChannelName === 'Booking.com') ? parseFloat(res.cardFee || 0) : 0;
     } else if (settings.cardFeeMode === 'custom') {
       cardFee = (res.otaChannelName === 'Booking.com') ?
         (amount * parseFloat(settings.customCardFeeRate || 0) / 100) : 0;
     }

     const cleaningFee = calculateCleaningFee(res);

     // Calculate exactly like in preview (lines 1901-1903)
     const netIncome = amount - commission - cardFee;
     const grossProfit = netIncome - cleaningFee;
     const pricePerNight = totalNights > 0 ? amount / totalNights : 0;

     return {
       guest_name: res.guestName || 'Unknown Guest',
       channel_name: res.otaChannelName || 'Direct',
       total_nights: totalNights,
       total_amount: amount,
       channel_commission: commission,
       card_fee: cardFee,
       cleaning_fee: cleaningFee,
       net_income: netIncome,
       total_services_price: cleaningFee,
       gross_profit: grossProfit,
       price_per_night: pricePerNight,
       other_costs: ''
     };
   });

  const propertyAddressResult = this.getPropertyAddress(propertyId);

  // FIX: NE PLUS RECALCULER - Utiliser directement les valeurs de calculatedMetrics
  // pour garantir que l'email contient EXACTEMENT les mêmes données que l'aperçu
  console.log('prepareBookingsForBackend: Using saved calculatedMetrics', metrics);

  // Récupérer les property expenses avec reçus depuis source
  const expensesWithReceipts = source.propertyExpenses.map(expense => ({
    id: expense.id,
    description: expense.description,
    amount: expense.amount,
    receipt_url: expense.receipt_url,
    receipt_filename: expense.receipt_filename,
    has_receipt: !!expense.receipt_file
  }));

   const selectedPmsForReport = JSON.parse(localStorage.getItem('selectedPmsConfig') || 'null');
   const pmsConfigIdForReport = selectedPmsForReport && selectedPmsForReport.id ? selectedPmsForReport.id : null;

   return {
     property_id: propertyId,
     pms_config_id: pmsConfigIdForReport,
     year: year,
     month: month,
     property_name: propertyName,
     property_address: propertyAddressResult,
     bookings: bookings,

     // FIX: Utiliser DIRECTEMENT les valeurs de calculatedMetrics (pas de recalcul)
     totals: {
       total_guest_payments: metrics.totalRevenue,
       platform_fees: metrics.totalPlatformFees,
       card_fees: metrics.totalCardFees,
       cleaning_fees: metrics.totalOutgoings,
       cleaning: metrics.cleaning,
       net_income: metrics.netIncome,
       gross_profit: metrics.grossProfit,
       management_fees: metrics.managementFees,
       vat: metrics.vat,
       consumables: metrics.consumables,
       laundry: metrics.laundry,
       maintenance: metrics.maintenance,
       other_expenses: metrics.otherExpenses,
       additional_expenses: metrics.additionalExpenses,
       settlement_amount: metrics.settlementAmount
     },

     // Envoyer les settings pour le backend (pour info)
     settings: {
       management_fee_base: settings.managementFeeBase,
       management_fee_rate: settings.managementFeeRate,
       management_fee_fixed_amount: settings.managementFeeFixedAmount || 0,
       vat_rate: settings.vatRate,
       vat_business_model: 'agent', // Default
       use_custom_maintenance: settings.useCustomMaintenance || false,
       use_custom_consumables: settings.useCustomConsumables || false,
       use_custom_other_expenses: settings.useCustomOtherExpenses || false
     },

     // Envoyer les expenses avec justificatifs
     expenses: expensesWithReceipts
   };
 },

 // Nouvelle fonction pour préparer les données de rapport pour le backend
 generateReportData() {
   try {
     console.log('=== DEBUT generateReportData ===');
     console.log('savedReportData:', this.savedReportData);
     console.log('modalData:', this.modalData);

     // Utiliser les données sauvegardées si disponibles (pour l'envoi d'email)
     // Sinon utiliser les données du modal actuel
     const sourceData = this.savedReportData || {
       modalData: this.modalData,
       modalSettings: this.modalSettings,
       propertyExpenses: this.propertyExpenses,
       totalAdditionalExpenses: this.totalAdditionalExpenses
     };

     console.log('sourceData utilisé:', sourceData);
     console.log('Nombre de reservations dans sourceData:', sourceData.modalData?.reservations?.length);

     if (sourceData.modalData && sourceData.modalData.reservations) {
       const reservations = sourceData.modalData.reservations;
       const propertyName = sourceData.modalData.propertyName;
       const period = sourceData.modalData.periodLabel;
       const settings = sourceData.modalSettings;

       console.log('Property:', propertyName);
       console.log('Period:', period);
       console.log('Reservations count:', reservations.length);

       // Calculer les outgoings correctement selon le mode de nettoyage
       const calculateOutgoings = (res) => {
         const isExtension = (res.guestName || '').toLowerCase().includes('extension');
         if (isExtension) return 0;

         if (settings.cleaningMode === 'rentlio') {
           return parseFloat(res.totalServicesPrice || 0);
         } else {
           // Custom Rates: Appliquer les frais uniquement au dernier segment
           const isLastSegment = !res.isSegment ||
             (res.segmentInfo?.part === res.segmentInfo?.total);

           if (isLastSegment) {
             const platform = res.otaChannelName || '';
             if (platform === 'Booking.com') {
               return settings.bookingCleaningFee || 150;
             } else {
               return settings.otherCleaningFee || 120;
             }
           } else {
             return 0;
           }
         }
       };

       // Préparer les données des réservations avec outgoings calculés correctement
       const bookings = reservations.map(res => {
         const amount = res.totalReservationPrice || 0;
         const commission = this.calculateCustomCommission(res, settings);
         const cardFee = res.cardFee || 0;
         const outgoings = calculateOutgoings(res);
         const netIncome = amount - commission - cardFee;
         const grossProfit = netIncome - outgoings;

         // IMPORTANT: Utiliser snake_case pour le backend Python
         return {
           guest_name: res.guestName || 'Unknown Guest',
           channel_name: res.otaChannelName || 'Direct',
           total_nights: res.totalNights || 0,
           total_amount: amount,
           channel_commission: commission,
           card_fee: cardFee,
           net_income: netIncome,
           total_services_price: outgoings,
           gross_profit: grossProfit,
           price_per_night: res.pricePerNight || 0,
           net_per_night: res.totalNights > 0 ? netIncome / res.totalNights : 0
         };
       });

       // Calculer les totaux des colonnes (utiliser snake_case car bookings utilise snake_case)
       const totalNights = bookings.reduce((sum, b) => sum + b.total_nights, 0);
       const totalAmount = bookings.reduce((sum, b) => sum + b.total_amount, 0);
       const totalFees = bookings.reduce((sum, b) => sum + b.channel_commission, 0);
       const totalCardFees = bookings.reduce((sum, b) => sum + b.card_fee, 0);
       const totalNetIncome = bookings.reduce((sum, b) => sum + b.net_income, 0);
       const totalOutgoings = bookings.reduce((sum, b) => sum + b.total_services_price, 0);
       const totalGrossProfitBookings = bookings.reduce((sum, b) => sum + b.gross_profit, 0);

       const totalGuestPayments = totalAmount;
       const platformFees = totalFees;
       const cardFees = totalCardFees;
       const cleaningFees = totalOutgoings;
       const grossProfit = totalGrossProfitBookings;

       // Utiliser les settings du modal au lieu de valeurs en dur
       let managementFees = 0;
       if (settings.managementFeeBase === 'revenue') {
         managementFees = totalAmount * (settings.managementFeeRate / 100);
       } else if (settings.managementFeeBase === 'gross') {
         managementFees = grossProfit * (settings.managementFeeRate / 100);
       } else if (settings.managementFeeBase === 'grossFixed') {
         managementFees = parseFloat(settings.managementFeeFixedAmount || 0);
       } else {
         managementFees = totalNetIncome * (settings.managementFeeRate / 100);
       }

       const vat = managementFees * (settings.vatRate / 100);
       const maintenance = settings.useCustomMaintenance
         ? parseFloat(settings.customMaintenanceAmount || 0)
         : parseFloat(settings.maintenance || 0);
       const consumables = settings.useCustomConsumables
         ? parseFloat(settings.customConsumablesAmount || 0)
         : parseFloat(settings.consumables || 0);
       const otherExpenses = settings.useCustomOtherExpenses
         ? parseFloat(settings.customOtherExpensesAmount || 0)
         : parseFloat(settings.otherExpenses || 0);

       const additionalExpensesTotal = sourceData.totalAdditionalExpenses;
       const totalDeductions = managementFees + vat + maintenance + consumables + otherExpenses + additionalExpensesTotal;
       const settlementAmount = grossProfit - totalDeductions;

       // Ajouter les dépenses supplémentaires avec justificatifs
       const expensesWithReceipts = sourceData.propertyExpenses.map(expense => ({
         id: expense.id,
         description: expense.description,
         amount: expense.amount,
         receipt_url: expense.receipt_url,
         receipt_filename: expense.receipt_filename,
         has_receipt: !!expense.receipt_file
       }));

       // IMPORTANT: Retourner les données en snake_case pour le backend Python
      const propertyAddressResult = this.getPropertyAddress(sourceData.modalData.realPropertyId);
       const reportData = {
         property_id: sourceData.modalData.realPropertyId,
         property_name: propertyName,
         property_address: propertyAddressResult,
         report_period: period,
         total_guest_payments: totalGuestPayments,
         platform_fees: platformFees,
         card_fees: cardFees,
         cleaning_fees: cleaningFees,
         gross_profit: grossProfit,
         management_fee_rate: settings.managementFeeRate,
         management_fees: managementFees,
         vat: vat,
         consumables: consumables,
         maintenance: maintenance,
         other_expenses: otherExpenses,
         additionalExpenses: expensesWithReceipts,  // Nouvelles dépenses avec justificatifs
         bookings: bookings,
         total_nights: totalNights,
         total_amount: totalAmount,
         total_fees: totalFees,
         total_card_fees: totalCardFees,
         total_net_income: totalNetIncome,
         total_outgoings: totalOutgoings,
         total_gross_profit: totalGrossProfitBookings,
         avg_per_night: totalNights > 0 ? totalAmount / totalNights : 0,
         avg_net_per_night: totalNights > 0 ? totalNetIncome / totalNights : 0,
         total_deductions: totalDeductions,
         settlement_amount: settlementAmount,
         current_year: new Date().getFullYear()
       };

       console.log('=== REPORT DATA GENERATED ===');
       console.log('Bookings:', reportData.bookings.length);
       console.log('Property:', reportData.property_name);
       console.log('Period:', reportData.report_period);
       console.log('Total amount:', reportData.total_amount);
       console.log('Full reportData:', reportData);

       return reportData;
     } else {
       console.error('No modal data available - sourceData:', sourceData);
       throw new Error('No modal data available for report generation');
     }
   } catch (error) {
     console.error('Error generating report data:', error);
     throw error;
   }
 },

 async prepareReportData(propertyName, period) {
   // Simuler la préparation des données - ajuster selon la logique existante
   const token = localStorage.getItem('token');
   const headers = { 'Authorization': `Bearer ${token}` };
   
   // Récupérer les données selon la période
   let reservations = [];
   if (period === 'Current Month') {
     const response = await axios.get(`${this.baseUrl}/reports/current`, { headers });
     reservations = Object.values(response.data.reservations);
   } else if (period === 'Last Month') {
     const response = await axios.get(`${this.baseUrl}/reports/lastmonth`, { headers });
     reservations = Object.values(response.data.reservations);
   }

   return {
     propertyName,
     periodLabel: period,
     reservations
   };
 },

 generatePDFHTMLForProperty(reportData) {
   // Utiliser la même logique que generatePDFHTML mais avec les données spécifiques
   // Pour simplifier, on retourne un HTML basique - à améliorer selon le besoin
   return `
     <!DOCTYPE html>
     <html>
     <head>
       <title>Report - ${reportData.propertyName}</title>
       <style>
         body { font-family: Arial, sans-serif; padding: 20px; }
         .header { text-align: center; margin-bottom: 30px; }
         .title { font-size: 24px; font-weight: bold; margin-bottom: 10px; }
         .subtitle { font-size: 18px; color: #666; }
       </style>
     </head>
     <body>
       <div class="header">
         <div class="title">Monthly Report</div>
         <div class="subtitle">${reportData.propertyName}</div>
         <div class="subtitle">${reportData.periodLabel}</div>
       </div>
       <div class="content">
         <p>Number of reservations: ${reportData.reservations.length}</p>
         <p>Report generated on: ${new Date().toLocaleDateString('en-US')}</p>
       </div>
     </body>
     </html>
   `;
 },

 closeEmailSender() {
   this.showEmailSender = false;
   this.currentPDFFile = null;
   this.currentReportData = {};
 },

 async viewGroupReport(period) {
   const group = period === 'current' ? this.currentGroup : this.lastMonthGroup;
   if (!group) return;
   const monthDef = this.monthForPeriod(period);
   if (!monthDef) return;
   const [year, month] = monthDef.value.split('-').map(Number);
   this.groupReportModalData = {
     period,
     group,
     monthLabel: monthDef.label,
     pdfUrl: null,
     previewHtml: '',
     generating: true,
     membersData: {},
   };
   // Seed sidebar settings from the group payload.
   this.groupModalSettings = {
     sharedCleaningMode: group.shared_cleaning_mode || 'per_booking',
     sharedLaundryMode: group.shared_laundry_mode || 'per_booking',
     sharedCleaningFee: parseFloat(group.shared_cleaning_fee || 0),
     sharedLaundryFee: parseFloat(group.shared_laundry_fee || 0),
     managementFeeRate: parseFloat(group.management_fee_rate ?? 16),
     managementFeeBase: group.management_fee_base || 'net',
     managementFeeFixedAmount: parseFloat(group.management_fee_fixed_amount ?? 500),
     platformFeeMode: group.platform_fee_mode || 'rentlio',
     applyCustomBookingCom: !!group.apply_custom_booking_com,
     bookingComCommissionRate: parseFloat(group.booking_com_commission_rate ?? 15),
     applyCustomAirbnb: !!group.apply_custom_airbnb,
     airbnbCommissionRate: parseFloat(group.airbnb_commission_rate ?? 3),
     applyCustomOther: !!group.apply_custom_other,
     otherPlatformCommissionRate: parseFloat(group.other_platform_commission_rate ?? 10),
     cardFeeMode: group.card_fee_mode || 'rentlio',
     customCardFeeRate: parseFloat(group.custom_card_fee_rate ?? 2.9),
     customCardFeeFixed: parseFloat(group.custom_card_fee_fixed ?? 0.3),
     consumables: parseFloat(group.consumables ?? 20),
     useCustomConsumables: !!group.use_custom_consumables,
     customConsumablesAmount: parseFloat(group.custom_consumables_amount ?? 0),
   };
   this.groupSettingsModified = false;
   this.groupExpenses = [];
   this.showGroupReportModal = true;
   await this.refreshGroupLockStatus(period);
   // Group expenses for this month (independent of report generation).
   this.loadGroupExpenses(group.id, year, month);
   // Live per-member data (bookings + totals), computed frontend-side so the
   // preview reflects real Rentlio data without requiring individual reports
   // to have been generated first.
   const bookingsMap = period === 'current'
     ? this.currentGroupBookingsByProperty
     : this.lastMonthGroupBookingsByProperty;
   if (!bookingsMap || Object.keys(bookingsMap).length === 0) {
     const fetched = await this.loadGroupBookings(group, period);
     if (period === 'current') this.currentGroupBookingsByProperty = fetched;
     else this.lastMonthGroupBookingsByProperty = fetched;
   }
   this.groupReportModalData.membersData = this.buildGroupMembersData(group, period);
   try {
     const result = await propertyGroups.previewHtml(group.id, {
       year, month,
       members_data: this.groupReportModalData.membersData,
     });
     this.groupReportModalData.previewHtml = (result && result.html) || '';
   } catch (err) {
     console.error('Group preview failed:', err);
     alert('Failed to render preview: ' + (err.response?.data?.detail || err.message));
     this.showGroupReportModal = false;
   } finally {
     this.groupReportModalData.generating = false;
   }
 },

 markGroupAsModified() {
   this.groupSettingsModified = true;
   this.scheduleGroupPreviewRefresh();
 },

 scheduleGroupPreviewRefresh(delay = 300) {
   if (this.groupPreviewDebounceTimer) {
     clearTimeout(this.groupPreviewDebounceTimer);
   }
   this.groupPreviewDebounceTimer = setTimeout(() => {
     this.groupPreviewDebounceTimer = null;
     this.refreshGroupPreview();
   }, delay);
 },

 _buildGroupOverridesPayload(year, month) {
   const s = this.groupModalSettings;
   return {
     year,
     month,
     shared_cleaning_mode: s.sharedCleaningMode,
     shared_laundry_mode: s.sharedLaundryMode,
     shared_cleaning_fee: s.sharedCleaningFee || 0,
     shared_laundry_fee: s.sharedLaundryFee || 0,
     management_fee_rate: s.managementFeeRate || 0,
     management_fee_base: s.managementFeeBase || 'net',
     management_fee_fixed_amount: s.managementFeeFixedAmount || 0,
     platform_fee_mode: s.platformFeeMode || 'rentlio',
     apply_custom_booking_com: !!s.applyCustomBookingCom,
     booking_com_commission_rate: s.bookingComCommissionRate || 0,
     apply_custom_airbnb: !!s.applyCustomAirbnb,
     airbnb_commission_rate: s.airbnbCommissionRate || 0,
     apply_custom_other: !!s.applyCustomOther,
     other_platform_commission_rate: s.otherPlatformCommissionRate || 0,
     card_fee_mode: s.cardFeeMode || 'rentlio',
     custom_card_fee_rate: s.customCardFeeRate || 0,
     custom_card_fee_fixed: s.customCardFeeFixed || 0,
     consumables: s.consumables || 0,
     use_custom_consumables: !!s.useCustomConsumables,
     custom_consumables_amount: s.customConsumablesAmount || 0,
     members_data: this.groupReportModalData.membersData || {},
   };
 },

 async refreshGroupPreview() {
   const g = this.groupReportModalData.group;
   const monthDef = this.monthForPeriod(this.groupReportModalData.period);
   if (!g || !monthDef) return;
   const [year, month] = monthDef.value.split('-').map(Number);
   this.groupPreviewLoading = true;
   try {
     const result = await propertyGroups.previewHtml(g.id, this._buildGroupOverridesPayload(year, month));
     if (result && typeof result.html === 'string') {
       this.groupReportModalData.previewHtml = result.html;
     }
   } catch (err) {
     console.error('Group preview refresh failed:', err);
   } finally {
     this.groupPreviewLoading = false;
   }
 },

 async loadGroupExpenses(groupId, year, month) {
   try {
     const token = localStorage.getItem('token');
     if (!token) return;
     const response = await axios.get(
       `${this.baseUrl}/api/pms/property-expenses/?property_group=${groupId}&year=${year}&month=${month}`,
       { headers: { 'Authorization': `Bearer ${token}` } }
     );
     if (response.data.success) {
       this.groupExpenses = response.data.data || [];
     }
   } catch (err) {
     console.error('Failed to load group expenses:', err);
   }
 },

 openGroupExpenseModal() {
   this.expenseScope = 'group';
   this.openExpenseModal();
 },

 openGroupGuestExtraModal() {
   this.expenseScope = 'group';
   this.openExpenseModal();
   if (this.newExpense) {
     this.newExpense.expense_category = 'guest_extra';
     this.newExpense.is_vat_applicable = false;
   }
 },

 async deleteGroupExpense(expenseId) {
   if (!confirm('Delete this group expense?')) return;
   try {
     const token = localStorage.getItem('token');
     await axios.delete(
       `${this.baseUrl}/api/pms/property-expenses/${expenseId}/`,
       { headers: { 'Authorization': `Bearer ${token}` } }
     );
     const g = this.groupReportModalData.group;
     const monthDef = this.monthForPeriod(this.groupReportModalData.period);
     if (g && monthDef) {
       const [year, month] = monthDef.value.split('-').map(Number);
       await this.loadGroupExpenses(g.id, year, month);
       this.scheduleGroupPreviewRefresh(0);
     }
   } catch (err) {
     console.error('Failed to delete group expense:', err);
     alert('Failed to delete expense.');
   }
 },

 async saveGroupSettings() {
   const g = this.groupReportModalData.group;
   if (!g) return;
   this.savingGroupSettings = true;
   try {
     const s = this.groupModalSettings;
     const payload = {
       shared_cleaning_mode: s.sharedCleaningMode,
       shared_laundry_mode: s.sharedLaundryMode,
       shared_cleaning_fee: s.sharedCleaningFee || 0,
       shared_laundry_fee: s.sharedLaundryFee || 0,
       management_fee_rate: s.managementFeeRate || 0,
       management_fee_base: s.managementFeeBase || 'net',
       management_fee_fixed_amount: s.managementFeeFixedAmount || 0,
       platform_fee_mode: s.platformFeeMode || 'rentlio',
       apply_custom_booking_com: !!s.applyCustomBookingCom,
       booking_com_commission_rate: s.bookingComCommissionRate || 0,
       apply_custom_airbnb: !!s.applyCustomAirbnb,
       airbnb_commission_rate: s.airbnbCommissionRate || 0,
       apply_custom_other: !!s.applyCustomOther,
       other_platform_commission_rate: s.otherPlatformCommissionRate || 0,
       card_fee_mode: s.cardFeeMode || 'rentlio',
       custom_card_fee_rate: s.customCardFeeRate || 0,
       custom_card_fee_fixed: s.customCardFeeFixed || 0,
       consumables: s.consumables || 0,
       use_custom_consumables: !!s.useCustomConsumables,
       custom_consumables_amount: s.customConsumablesAmount || 0,
     };
     const updated = await propertyGroups.update(g.id, payload);
     // Refresh local copy so subsequent reopens are seeded with the saved values.
     Object.assign(g, updated || {});
     this.groupSettingsModified = false;
   } catch (err) {
     console.error('Failed to save group settings:', err);
     alert('Failed to save: ' + (err.response?.data?.detail || err.message));
   } finally {
     this.savingGroupSettings = false;
   }
 },

 closeGroupReportModal() {
   if (this.groupPreviewDebounceTimer) {
     clearTimeout(this.groupPreviewDebounceTimer);
     this.groupPreviewDebounceTimer = null;
   }
   if (this.groupReportModalData.pdfUrl) {
     try {
       window.URL.revokeObjectURL(this.groupReportModalData.pdfUrl);
     } catch (e) {
       /* noop */
     }
   }
   this.showGroupReportModal = false;
   this.groupReportModalData = { period: null, group: null, monthLabel: '', pdfUrl: null, previewHtml: '', generating: false, membersData: {} };
   this.groupPreviewLoading = false;
 },

 async downloadGroupReportPDF() {
   const g = this.groupReportModalData.group;
   const monthDef = this.monthForPeriod(this.groupReportModalData.period);
   if (!g || !monthDef) return;
   // Persist any pending sidebar changes so the downloaded PDF reflects what
   // the user sees on screen.
   if (this.groupSettingsModified) {
     await this.saveGroupSettings();
   }
   const [year, month] = monthDef.value.split('-').map(Number);
   this.groupDownloadingPDF = true;
   try {
     const result = await propertyGroups.generateReport(g.id, this._buildGroupOverridesPayload(year, month));
     if (!result || !result.pdf_base64) {
       alert('PDF generation failed.');
       return;
     }
     const blob = this.base64ToBlob(result.pdf_base64, 'application/pdf');
     const url = window.URL.createObjectURL(blob);
     const a = document.createElement('a');
     a.href = url;
     a.download = `${g.name}_${this.groupReportModalData.monthLabel}.pdf`.replace(/\s+/g, '_');
     a.click();
     setTimeout(() => window.URL.revokeObjectURL(url), 1000);
   } catch (err) {
     console.error('Group PDF download failed:', err);
     alert('Failed to download PDF: ' + (err.response?.data?.detail || err.message));
   } finally {
     this.groupDownloadingPDF = false;
   }
 },

 groupMemberNames(group) {
   if (!group || !Array.isArray(group.memberships)) return '';
   return group.memberships.map(m => this.propertyNameById(m.property_id)).join(', ');
 },

 downloadGroupExcel(period, reportStyle = false) {
   const group = period === 'current' ? this.currentGroup : this.lastMonthGroup;
   const byPropertyMap = period === 'current'
     ? this.currentGroupBookingsByProperty
     : this.lastMonthGroupBookingsByProperty;
   if (!group) return;
   const wb = XLSX.utils.book_new();
   const ids = this.memberPropertyIds(group);
   for (const pid of ids) {
     const reservations = byPropertyMap[pid] || [];
     const propName = this.propertyNameById(pid);
     const rows = reservations.map(r => ({
       'Guest Name': r.guest_name || r.guestName || '',
       'Source': r.channel_name || r.otaChannelName || 'Direct',
       'Check-in': this.formatDate(r.arrival_date || r.arrivalDate),
       'Check-out': this.formatDate(r.departure_date || r.departureDate),
       'Nights': r.total_nights || r.totalNights || 0,
       'Total Amount': r.total_reservation_price || r.totalReservationPrice || 0,
       'Services': r.total_services_price || r.totalServicesPrice || 0,
     }));
     const ws = XLSX.utils.json_to_sheet(rows);
     XLSX.utils.book_append_sheet(wb, ws, propName.slice(0, 30));
   }
   const fname = `${group.name}_${period}_${reportStyle ? 'report' : 'bookings'}.xlsx`;
   XLSX.writeFile(wb, fname);
 },

 base64ToBlob(b64, mime) {
   const byteChars = atob(b64);
   const byteNumbers = new Array(byteChars.length);
   for (let i = 0; i < byteChars.length; i++) {
     byteNumbers[i] = byteChars.charCodeAt(i);
   }
   return new Blob([new Uint8Array(byteNumbers)], { type: mime });
 }
 },

 mounted() {
   this.loadContacts();
   this.fetchData();
   this.loadAllPropertySettings(); // Load property settings from API
   this.fetchGroups();

   // Check if should open contacts modal from URL parameter
   if (this.$route.query.openContacts === 'true') {
     this.openContactsModal();
   }
 }
};

</script>

<style scoped>
/* Styles des segments déplacés en haut */

/* Styles pour les segments de réservations */
.segment-row {
  background: #f8fafc !important;
  border-left: 3px solid #667eea;
}

.segment-row:hover {
  background: #f1f5f9 !important;
}

.segment-badge {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 9px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  cursor: help;
}

.segment-badge:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
  transform: scale(1.05);
}

/* Sidebar styles removed - using component Sidebar.vue styles */

/* Main Content (minimal styles only) */
.content-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 30px;
}

.content-header h1 {
 font-size: 32px;
 font-weight: 700;
 color: #1a202c;
 margin: 0;
}

.user-info {
 display: flex;
 align-items: center;
 gap: 15px;
}

.logout-btn {
 background: #e53e3e;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 transition: background 0.3s ease;
}

.logout-btn:hover {
 background: #c53030;
}

/* Section Cards */
.section-card {
 background: white;
 border-radius: 16px;
 box-shadow: 0 4px 25px rgba(0,0,0,0.08);
 margin-bottom: 30px;
 overflow: hidden;
 border: 1px solid #e2e8f0;
}

.section-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 25px 30px;
 border-bottom: 1px solid #e2e8f0;
 background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.section-header h2 {
 margin: 0;
 font-size: 20px;
 font-weight: 600;
 color: #1a202c;
}

.section-title-container {
 display: flex;
 flex-direction: column;
 gap: 10px;
}

.property-selector {
 display: flex;
 align-items: center;
 gap: 8px;
}

.property-selector label {
 font-size: 13px;
 font-weight: 600;
 color: #4a5568;
 min-width: 60px;
}

.property-select-small {
 padding: 6px 12px;
 border: 1px solid #d1d5db;
 border-radius: 6px;
 font-size: 13px;
 font-weight: 500;
 min-width: 150px;
 transition: border-color 0.3s ease;
 background: white;
}

.properties-section {
 padding: 0;
}

.property-group {
 border-bottom: 1px solid #e2e8f0;
}

.property-group:last-child {
 border-bottom: none;
}

.property-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 20px 30px;
 background: #f8fafc;
 border-bottom: 1px solid #e2e8f0;
}

.property-header h3 {
 margin: 0;
 font-size: 18px;
 font-weight: 600;
 color: #2b6cb0;
}

.property-stats {
 display: flex;
 gap: 20px;
 align-items: center;
}

.stat {
 font-size: 14px;
 color: #64748b;
 font-weight: 500;
}

.stat.revenue {
 font-weight: 700;
 color: #22543d;
 font-size: 16px;
}

.property-actions {
 display: flex;
 gap: 8px;
}

/* Buttons */
.btn {
 display: inline-flex;
 align-items: center;
 justify-content: center;
 padding: 10px 20px;
 border-radius: 8px;
 text-decoration: none;
 font-weight: 600;
 font-size: 14px;
 transition: all 0.3s ease;
 border: 2px solid transparent;
 cursor: pointer;
 min-width: 120px;
}

.btn:disabled {
 opacity: 0.6;
 cursor: not-allowed;
}

.btn-sm {
 padding: 6px 12px;
 font-size: 12px;
 min-width: 70px;
}

.btn-excel {
 background-color: #80CBC4;
 color: white;
 border: 1px solid #80CBC4;
}

.btn-excel:hover:not(:disabled) {
 background-color: #6bb3ad;
 border-color: #6bb3ad;
 transform: translateY(-1px);
}

.btn-report-excel {
 background-color: #FBF8EF;
 color: #333;
 border: 1px solid #d6d2c4;
}

.btn-report-excel:hover:not(:disabled) {
 background-color: #f0ece3;
 border-color: #c9c4b7;
 transform: translateY(-1px);
}

.btn-report {
 background-color: #667eea;
 color: white;
 border: 1px solid #667eea;
}

.btn-report:hover:not(:disabled) {
 background-color: #5a67d8;
 border-color: #5a67d8;
 transform: translateY(-1px);
}

.btn-search {
 background-color: #48BB78;
 color: white;
 border: 1px solid #48BB78;
}

.btn-search:hover:not(:disabled) {
 background-color: #38A169;
 border-color: #38A169;
 transform: translateY(-1px);
}

.btn-outline {
 background: transparent;
 color: #667eea;
 border-color: #667eea;
}

.btn-outline:hover:not(:disabled) {
 background: #667eea;
 color: white;
}

.btn-primary {
 background: #667eea;
 color: white;
 border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
 background: #5a67d8;
 transform: translateY(-1px);
}

/* Combined test-send input + button (segment style) */
.test-send-group {
 display: inline-flex;
 align-items: stretch;
 background: #f5f3ff;
 border: 1px solid #ebe9f8;
 border-radius: 8px;
 overflow: hidden;
 height: 32px;
 transition: border-color 0.15s ease;
}

.test-send-group:focus-within {
 border-color: rgba(128, 117, 240, 0.5);
 box-shadow: 0 0 0 3px rgba(91, 88, 212, 0.1);
}

.test-input-wrap {
 display: inline-flex;
 align-items: center;
 gap: 6px;
 padding: 0 10px;
}

.test-input-wrap svg {
 color: #9896b8;
 flex-shrink: 0;
}

.test-email-input {
 padding: 0;
 border: none;
 background: transparent;
 font-size: 12px;
 font-family: 'DM Sans', sans-serif;
 width: 170px;
 outline: none;
 color: #12113a;
 min-width: 0;
 height: 100%;
}

.test-email-input::placeholder { color: #9896b8; }

.test-email-input:disabled {
 opacity: 0.5;
 cursor: not-allowed;
}

.preview-actions .btn-test-send {
 display: inline-flex;
 align-items: center;
 gap: 5px;
 padding: 0 12px;
 background: rgba(128, 117, 240, 0.08);
 border: none;
 border-left: 1px solid #ebe9f8;
 border-radius: 0;
 font-family: 'DM Sans', sans-serif;
 font-size: 12px;
 font-weight: 600;
 color: #5b58d4;
 cursor: pointer;
 white-space: nowrap;
 min-width: auto;
 height: 100%;
}

.preview-actions .btn-test-send:hover:not(:disabled) {
 background: rgba(128, 117, 240, 0.18);
 color: #4e4bbb;
 transform: none;
 box-shadow: none;
}

.preview-actions .btn-test-send:disabled {
 opacity: 0.5;
 cursor: not-allowed;
}

.preview-actions .btn-test-send svg { flex-shrink: 0; }

/* Download PDF button — neutral outline */
.preview-actions .btn-pdf {
 display: inline-flex;
 align-items: center;
 gap: 6px;
 background: white;
 color: #4a4870;
 border: 1px solid #ebe9f8;
 padding: 7px 14px;
 font-weight: 600;
 white-space: nowrap;
}

.preview-actions .btn-pdf:hover:not(:disabled) {
 background: #f5f3ff;
 border-color: rgba(128, 117, 240, 0.35);
 color: #12113a;
 transform: none;
}

.preview-actions .btn-pdf svg {
 flex-shrink: 0;
 color: #9896b8;
 transition: color 0.15s ease;
}

.preview-actions .btn-pdf:hover:not(:disabled) svg { color: #5b58d4; }

/* Approve Report — primary purple gradient */
.preview-actions .btn-lock-report {
 display: inline-flex;
 align-items: center;
 gap: 6px;
 background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 100%);
 color: white;
 border: 1px solid #4e4bbb;
 padding: 7px 16px;
 font-weight: 600;
 white-space: nowrap;
 margin-left: 4px !important;
}

.preview-actions .btn-lock-report:hover:not(:disabled) {
 background: linear-gradient(180deg, #4e4bbb 0%, #3f3d99 100%);
 transform: translateY(-1px);
 box-shadow: 0 4px 12px rgba(91, 88, 212, 0.35);
}

.preview-actions .btn-lock-report svg { flex-shrink: 0; }

/* Unlock in preview header (group modal) */
.preview-actions .btn-unlock {
 display: inline-flex;
 align-items: center;
 gap: 6px;
 background: #fffbeb;
 color: #92400e;
 border: 1px solid #fcd34d;
 padding: 7px 14px;
 font-weight: 600;
 white-space: nowrap;
 margin-left: 4px !important;
 border-radius: 8px;
}

.preview-actions .btn-unlock:hover:not(:disabled) {
 background: #fef3c7;
 border-color: #f59e0b;
 color: #78350f;
 transform: none;
}

.preview-actions .btn-unlock svg { flex-shrink: 0; }

/* Table Styles */
.table-container {
 overflow-x: auto;
}

.data-table {
 width: 100%;
 border-collapse: collapse;
}

.data-table th {
 background: #f8fafc;
 padding: 16px 20px;
 text-align: left;
 font-weight: 600;
 color: #4a5568;
 font-size: 13px;
 text-transform: uppercase;
 letter-spacing: 0.5px;
 border-bottom: 2px solid #e2e8f0;
}

.data-table td {
 padding: 16px 20px;
 border-bottom: 1px solid #e2e8f0;
 color: #2d3748;
 vertical-align: middle;
}

.data-table tbody tr:hover {
 background: #f7fafc;
}

.property-name {
 font-weight: 600;
 color: #2b6cb0;
}

.guest-name {
 font-weight: 500;
 color: #2d3748;
}

.amount {
 text-align: right;
 font-weight: 600;
 color: #22543d;
}

.text-center {
 text-align: center;
}

.source-badge {
 display: inline-block;
 padding: 4px 8px;
 background: #e2e8f0;
 color: #4a5568;
 border-radius: 12px;
 font-size: 11px;
 font-weight: 600;
 text-transform: uppercase;
}

.loading-state {
 display: flex;
 flex-direction: column;
 align-items: center;
 justify-content: center;
 padding: 60px 20px;
 color: #64748b;
}

.spinner {
 width: 40px;
 height: 40px;
 border: 4px solid #e2e8f0;
 border-top: 4px solid #667eea;
 border-radius: 50%;
 animation: spin 1s linear infinite;
 margin-bottom: 15px;
}

@keyframes spin {
 0% { transform: rotate(0deg); }
 100% { transform: rotate(360deg); }
}

.empty-state {
 padding: 60px 20px;
 text-align: center;
 color: #64748b;
}

.empty-state p {
 font-size: 16px;
 margin: 0;
}

/* Filters */
.filters-section {
 display: flex;
 align-items: end;
 gap: 20px;
 padding: 25px 30px;
 background: #f8fafc;
 border-bottom: 1px solid #e2e8f0;
 flex-wrap: wrap;
}

.filter-group {
 display: flex;
 flex-direction: column;
 gap: 5px;
}

.filter-group label {
 font-size: 13px;
 font-weight: 600;
 color: #4a5568;
 text-transform: uppercase;
 letter-spacing: 0.5px;
}

.filter-select,
.filter-input {
 padding: 8px 12px;
 border: 1px solid #d1d5db;
 border-radius: 6px;
 font-size: 14px;
 min-width: 150px;
 transition: border-color 0.3s ease;
}

.filter-actions {
 display: flex;
 gap: 10px;
 align-items: end;
}

/* Pagination */
.pagination {
 display: flex;
 justify-content: center;
 align-items: center;
 gap: 20px;
 padding: 25px 30px;
 border-top: 1px solid #e2e8f0;
 background: #f8fafc;
}

.pagination-btn {
 background: #667eea;
 color: white;
 border: none;
 padding: 8px 16px;
 border-radius: 6px;
 cursor: pointer;
 font-weight: 500;
 transition: background 0.3s ease;
}

.pagination-btn:hover:not(:disabled) {
 background: #5a67d8;
}

.pagination-btn:disabled {
 background: #cbd5e0;
 cursor: not-allowed;
}

.pagination-info {
 font-size: 14px;
 color: #64748b;
 font-weight: 500;
}

/* Report Modal Styles */
.report-modal-overlay {
 position: fixed;
 top: 0;
 left: 0;
 right: 0;
 bottom: 0;
 background: rgba(15, 14, 46, 0.55);
 backdrop-filter: blur(4px);
 -webkit-backdrop-filter: blur(4px);
 display: flex;
 align-items: center;
 justify-content: center;
 z-index: 10000;
 padding: 20px;
 animation: modalFadeIn 0.18s ease;
}

@keyframes modalFadeIn {
 from { opacity: 0; }
 to { opacity: 1; }
}

.report-modal {
 background: white;
 border-radius: 18px;
 width: 95vw;
 height: 90vh;
 max-width: 1600px;
 box-shadow:
   0 4px 12px rgba(15, 14, 46, 0.08),
   0 24px 60px rgba(91, 88, 212, 0.25);
 display: flex;
 flex-direction: column;
 overflow: hidden;
 animation: modalSlideUp 0.22s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes modalSlideUp {
 from { opacity: 0; transform: translateY(16px); }
 to { opacity: 1; transform: translateY(0); }
}

.modal-header {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 12px 22px;
 background: linear-gradient(180deg, #5b58d4 0%, #4e4bbb 30%, #6b4fd8 70%, #7b52c8 100%);
 color: white;
 position: relative;
 flex-shrink: 0;
}

.modal-header::after {
 content: '';
 position: absolute;
 left: 0;
 right: 0;
 bottom: 0;
 height: 1px;
 pointer-events: none;
 background: linear-gradient(90deg,
   rgba(255,255,255,0) 0%,
   rgba(255,255,255,0.15) 15%,
   rgba(255,255,255,0.6) 50%,
   rgba(255,255,255,0.15) 85%,
   rgba(255,255,255,0) 100%);
}

.modal-title {
 min-width: 0;
 flex: 1;
}

.modal-title h2 {
 margin: 0 0 4px 0;
 font-family: 'Bricolage Grotesque', sans-serif;
 font-size: 16px;
 font-weight: 700;
 letter-spacing: -0.3px;
 line-height: 1.2;
 color: white;
}

.modal-subtitle {
 font-size: 12px;
 display: inline-flex;
 align-items: center;
 gap: 8px;
 flex-wrap: wrap;
}

.status-pill {
 display: inline-flex;
 align-items: center;
 gap: 7px;
 padding: 4px 10px 4px 8px;
 border-radius: 100px;
 background: rgba(255,255,255,0.14);
 border: 1px solid rgba(255,255,255,0.22);
 font-size: 11px;
 font-weight: 600;
 color: white;
 backdrop-filter: blur(4px);
 -webkit-backdrop-filter: blur(4px);
 letter-spacing: 0.01em;
}

.status-pill.saved .status-dot {
 background: #34d399;
 box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.28);
}

.status-pill.unsaved .status-dot {
 background: #fbbf24;
 box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.32);
 animation: pulseAmber 1.8s ease-in-out infinite;
}

.status-dot {
 width: 7px;
 height: 7px;
 border-radius: 50%;
 flex-shrink: 0;
}

@keyframes pulseAmber {
 0%, 100% { transform: scale(1); opacity: 1; }
 50% { transform: scale(1.15); opacity: 0.8; }
}

/* Legacy inline dot indicators (group modal) */
.unsaved-indicator, .saved-indicator {
 display: inline-block;
 width: 7px;
 height: 7px;
 border-radius: 50%;
 font-size: 0;
 line-height: 0;
 vertical-align: middle;
 flex-shrink: 0;
}
.unsaved-indicator {
 background: #fbbf24;
 box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.32);
 animation: pulseAmber 1.8s ease-in-out infinite;
 color: transparent;
}
.saved-indicator {
 background: #34d399;
 box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.28);
 color: transparent;
}

.group-modal-props {
 font-size: 11px;
 color: rgba(255,255,255,0.72);
 font-weight: 500;
}

.close-btn {
 background: rgba(255,255,255,0.14);
 border: 1px solid rgba(255,255,255,0.22);
 color: white;
 cursor: pointer;
 padding: 0;
 width: 30px;
 height: 30px;
 display: flex;
 align-items: center;
 justify-content: center;
 border-radius: 9px;
 transition: background 0.15s ease, border-color 0.15s ease;
 backdrop-filter: blur(4px);
 -webkit-backdrop-filter: blur(4px);
 flex-shrink: 0;
 font-size: 0;
 line-height: 0;
}

.close-btn:hover {
 background: rgba(255,255,255,0.24);
 border-color: rgba(255,255,255,0.35);
}

.close-btn svg { flex-shrink: 0; }

.modal-content {
 display: flex;
 flex: 1;
 overflow: hidden;
}

.settings-panel {
 width: 400px;
 background: #f8fafc;
 border-right: 1px solid #e2e8f0;
 display: flex;
 flex-direction: column;
 overflow-y: auto;
}

.settings-header {
 padding: 25px 25px 20px;
 border-bottom: 1px solid #e2e8f0;
 background: white;
}

.settings-header h3 {
 margin: 0 0 5px 0;
 font-size: 18px;
 font-weight: 600;
 color: #1a202c;
}

.settings-header p {
 margin: 0;
 font-size: 14px;
 color: #64748b;
}

.settings-form {
 flex: 1;
 padding: 25px;
}

.setting-group {
 margin-bottom: 30px;
}

.subsection-title {
 font-size: 16px;
 font-weight: 600;
 color: #1a202c;
 margin: 0 0 15px 0;
 padding-bottom: 8px;
 border-bottom: 2px solid #e2e8f0;
}

.form-row {
 margin-bottom: 20px;
}

.form-label {
 display: block;
 font-size: 14px;
 font-weight: 600;
 color: #374151;
 margin-bottom: 8px;
}

.input-group {
 position: relative;
 display: flex;
 align-items: center;
}

.input-prefix,
.input-suffix {
 position: absolute;
 color: #6b7280;
 font-weight: 500;
 z-index: 2;
 pointer-events: none;
 font-size: 13px;
}

.input-prefix {
 left: 12px;
}

.input-suffix {
 right: 12px;
}

.form-input {
 width: 100%;
 padding: 12px 16px;
 border: 2px solid #e5e7eb;
 border-radius: 8px;
 font-size: 14px;
 font-weight: 500;
 transition: border-color 0.3s ease, box-shadow 0.3s ease;
}

.form-input:focus {
 outline: none;
 border-color: #667eea;
 box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.input-prefix + .form-input {
 padding-left: 35px;
}

.form-input + .input-suffix {
 right: 12px;
}

.radio-group {
 display: flex;
 flex-direction: column;
 gap: 10px;
 margin-top: 8px;
}

.radio-option {
 display: flex;
 align-items: center;
 gap: 8px;
 padding: 12px 14px;
 border: 2px solid #e5e7eb;
 border-radius: 8px;
 cursor: pointer;
 transition: all 0.3s ease;
 font-size: 14px;
}

.radio-option:hover {
 border-color: #667eea;
 background: #f8fafc;
}

.radio-option input[type="radio"] {
 margin: 0;
}

.radio-option input[type="radio"]:checked + span {
 font-weight: 600;
 color: #667eea;
}

.cleaning-custom-rates,
.card-custom-rates {
 margin-top: 15px;
 padding: 15px;
 background: white;
 border-radius: 8px;
 border: 1px solid #e2e8f0;
}

.settings-actions {
 padding: 20px 25px;
 border-top: 1px solid #e2e8f0;
 background: white;
 display: flex;
 gap: 12px;
}

.settings-actions .btn {
 flex: 1;
 min-width: auto;
}

.preview-panel {
 flex: 1;
 display: flex;
 flex-direction: column;
 background: white;
 overflow: hidden;
}

.preview-header {
 display: flex;
 justify-content: flex-end;
 align-items: center;
 padding: 12px 20px;
 border-bottom: 1px solid #ebe9f8;
 background: white;
 gap: 10px;
 flex-shrink: 0;
}

.preview-actions {
 display: flex;
 gap: 8px;
 align-items: center;
 flex-wrap: wrap;
 justify-content: flex-end;
}

/* Reset .btn defaults inside preview-actions to allow smaller compact buttons */
.preview-actions .btn {
 padding: 7px 14px;
 font-size: 12.5px;
 min-width: auto;
 border-width: 1px;
 border-radius: 8px;
 transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}

.preview-content {
 flex: 1;
 overflow-y: auto;
 padding: 30px;
 background: #f8f9fa;
}

/* Ajouter ces styles dans la section <style scoped> */

/* PDF Preview Styles - EXACTEMENT IDENTIQUES AU PDF */
.pdf-preview-container {
  font-family: 'Playfair Display', 'Times New Roman', serif;
  background: #f8f9f9;
  color: #2c3e50;
  font-size: 12px;
  line-height: 1.4;
}

.pdf-preview-container .page-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  position: relative;
  background: white;
}

.pdf-preview-container .report-header {
  margin-bottom: 25px;
}

.pdf-preview-container .report-title {
  font-size: 38px;
  font-weight: 400;
  color: #2c3e50;
  margin-bottom: 5px;
  letter-spacing: 1px;
}

.pdf-preview-container .property-name {
  font-size: 22px;
  font-weight: 300;
  color: #34495e;
  margin-bottom: 15px;
  letter-spacing: 0.5px;
}

.pdf-preview-container .divider {
  width: 100%;
  height: 1px;
  background: #bdc3c7;
  margin: 15px 0 12px 0;
}

.pdf-preview-container .report-period {
  font-size: 13px;
  font-weight: 400;
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.pdf-preview-container .section-title {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin: 20px 0 10px 0;
}

.pdf-preview-container .data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8px;
  margin-bottom: 15px;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .data-table th {
  background: #ecf0f1;
  padding: 5px 3px;
  text-align: center;
  font-weight: 600;
  border: 1px solid #bdc3c7;
  font-size: 7px;
  line-height: 1.2;
  color: #2c3e50;
}

.pdf-preview-container .data-table td {
  padding: 3px 2px;
  border: 1px solid #d5dbdb;
  font-size: 7px;
  vertical-align: middle;
  color: #2c3e50;
}

.pdf-preview-container .data-table .totals-row {
  background: #f8f9fa;
  font-weight: 700;
  border-top: 2px solid #34495e;
}

.pdf-preview-container .data-table .totals-row td {
  border: 1px solid #34495e;
  font-weight: 700;
  color: #2c3e50;
}

.pdf-preview-container .content-sections {
  margin: 15px 0;
}

.pdf-preview-container .summary-section {
  margin-bottom: 20px;
  margin-top: 20px;
}

.pdf-preview-container .summary-title {
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 8px;
  text-transform: uppercase;
  color: #2c3e50;
  letter-spacing: 1px;
}

.pdf-preview-container .summary-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .summary-table td {
  padding: 2px 0;
  font-size: 8px;
  color: #2c3e50;
  border: none;
}

.pdf-preview-container .summary-table .label {
  text-align: left;
  width: 70%;
}

.pdf-preview-container .summary-table .value {
  text-align: right;
  width: 30%;
  font-weight: 600;
}

.pdf-preview-container .summary-table .total-row {
  border-top: 1px solid #34495e;
  padding-top: 4px;
  margin-top: 4px;
  font-weight: 700;
  font-size: 11px;
}

.pdf-preview-container .summary-table .total-row td {
  padding-top: 4px;
  border-top: 1px solid #34495e;
}

.pdf-preview-container .settlement-section {
  margin: 25px 0;
  padding: 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  text-align: center;
}

.pdf-preview-container .settlement-title {
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #2c3e50;
}

.pdf-preview-container .settlement-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Arial', sans-serif;
}

.pdf-preview-container .settlement-table td {
  padding: 3px 0;
  font-size: 11px;
  color: #2c3e50;
  border: none;
}

.pdf-preview-container .settlement-table .label {
  text-align: left;
  width: 70%;
}

.pdf-preview-container .settlement-table .value {
  text-align: right;
  width: 30%;
  font-weight: 600;
}

.pdf-preview-container .settlement-table .final-row {
  border-top: 2px solid #2c3e50;
  padding-top: 6px;
  margin-top: 6px;
  font-weight: 700;
  font-size: 13px;
}

.pdf-preview-container .settlement-table .final-row td {
  padding-top: 6px;
  border-top: 2px solid #2c3e50;
}

.pdf-preview-container .footer-section {
  margin-top: auto;
  padding-top: 15px;
  text-align: center;
  border-top: 1px solid #bdc3c7;
}

.pdf-preview-container .prepared-info {
  margin-bottom: 12px;
}

.pdf-preview-container .prepared-label {
  font-size: 8px;
  color: #7f8c8d;
  margin-bottom: 2px;
}

.pdf-preview-container .prepared-company {
  font-size: 10px;
  font-weight: 600;
  color: #2c3e50;
}

.pdf-preview-container .company-logo {
  margin-bottom: 15px;
}

.pdf-preview-container .company-name {
  font-size: 16px;
  font-weight: 300;
  letter-spacing: 6px;
  color: #2c3e50;
  margin-bottom: 1px;
}

.pdf-preview-container .company-tagline {
  font-size: 6px;
  color: #95a5a6;
  text-transform: uppercase;
  letter-spacing: 3px;
}

.pdf-preview-container .footer-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 9px;
  color: #7f8c8d;
  margin-top: 10px;
}



.report-preview .report-header {
 margin-bottom: 30px;
 text-align: center;
}

.report-preview .report-title {
 font-size: 32px;
 font-weight: 700;
 color: #1a202c;
 margin: 0 0 10px 0;
 letter-spacing: 1px;
}

.report-preview .property-name {
 font-size: 24px;
 font-weight: 300;
 color: #34495e;
 margin: 0 0 15px 0;
 letter-spacing: 0.5px;
}

.report-preview .report-period {
 font-size: 14px;
 font-weight: 600;
 color: #7f8c8d;
 text-transform: uppercase;
 letter-spacing: 2px;
}

.report-preview .section-title {
 font-size: 14px;
 font-weight: 600;
 color: #2c3e50;
 text-transform: uppercase;
 letter-spacing: 1px;
 margin: 25px 0 15px 0;
 padding-bottom: 8px;
 border-bottom: 2px solid #e2e8f0;
}

.report-preview .data-table {
 width: 100%;
 border-collapse: collapse;
 font-size: 12px;
 margin-bottom: 20px;
 font-family: Arial, sans-serif;
}

.report-preview .data-table th {
 background: #ecf0f1;
 padding: 10px 8px;
 text-align: center;
 font-weight: 600;
 border: 1px solid #bdc3c7;
 font-size: 11px;
 line-height: 1.2;
 color: #2c3e50;
}

.report-preview .data-table td {
 padding: 8px;
 border: 1px solid #d5dbdb;
 font-size: 11px;
 vertical-align: middle;
 color: #2c3e50;
}

.report-preview .totals-row {
 background: #f8f9fa;
 font-weight: 700;
 border-top: 2px solid #34495e;
}

.report-preview .totals-row td {
 border: 1px solid #34495e;
 font-weight: 700;
 color: #2c3e50;
}

.report-preview .summary-section {
 margin: 25px 0;
}

.report-preview .summary-title {
 font-size: 16px;
 font-weight: 700;
 margin-bottom: 15px;
 text-transform: uppercase;
 color: #2c3e50;
 letter-spacing: 1px;
}

.report-preview .summary-grid {
 display: flex;
 flex-direction: column;
 gap: 8px;
}

.report-preview .summary-item {
 display: flex;
 justify-content: space-between;
 align-items: center;
 padding: 8px 0;
 font-size: 14px;
 color: #2c3e50;
}

.report-preview .summary-item.total {
 border-top: 2px solid #34495e;
 padding-top: 12px;
 margin-top: 10px;
 font-weight: 700;
 font-size: 16px;
}

.report-preview .summary-label {
 font-weight: 500;
}

.report-preview .summary-value {
 font-weight: 600;
 color: #27ae60;
}

.report-preview .settlement-section {
 margin: 30px 0;
 padding: 20px;
 background: #f8f9fa;
 border: 1px solid #e9ecef;
 border-radius: 8px;
 text-align: center;
}

.report-preview .settlement-title {
 font-size: 18px;
 font-weight: 700;
 margin-bottom: 20px;
 text-transform: uppercase;
 letter-spacing: 1px;
 color: #2c3e50;
}

.report-preview .settlement-content {
 display: flex;
 flex-direction: column;
 gap: 12px;
}

.report-preview .settlement-item {
 display: flex;
 justify-content: space-between;
 align-items: center;
 font-size: 14px;
 color: #2c3e50;
}

.report-preview .settlement-item.final {
 border-top: 2px solid #2c3e50;
 padding-top: 15px;
 margin-top: 15px;
 font-weight: 700;
 font-size: 18px;
 color: #27ae60;
}

.report-preview .settlement-label {
 font-weight: 500;
}


.report-preview .settlement-value {
 font-weight: 600;
}

.report-preview .report-footer {
 margin-top: 40px;
 padding-top: 20px;
 border-top: 1px solid #bdc3c7;
 text-align: center;
}

.report-preview .prepared-info {
 margin-bottom: 15px;
}

.report-preview .prepared-label {
 font-size: 12px;
 color: #7f8c8d;
 margin-bottom: 2px;
}

.report-preview .prepared-company {
 font-size: 16px;
 font-weight: 600;
 color: #2c3e50;
}

.report-preview .generation-date {
 font-size: 12px;
 color: #95a5a6;
}

/* Responsive Design */
@media (max-width: 1200px) {
 .report-modal {
   width: 98vw;
   height: 95vh;
 }

 .modal-content {
   flex-direction: column;
 }

 .settings-panel {
   width: 100%;
   height: 50%;
   border-right: none;
   border-bottom: 1px solid #e2e8f0;
 }

 .preview-panel {
   height: 50%;
 }
}

@media (max-width: 768px) {
 .sidebar {
   transform: translateX(-100%);
 }
 
 .main-content {
   margin-left: 0;
   padding: 20px 15px;
 }
 
 .content-header {
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
 }
 
 .section-header {
   padding: 20px;
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
 }
 
 .property-header {
   flex-direction: column;
   align-items: flex-start;
   gap: 15px;
   padding: 15px 20px;
 }
 
 .property-stats {
   flex-direction: column;
   align-items: flex-start;
   gap: 8px;
 }
 
 .filters-section {
   flex-direction: column;
   align-items: stretch;
   gap: 15px;
   padding: 20px;
 }
 
 .filter-select,
 .filter-input {
   min-width: 100%;
 }
 
 .filter-actions {
   flex-direction: column;
   width: 100%;
 }
 
 .filter-actions .btn {
   width: 100%;
 }
 
 .data-table {
   font-size: 12px;
 }
 
 .data-table th,
 .data-table td {
   padding: 8px 6px;
 }
 
 .btn {
   width: 100%;
 }

 .report-modal {
   width: 100vw;
   height: 100vh;
   border-radius: 0;
   padding: 0;
 }

 .modal-header {
   padding: 15px 20px;
 }

 .modal-title h2 {
   font-size: 18px;
 }

 .settings-panel {
   height: 60%;
 }

 .preview-panel {
   height: 40%;
 }

 .settings-form {
   padding: 20px;
 }

 .preview-content {
   padding: 20px;
 }

 .report-preview {
   padding: 20px;
 }

 .section-title-container {
   gap: 8px;
 }
 
 .property-selector {
   flex-direction: column;
   align-items: flex-start;
   gap: 5px;
 }
 
 .property-select-small {
   width: 100%;
   min-width: 100%;
 }
}

/* Professional Email Modal Styles */
.email-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.email-modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 650px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

@keyframes slideIn {
  from { 
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.email-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon .icon-circle {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.email-icon {
  font-size: 24px;
}

.header-text h2 {
  margin: 0 0 5px 0;
  font-size: 24px;
  font-weight: 600;
}

.header-text p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Body */
.email-modal-body {
  padding: 30px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Report Preview Card */
.report-preview-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 25px;
  overflow: hidden;
}

.card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.card-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.card-content {
  padding: 25px;
}

.report-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 15px;
}

.detail-icon {
  width: 45px;
  height: 45px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 16px;
  color: #1a202c;
  font-weight: 600;
}

/* Recipient Section */
.recipient-section {
  margin-bottom: 25px;
}

.section-title {
  margin-bottom: 20px;
}

.section-title h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.section-title p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.recipient-selector {
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
}

.contact-dropdown {
  flex: 1;
}

.professional-select {
  width: 100%;
  padding: 15px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.professional-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.manage-contacts-btn {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  color: #4a5568;
  padding: 15px 20px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.manage-contacts-btn:hover {
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
  transform: translateY(-1px);
}

/* Selected Contact Preview */
.selected-contact-preview {
  animation: slideDown 0.3s ease;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.contact-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  color: #1a202c;
}

.contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
}

.contact-info {
  flex: 1;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 2px;
}

.contact-email {
  font-size: 14px;
  opacity: 0.9;
}

.status-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

/* Email Options */
.email-options-section {
  margin-bottom: 25px;
}

.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.option-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
}

.option-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.option-icon {
  font-size: 18px;
}

.option-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a202c;
}

.option-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Custom Checkbox */
.checkbox-container {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #4a5568;
  position: relative;
}

.checkbox-container input[type="checkbox"] {
  opacity: 0;
  position: absolute;
}

.checkmark {
  width: 18px;
  height: 18px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark {
  background: #667eea;
  border-color: #667eea;
}

.checkbox-container input[type="checkbox"]:checked + .checkmark::after {
  content: "✓";
  color: white;
  font-size: 12px;
  font-weight: bold;
}

/* Message Textarea */
.message-textarea {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  transition: all 0.3s ease;
}

.message-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

/* Footer */
.email-modal-footer {
  background: #f8fafc;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
}

.footer-info {
  display: flex;
  align-items: center;
}

.info-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #64748b;
}

.info-icon {
  font-size: 16px;
}

.footer-actions {
  display: flex;
  gap: 15px;
}

.cancel-btn {
  background: transparent;
  border: 2px solid #e2e8f0;
  color: #4a5568;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e0;
}

.send-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.send-btn:hover:not(.disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.send-btn.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-icon {
  font-size: 16px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .email-modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .header-content {
    gap: 15px;
  }
  
  .header-icon .icon-circle {
    width: 50px;
    height: 50px;
  }
  
  .email-modal-body {
    padding: 20px;
  }
  
  .options-grid {
    grid-template-columns: 1fr;
  }
  
  .recipient-selector {
    flex-direction: column;
  }
  
  .email-modal-footer {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .footer-actions {
    width: 100%;
    justify-content: center;
  }
}

/* Professional Contacts Management Modal Styles */
.contacts-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.contacts-modal-container {
  background: white;
  border-radius: 20px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

/* Header */
.contacts-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contacts-icon {
  font-size: 24px;
}

/* Body */
.contacts-modal-body {
  padding: 30px;
  max-height: 60vh;
  overflow-y: auto;
}

/* Add Contact Card */
.add-contact-card {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  margin-bottom: 25px;
  overflow: hidden;
}

.add-contact-card .card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.add-contact-card .card-header h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.add-contact-card .card-header p {
  margin: 0;
  font-size: 14px;
  color: #64748b;
}

.add-contact-form {
  padding: 25px;
}

.input-group {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
}

.input-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
}

.professional-input {
  padding: 15px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.professional-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.add-contact-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 15px 30px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  align-self: flex-start;
}

.add-contact-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.add-contact-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Contacts List Card */
.contacts-list-card {
  background: #f8fafc;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.contacts-list-card .card-header {
  background: white;
  padding: 20px 25px;
  border-bottom: 1px solid #e2e8f0;
}

.header-with-count {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-with-count h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1a202c;
}

.contact-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

/* Empty State */
.empty-contacts-state {
  padding: 60px 20px;
  text-align: center;
  color: #64748b;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.empty-contacts-state h4 {
  margin: 0 0 10px 0;
  font-size: 18px;
  font-weight: 600;
  color: #374151;
}

.empty-contacts-state p {
  margin: 0;
  font-size: 14px;
}

/* Contacts Grid */
.contacts-grid {
  padding: 25px;
  display: grid;
  gap: 15px;
}

.contact-item {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.contact-item:hover {
  border-color: #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.contact-item.editing {
  border-color: #f59e0b;
  background: #fffbeb;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
}

.contact-item .contact-avatar {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
}

.contact-details {
  flex: 1;
  min-width: 0;
}

.contact-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.contact-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a202c;
}

.contact-email {
  font-size: 14px;
  color: #64748b;
  word-break: break-all;
}

.contact-meta {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
}

/* Edit Form */
.edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.edit-input {
  padding: 10px 15px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #1a202c;
  background: white;
  transition: all 0.3s ease;
}

.edit-input:focus {
  outline: none;
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
}

/* Contact Actions */
.contact-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: all 0.3s ease;
}

.edit-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.edit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(59, 130, 246, 0.4);
}

.delete-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.delete-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(239, 68, 68, 0.4);
}

.save-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(102, 126, 234, 0.4);
}

.cancel-btn {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
  color: white;
}

.cancel-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(107, 114, 128, 0.4);
}

/* Footer */
.contacts-modal-footer {
  background: #f8fafc;
  padding: 25px 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #e2e8f0;
}

.close-modal-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.close-modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

/* Responsive Design for Contacts Modal */
@media (max-width: 768px) {
  .contacts-modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .contacts-modal-body {
    padding: 20px;
  }
  
  .input-group {
    grid-template-columns: 1fr;
    gap: 15px;
  }
  
  .contact-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .contact-details {
    width: 100%;
  }
  
  .contact-actions {
    align-self: flex-end;
  }
  
  .contacts-modal-footer {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
}

/* Email Sent Popup Styles */
.email-sent-popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.email-sent-popup {
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 450px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: popupSlideIn 0.4s ease-out;
}

@keyframes popupSlideIn {
  from {
    opacity: 0;
    transform: scale(0.7) translateY(-50px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-icon {
  margin-bottom: 25px;
}

.success-checkmark {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: block;
  stroke-width: 3;
  stroke: #28a745;
  stroke-miterlimit: 10;
  margin: 0 auto 20px;
  box-shadow: inset 0px 0px 0px #28a745;
  animation: fill 0.4s ease-in-out 0.4s forwards, scale 0.3s ease-in-out 0.9s both;
}

.success-checkmark .check-icon {
  width: 80px;
  height: 80px;
  position: relative;
  border-radius: 50%;
  box-sizing: border-box;
  border: 3px solid #28a745;
}

.success-checkmark .check-icon::before {
  top: 3px;
  left: -2px;
  width: 30px;
  transform-origin: 100% 50%;
  border-radius: 100px 0 0 100px;
}

.success-checkmark .check-icon::after {
  top: 0;
  left: 30px;
  width: 60px;
  transform-origin: 0 50%;
  border-radius: 0 100px 100px 0;
  animation: rotate-circle 4.25s ease-in;
}

.success-checkmark .check-icon::before,
.success-checkmark .check-icon::after {
  content: '';
  height: 100px;
  position: absolute;
  background: white;
  transform: rotate(-45deg);
}

.success-checkmark .check-icon .icon-line {
  height: 3px;
  background-color: #28a745;
  display: block;
  border-radius: 2px;
  position: absolute;
  z-index: 10;
}

.success-checkmark .check-icon .icon-line.line-tip {
  top: 46px;
  left: 14px;
  width: 25px;
  transform: rotate(45deg);
  animation: icon-line-tip 0.75s;
}

.success-checkmark .check-icon .icon-line.line-long {
  top: 38px;
  right: 8px;
  width: 47px;
  transform: rotate(-45deg);
  animation: icon-line-long 0.75s;
}

.success-checkmark .check-icon .icon-circle {
  top: -3px;
  left: -3px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  position: absolute;
  box-sizing: content-box;
  border: 3px solid #28a745;
}

.success-checkmark .check-icon .icon-fix {
  top: 8px;
  width: 5px;
  left: 26px;
  z-index: 1;
  height: 85px;
  position: absolute;
  transform: rotate(-45deg);
  background-color: white;
}

@keyframes rotate-circle {
  0% {
    transform: rotate(-45deg);
  }
  5% {
    transform: rotate(-45deg);
  }
  12% {
    transform: rotate(-405deg);
  }
  100% {
    transform: rotate(-405deg);
  }
}

@keyframes icon-line-tip {
  0% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  54% {
    width: 0;
    left: 1px;
    top: 19px;
  }
  70% {
    width: 50px;
    left: -8px;
    top: 37px;
  }
  84% {
    width: 17px;
    left: 21px;
    top: 48px;
  }
  100% {
    width: 25px;
    left: 14px;
    top: 45px;
  }
}

@keyframes icon-line-long {
  0% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  65% {
    width: 0;
    right: 46px;
    top: 54px;
  }
  84% {
    width: 55px;
    right: 0px;
    top: 35px;
  }
  100% {
    width: 47px;
    right: 8px;
    top: 38px;
  }
}

.popup-content h2 {
  color: #28a745;
  margin-bottom: 15px;
  font-size: 28px;
  font-weight: 600;
}

.popup-content p {
  color: #555;
  margin-bottom: 10px;
  font-size: 16px;
  line-height: 1.5;
}

.popup-content .delivery-info {
  color: #667eea;
  font-weight: 500;
  background: #f8faff;
  padding: 12px;
  border-radius: 8px;
  margin-top: 15px;
}

.popup-close-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 30px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 25px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
}

.popup-close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.popup-close-btn:active {
  transform: translateY(0);
}

/* Checkbox styles for custom maintenance */
.checkbox-row {
  margin: 8px 0 12px 0;
}

.checkbox-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;
}

.checkbox-option:hover {
  border-color: #667eea;
  background: #f1f5f9;
}

.checkbox-option input[type="checkbox"] {
  margin: 0;
  width: 16px;
  height: 16px;
}

.checkbox-option input[type="checkbox"]:checked + span {
  font-weight: 600;
  color: #667eea;
}

.setting-description {
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0 0;
  font-style: italic;
  line-height: 1.4;
}

/* ========== Property Expenses (Other Maintenance) Styles ========== */

/* Add Expense Button */
.btn-add-expense {
  background: #10b981;
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  margin-top: 12px;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-add-expense:hover {
  background: #059669;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.2);
}

/* Expenses List */
.expenses-list {
  background: #f9fafb;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
  border: 1px solid #e5e7eb;
}

.expense-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  margin-bottom: 8px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
}

.expense-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  border-color: #d1d5db;
}

.expense-desc {
  flex: 1;
  font-weight: 500;
  color: #1f2937;
  font-size: 13px;
}

.expense-amount {
  font-weight: 600;
  color: #059669;
  margin: 0 12px;
  font-size: 14px;
}

.expense-receipt {
  color: #f59e0b;
  font-size: 16px;
  margin: 0 8px;
}

.btn-delete {
  background: #ef4444;
  color: white;
  border: none;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
}

.btn-delete:hover {
  background: #dc2626;
  transform: scale(1.1);
}

.expense-total {
  text-align: right;
  padding-top: 12px;
  border-top: 2px solid #e5e7eb;
  margin-top: 10px;
  color: #1f2937;
  font-size: 14px;
}

/* Expense Modal */
.expense-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.expense-modal {
  background: white;
  padding: 0;
  border-radius: 16px;
  width: 500px;
  max-width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateY(-50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.expense-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 30px;
  border-bottom: 1px solid #e5e7eb;
}

.expense-modal-header h3 {
  margin: 0;
  color: #1f2937;
  font-size: 20px;
  font-weight: 700;
}

.expense-modal-header .close-btn {
  background: none;
  border: none;
  font-size: 32px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.expense-modal-header .close-btn:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.expense-modal-body {
  padding: 30px;
}

.expense-modal-body .form-group {
  margin-bottom: 20px;
}

.expense-modal-body .form-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #374151;
  font-size: 14px;
}

.expense-modal-body .form-input {
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.expense-modal-body .form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.expense-modal-body .form-input-file {
  width: 100%;
  padding: 10px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f9fafb;
}

.expense-modal-body .form-input-file:hover {
  border-color: #667eea;
  background: #f3f4f6;
}

.file-selected {
  margin-top: 8px;
  color: #059669;
  font-size: 13px;
  font-weight: 500;
}

.expense-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 30px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 16px 16px;
}

.expense-modal-footer .btn {
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;
}

.expense-modal-footer .btn-outline {
  background: transparent;
  color: #6b7280;
  border-color: #d1d5db;
}

.expense-modal-footer .btn-outline:hover {
  background: #f3f4f6;
  border-color: #9ca3af;
}

.expense-modal-footer .btn-primary {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

.expense-modal-footer .btn-primary:hover:not(:disabled) {
  background: #5a67d8;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
}

.expense-modal-footer .btn-primary:disabled {
  background: #d1d5db;
  border-color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.6;
}

/* VAT Toggle in expense modal */
.vat-toggle-group {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.vat-toggle-option {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
  background: #f9fafb;
  text-align: center;
  transition: all 0.15s;
  user-select: none;
}

.vat-toggle-option:hover {
  border-color: #6b7280;
  background: #f3f4f6;
}

.vat-toggle-option.active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
}

.form-hint {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  font-style: italic;
}

.form-row-two {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.expense-modal-body .input-group {
  display: flex;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  overflow: hidden;
}

.expense-modal-body .input-prefix {
  padding: 8px 10px;
  background: #f3f4f6;
  border-right: 1px solid #d1d5db;
  color: #6b7280;
  font-size: 13px;
}

.expense-modal-body .input-group .form-input {
  border: none;
  border-radius: 0;
}

.expense-modal-body .input-group .form-input:focus {
  outline: none;
  box-shadow: none;
}

/* Laundry Per-Booking Styles */
.laundry-per-booking-container {
  margin-top: 15px;
  padding: 15px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.form-sub-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #6b7280;
  font-size: 13px;
}

.booking-counter-group {
  margin-top: 15px;
}

.counter-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.counter-btn {
  width: 40px;
  height: 40px;
  border: 2px solid #3b82f6;
  background: white;
  color: #3b82f6;
  font-size: 22px;
  font-weight: bold;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  line-height: 1;
  padding: 0;
}

.counter-btn:hover:not(:disabled) {
  background: #3b82f6;
  color: white;
  transform: scale(1.05);
}

.counter-btn:disabled {
  border-color: #d1d5db;
  color: #d1d5db;
  cursor: not-allowed;
  opacity: 0.5;
}

.counter-input {
  width: 80px;
  height: 40px;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  border: 2px solid #3b82f6;
  border-radius: 8px;
  padding: 0 10px;
}

.counter-input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.counter-info {
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
  padding: 8px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.info-icon {
  margin-right: 6px;
}

.adjustment-badge {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  background: #fef3c7;
  color: #92400e;
  border-radius: 4px;
  font-weight: 600;
  font-size: 12px;
}

.total-display {
  padding: 12px;
  background: white;
  border-radius: 6px;
  border: 2px solid #10b981;
  font-size: 14px;
  color: #047857;
}

.total-display strong {
  color: #065f46;
  font-size: 15px;
}

.calculation-hint {
  display: inline-block;
  margin-left: 8px;
  color: #6b7280;
  font-size: 13px;
}

.radio-group {
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
}

.radio-option input[type="radio"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.radio-option:hover {
  color: #1f2937;
}

/* ========================================
   MILESTONE 2: LOCK REPORT MODAL STYLES
   ======================================== */

.lock-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.lock-modal-container {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.lock-modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.lock-modal-header .header-icon {
  flex-shrink: 0;
}

.lock-modal-header .icon-circle {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
}

.lock-modal-header .header-text {
  flex: 1;
}

.lock-modal-header h2 {
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.lock-modal-header p {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.lock-modal-header .modal-close-btn {
  background: none;
  border: none;
  font-size: 28px;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.lock-modal-header .modal-close-btn:hover {
  background: #f3f4f6;
  color: #4b5563;
}

.lock-modal-body {
  padding: 24px;
}

.report-summary-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #e5e7eb;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.summary-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.summary-value.email-highlight {
  color: #667eea;
  font-weight: 700;
}

.warning-box {
  background: #fef3c7;
  border: 2px solid #fbbf24;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
}

.warning-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.warning-content h3 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 700;
  color: #92400e;
}

.warning-content ul {
  margin: 0;
  padding-left: 20px;
  color: #92400e;
}

.warning-content li {
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 1.5;
}

.warning-content strong {
  font-weight: 700;
}

.lock-modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.lock-modal-footer .cancel-btn {
  padding: 10px 20px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.lock-modal-footer .cancel-btn:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.lock-modal-footer .confirm-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 6px;
  color: white;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.lock-modal-footer .confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* ===================================
   NOTIFICATION MODAL (Replaces Alerts)
   =================================== */

.notification-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease;
}

.notification-modal-container {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Header Styles */
.notification-modal-header {
  padding: 32px 24px 24px 24px;
  position: relative;
  text-align: center;
}

.notification-modal-header.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.notification-modal-header.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.notification-modal-header.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.notification-modal-header.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.notification-icon-circle {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.notification-icon {
  font-size: 36px;
  line-height: 1;
  color: white;
  font-weight: 400;
}

.notification-modal-header .modal-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-size: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.notification-modal-header .modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

/* Body Styles */
.notification-modal-body {
  padding: 24px;
  text-align: center;
}

.notification-title {
  margin: 0 0 12px 0;
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  line-height: 1.3;
}

.notification-message {
  margin: 0 0 20px 0;
  font-size: 16px;
  color: #6b7280;
  line-height: 1.5;
}

.notification-details {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
  text-align: left;
}

.notification-details .detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.notification-details .detail-row:last-child {
  border-bottom: none;
}

.notification-details .detail-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.notification-details .detail-value {
  font-size: 14px;
  color: #111827;
  font-weight: 600;
}

/* Footer Styles */
.notification-modal-footer {
  padding: 16px 24px 24px 24px;
  display: flex;
  justify-content: center;
}

.notification-btn {
  padding: 12px 40px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  color: white;
  min-width: 120px;
}

.notification-btn.success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.notification-btn.error {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.notification-btn.warning {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.notification-btn.info {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.notification-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.notification-btn:active {
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 640px) {
  .notification-modal-container {
    width: 95%;
    margin: 20px;
  }

  .notification-title {
    font-size: 20px;
  }

  .notification-message {
    font-size: 14px;
  }

  .notification-icon-circle {
    width: 60px;
    height: 60px;
  }

  .notification-icon {
    font-size: 30px;
  }
}

/* ── MAIN ── */
.dashboard-container {
  background: #f5f3ff !important;
}

.main {
  margin-left: 240px;
  flex: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  padding: 28px 36px;
  flex: 1;
}

/* ──────────────────────────────────────────────────────────────── */
/* W2 — Reservations + Linked Properties (mockup invoxis_reservations-6) */
/* ──────────────────────────────────────────────────────────────── */

/* MONTH TABS */
.month-tabs { display: flex; gap: 6px; margin-bottom: 20px; }
.month-tab { padding: 7px 16px; border-radius: 100px; font-size: 13px; font-weight: 500; cursor: pointer; border: 1px solid #e4e3f0; background: white; color: #8b8aaa; transition: all 0.15s; }
.month-tab.active { background: #4a38b5; color: white; border-color: #5b4fcf; }

/* PROPERTY SELECTOR */
.prop-selector-row { display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }
.prop-label { font-size: 13px; color: #8b8aaa; font-weight: 500; }

/* CUSTOM DROPDOWN (replaces native select for professional appearance) */
.dd-wrap { position: relative; min-width: 280px; }

.dd-trigger {
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 9px 12px 9px 14px; border-radius: 10px;
  border: 1.5px solid #d1d0e0; background: white; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 13px; color: #1c1b2e;
  text-align: left; transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.dd-trigger:hover { border-color: #c4b8f0; background: #fafafe; }
.dd-trigger.open { border-color: #5b4fcf; box-shadow: 0 0 0 3px rgba(91, 79, 207, 0.1); }
.dd-trigger:disabled { opacity: 0.6; cursor: not-allowed; }

.dd-trigger-icon {
  width: 28px; height: 28px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.dd-trigger-icon.is-prop { background: rgba(91, 79, 207, 0.08); color: #5b4fcf; }
.dd-trigger-icon.is-group { background: rgba(91, 79, 207, 0.1); color: #5b4fcf; }

.dd-trigger-label {
  flex: 1; font-weight: 500; color: #1c1b2e; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis; display: flex; align-items: center; gap: 8px;
}
.dd-trigger-pill {
  font-size: 10px; padding: 2px 8px; border-radius: 100px;
  background: #f3f0ff; border: 1px solid #c4b8f0; color: #5b4fcf;
  font-weight: 600; flex-shrink: 0;
}
.dd-trigger-chev { color: #8b8aaa; transition: transform 0.18s; flex-shrink: 0; }
.dd-trigger.open .dd-trigger-chev { transform: rotate(180deg); color: #5b4fcf; }

.dd-panel {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0;
  background: white; border: 1px solid #e4e3f0; border-radius: 10px;
  box-shadow: 0 8px 24px rgba(26, 24, 61, 0.12), 0 2px 6px rgba(26, 24, 61, 0.06);
  z-index: 60; padding: 6px 0; max-height: 380px; overflow-y: auto;
  animation: ddFadeIn 0.12s ease-out;
}
@keyframes ddFadeIn {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

.dd-section-title {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em;
  color: #8b8aaa; padding: 8px 14px 5px;
}
.dd-section-title:not(:first-child) { margin-top: 4px; border-top: 1px solid #f0eef8; padding-top: 10px; }

.dd-item {
  width: 100%; display: flex; align-items: center; gap: 10px;
  padding: 8px 14px; background: none; border: none; cursor: pointer;
  font-family: 'DM Sans', sans-serif; font-size: 13px; color: #1c1b2e;
  text-align: left; transition: background 0.1s;
}
.dd-item:hover { background: #f9f9fb; }
.dd-item.active { background: rgba(91, 79, 207, 0.06); color: #5b4fcf; font-weight: 600; }

.dd-item-icon {
  width: 24px; height: 24px; border-radius: 6px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: rgba(91, 79, 207, 0.08); color: #5b4fcf;
}
.dd-item.active .dd-item-icon { background: rgba(91, 79, 207, 0.14); }

.dd-item-name { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.dd-item-pill {
  font-size: 10px; padding: 2px 8px; border-radius: 100px;
  background: #f3f0ff; border: 1px solid #c4b8f0; color: #5b4fcf;
  font-weight: 600; flex-shrink: 0;
}

.dd-item-check { color: #5b4fcf; flex-shrink: 0; margin-left: 4px; }

/* GROUP SUMMARY */
.group-summary {
  background: white; border: 1px solid #e4e3f0; border-radius: 12px;
  padding: 16px 20px; margin-bottom: 16px;
  display: grid; grid-template-columns: 1fr auto; gap: 16px; align-items: center;
}
.group-name { font-size: 16px; font-weight: 600; color: #1c1b2e; display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
.group-pill { font-size: 11px; padding: 2px 9px; border-radius: 100px; background: #f3f0ff; border: 1px solid #c4b8f0; color: #5b4fcf; font-weight: 500; }
.group-meta { display: flex; align-items: center; gap: 18px; font-size: 12px; color: #8b8aaa; }
.group-total { font-size: 24px; font-weight: 600; color: #1c1b2e; text-align: right; }
.group-total-label { font-size: 11px; color: #8b8aaa; text-align: right; margin-top: 2px; }

/* GROUP ACTIONS */
.group-actions { display: flex; gap: 8px; margin-bottom: 20px; align-items: center; flex-wrap: wrap; }

/* GROUP REPORT MODAL — réutilise .report-modal du single-property + helpers ci-dessous */
.preview-panel.preview-panel-full { width: 100%; }
.group-report-iframe {
  width: 100%; min-height: 70vh; border: none; border-radius: 6px;
  background: white; box-shadow: 0 2px 12px rgba(26, 24, 61, 0.08);
}
.group-report-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #8b8aaa; gap: 12px; padding: 40px 0;
}
.group-modal-props {
  font-size: 12px; color: #8b8aaa; margin-left: 6px;
}

/* LOCK / UNLOCK */
.lock-badge {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 4px 10px; border-radius: 100px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.02em;
  background: #fef3c7; color: #92400e; border: 1px solid #fcd34d;
  margin-right: 4px;
}
.lock-badge svg { stroke: currentColor; }
.btn-lock {
  background: #4a38b5; color: white; border-color: #5b4fcf;
}
.btn-lock:hover { background: #3d2f9e; color: white; border-color: #3d2f9e; }
.btn-unlock {
  background: white; color: #92400e; border-color: #fcd34d;
}
.btn-unlock:hover { background: #fef3c7; color: #92400e; border-color: #f59e0b; }
.btn-lock:disabled,
.btn-unlock:disabled { opacity: 0.55; cursor: not-allowed; }

/* SHARED BTN STYLES used in mockup views */
.month-tabs ~ .single-actions .btn,
.group-actions .btn,
.single-actions .btn {
  padding: 7px 14px; border-radius: 8px; font-size: 12px; font-weight: 500;
  cursor: pointer; border: 1px solid #e4e3f0; background: white; color: #4a4868;
  white-space: nowrap; font-family: 'DM Sans', sans-serif; transition: all 0.15s;
}
.group-actions .btn:hover,
.single-actions .btn:hover { border-color: #c4b8f0; color: #5b4fcf; }
.btn-purple { background: #4a38b5; color: white; border-color: #5b4fcf; }
.btn-purple:hover { background: #3d2f9e; color: white; border-color: #3d2f9e; }
.btn-green { background: #0f6e56; color: white; border-color: #0f6e56; }
.btn-green:hover { background: #085041; }

/* INDIVIDUAL PROPERTY VIEW */
.single-summary {
  background: white; border: 1px solid #e4e3f0; border-radius: 12px;
  padding: 14px 20px; margin-bottom: 16px;
  display: flex; align-items: center; justify-content: space-between;
}
.single-name { font-size: 16px; font-weight: 600; color: #5b4fcf; }
.single-meta { font-size: 13px; color: #8b8aaa; display: flex; gap: 16px; }
.single-total { font-size: 16px; font-weight: 600; color: #1c1b2e; }
.single-actions { display: flex; gap: 8px; }

/* PROPERTY SECTION (inside group view) */
.prop-section {
  margin-bottom: 16px; border: 1px solid #e4e3f0; border-radius: 12px;
  overflow: hidden; background: white;
}
.prop-section-header {
  padding: 12px 18px; display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px solid #e4e3f0; background: #f9f9fb;
}
.prop-section-name { font-size: 14px; font-weight: 600; color: #5b4fcf; }
.prop-section-meta { font-size: 12px; color: #8b8aaa; display: flex; align-items: center; gap: 16px; }
.prop-section-total { font-size: 14px; font-weight: 600; color: #1c1b2e; }

/* INDIVIDUAL VIEW SECTION */
.indiv-section {
  margin-bottom: 16px; border: 1px solid #e4e3f0; border-radius: 12px;
  overflow: hidden; background: white;
}

/* TABLE inside .prop-section and .indiv-section — uniform columns across all property tables */
.prop-section table,
.indiv-section table { width: 100%; border-collapse: collapse; table-layout: fixed; }

/* Column widths shared by every property table so columns align vertically across groups */
.prop-section col.col-guest,
.indiv-section col.col-guest      { width: 28%; }
.prop-section col.col-source,
.indiv-section col.col-source     { width: 11%; }
.prop-section col.col-checkin,
.indiv-section col.col-checkin    { width: 11%; }
.prop-section col.col-checkout,
.indiv-section col.col-checkout   { width: 11%; }
.prop-section col.col-nights,
.indiv-section col.col-nights     { width: 8%; }
.prop-section col.col-amount,
.indiv-section col.col-amount     { width: 15%; }
.prop-section col.col-services,
.indiv-section col.col-services   { width: 16%; }

.prop-section th,
.indiv-section th {
  text-align: left; padding: 9px 14px; font-size: 11px; font-weight: 600; color: #8b8aaa;
  text-transform: uppercase; letter-spacing: 0.04em; border-bottom: 1px solid #e4e3f0; background: white;
  white-space: nowrap;
}
.prop-section td,
.indiv-section td {
  padding: 10px 14px; border-bottom: 1px solid #e4e3f0; color: #1c1b2e; font-size: 13px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.prop-section td.guest-name,
.indiv-section td.guest-name { white-space: normal; overflow: visible; text-overflow: clip; }
.prop-section tr:last-child td,
.indiv-section tr:last-child td { border-bottom: none; }
.prop-section tr:hover td,
.indiv-section tr:hover td { background: #f9f9fb; }

/* SOURCE BADGES */
.src-badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; letter-spacing: 0.03em; }
.src-airbnb { background: #fee2e2; color: #a32d2d; }
.src-booking { background: #dbeafe; color: #1e3a8a; }
.src-direct { background: #dcfce7; color: #14532d; }

/* SPLIT BADGE (overrides legacy gradient version) */
.split-badge {
  display: inline-block; padding: 2px 7px; border-radius: 100px;
  font-size: 10px; font-weight: 600; background: #eeeaff; color: #5b4fcf;
  margin-left: 6px; text-transform: uppercase; letter-spacing: 0;
}

/* SHARED COSTS BAR */
.shared-costs-bar {
  border-top: 1px solid #e4e3f0; background: #f9f9fb;
  padding: 10px 18px; display: flex; align-items: center; gap: 20px; flex-wrap: wrap;
}
.sc-label { font-size: 11px; font-weight: 600; color: #8b8aaa; text-transform: uppercase; letter-spacing: 0.04em; }
.sc-item { font-size: 12px; color: #1c1b2e; display: flex; align-items: center; gap: 5px; }
.sc-note { font-size: 11px; color: #8b8aaa; }

/* ============================================================
   REPORT SIDEBAR (W3) — styles from invoxis-report-sidebar-2.html
   ============================================================ */
.rsb-divider {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9896bb;
  padding: 16px 4px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.rsb-divider.expense { color: #854F0B; }
.rsb-divider.income { color: #3B6D11; }

.rsb-source-row {
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
}
.rsb-source-selector {
  flex: 1;
  background: white;
  border: 1px solid #e8e6f4;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.12s;
  white-space: nowrap;
}
.rsb-source-selector:hover { border-color: #c4b8f0; color: #5b4fcf; }
.rsb-source-selector.active {
  background: #ede9fe;
  border-color: #5b4fcf;
  color: #5b4fcf;
  font-weight: 600;
}

.rsb-hint {
  font-size: 11px;
  color: #9896bb;
  margin-top: 5px;
  line-height: 1.45;
}

/* VAT pill (read-only, mirrors maquette) */
.rsb-vat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0 0;
}
.rsb-vat-label {
  font-size: 12px;
  color: #9896bb;
}
.rsb-vat-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid #e8e6f4;
  background: #f5f4fb;
  color: #9896bb;
}
.rsb-vat-pill.on {
  background: rgba(0,184,148,0.1);
  border-color: rgba(0,184,148,0.25);
  color: #007a63;
}
.rsb-vat-pill::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.7;
}

/* Consumables on/off toggle row */
.rsb-cons-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
}
.rsb-toggle {
  position: relative;
  display: inline-block;
  width: 36px;
  height: 20px;
  flex-shrink: 0;
  cursor: pointer;
}
.rsb-toggle input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.rsb-toggle-track {
  position: absolute;
  inset: 0;
  background: #e8e6f4;
  border-radius: 10px;
  transition: background 0.2s;
}
.rsb-toggle input:checked + .rsb-toggle-track {
  background: #5b4fcf;
}
.rsb-toggle-thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s;
  pointer-events: none;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}
.rsb-toggle input:checked ~ .rsb-toggle-thumb {
  transform: translateX(16px);
}

/* Expense item with category dropdown + multi-files */
.rsb-expense-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: start;
  padding: 10px 12px;
  background: #f9f9fb;
  border: 1px solid #e8e6f4;
  border-radius: 8px;
  margin-bottom: 6px;
}
.rsb-expense-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.rsb-expense-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.rsb-expense-desc {
  font-size: 13px;
  font-weight: 500;
  color: #1c1b2e;
}
.rsb-expense-cat {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.rsb-expense-cat.maintenance { background: #fde9d0; color: #854F0B; }
.rsb-expense-cat.guest_extra { background: #e6f4d8; color: #3B6D11; }
.rsb-expense-cat.cleaning { background: #ddd5fb; color: #5b4fcf; }
.rsb-expense-cat.other { background: #f0eef8; color: #6b7280; }
.rsb-expense-files {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 11px;
  color: #5b4fcf;
}
.rsb-expense-files a {
  color: inherit;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: #ede9fe;
  padding: 2px 6px;
  border-radius: 4px;
}
.rsb-expense-files a:hover { text-decoration: underline; }
.rsb-expense-amount {
  font-size: 13px;
  font-weight: 600;
  color: #1c1b2e;
  white-space: nowrap;
}
.rsb-expense-actions {
  display: flex;
  gap: 4px;
  align-items: center;
}

/* Pending files list in expense modal (multi-file uploader) */
.file-selected-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
}
.file-selected-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f4fb;
  border: 1px solid #e8e6f4;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  color: #4a4880;
}
.btn-delete-mini {
  background: transparent;
  border: none;
  color: #d63031;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
  line-height: 1;
}
.btn-delete-mini:hover { color: #a02020; }

/* ============================================================
   REPORT SIDEBAR — exact CSS copied from invoxis-report-sidebar-2.html
   Scoped under .report-sidebar to avoid collisions with other panels.
   ============================================================ */
.report-sidebar {
  --accent: #5b4fcf;
  --accent-dim: rgba(91,79,207,0.08);
  --accent-border: rgba(91,79,207,0.25);
  --bg: #f5f4fb;
  --white: #ffffff;
  --text: #1a1a2e;
  --text-mid: #4a4880;
  --text-muted: #9896bb;
  --border: #e8e6f4;
  --green: #00b894;
  --green-dim: rgba(0,184,148,0.1);
  --green-border: rgba(0,184,148,0.25);
  --amber-text: #b7791f;
  --amber-dim: rgba(253,203,110,0.2);
  --amber-border: rgba(253,203,110,0.5);
  --shadow-sm: 0 1px 4px rgba(26,24,61,0.07);
  --radius: 8px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 8px 0;
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: var(--text);
  -webkit-font-smoothing: antialiased;
}
.report-sidebar *, .report-sidebar *::before, .report-sidebar *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* DIVIDER LABELS */
.report-sidebar .divider-label { font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--text-muted); padding: 12px 16px 6px; display: flex; align-items: center; gap: 6px; }
.report-sidebar .divider-label.income { color: #3B6D11; }
.report-sidebar .divider-label.expense { color: #854F0B; }

/* SECTIONS */
.report-sidebar .section { padding: 14px 16px; background: var(--white); border-radius: 8px; border: 1px solid var(--border); box-shadow: 0 1px 3px rgba(26,24,61,0.06); margin: 0 10px 8px; }
.report-sidebar .section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); margin-bottom: 12px; }

/* FIELDS */
.report-sidebar .field-label { font-size: 11px; color: var(--text-muted); font-weight: 500; margin-bottom: 4px; display: inline-flex; align-items: center; gap: 4px; }
.report-sidebar .input-wrap { display: flex; align-items: center; background: var(--bg); border: 1px solid var(--border); border-radius: var(--radius); padding: 6px 10px; gap: 6px; }
.report-sidebar .input-wrap input { background: none; border: none; outline: none; font-size: 13px; color: var(--text); width: 100%; font-family: 'Inter', sans-serif; }
.report-sidebar .input-wrap span { font-size: 12px; color: var(--text-muted); white-space: nowrap; flex-shrink: 0; }
.report-sidebar .input-wrap.readonly { background: #f0eef8; }
.report-sidebar .input-wrap.readonly input { color: var(--text-muted); }

/* RADIO */
.report-sidebar .radio-opt { display: flex; align-items: center; gap: 8px; padding: 7px 10px; border: 1px solid var(--border); border-radius: var(--radius); margin-bottom: 6px; cursor: pointer; user-select: none; transition: border-color 0.12s, background 0.12s; }
.report-sidebar .radio-opt:last-of-type { margin-bottom: 0; }
.report-sidebar .radio-opt:hover { background: var(--bg); }
.report-sidebar .radio-opt.active { border-color: var(--accent); background: var(--accent-dim); }
.report-sidebar .radio-dot { width: 14px; height: 14px; border-radius: 50%; border: 1.5px solid var(--border); flex-shrink: 0; display: flex; align-items: center; justify-content: center; transition: border-color 0.12s, background 0.12s; }
.report-sidebar .radio-opt.active .radio-dot { border-color: var(--accent); background: var(--accent); }
.report-sidebar .radio-opt.active .radio-dot::after { content: ''; width: 5px; height: 5px; border-radius: 50%; background: white; }
.report-sidebar .radio-opt span { font-size: 13px; color: var(--text-muted); }
.report-sidebar .radio-opt.active span { color: var(--accent); font-weight: 500; }

/* VAT TOGGLE */
.report-sidebar .vat-toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 0 0; }
.report-sidebar .vat-toggle-label { font-size: 12px; color: var(--text-muted); display: flex; align-items: center; gap: 6px; }
.report-sidebar .vat-toggle-pill { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; cursor: pointer; user-select: none; transition: all 0.15s; border: 1px solid var(--border); background: var(--bg); color: var(--text-muted); }
.report-sidebar .vat-toggle-pill.on { background: var(--green-dim); border-color: var(--green-border); color: #007a63; }
.report-sidebar .vat-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; opacity: 0.7; }

/* PLATFORM ROWS */
.report-sidebar .platform-row { display: flex; align-items: center; gap: 8px; padding: 8px 0; border-bottom: 1px solid var(--border); }
.report-sidebar .platform-row:last-child { border-bottom: none; }
.report-sidebar .platform-name { font-size: 13px; font-weight: 500; color: var(--text); width: 90px; flex-shrink: 0; }

/* SOURCE SELECTOR */
.report-sidebar .source-selector { padding: 4px 10px; border-radius: 20px; border: 1px solid var(--border); background: var(--bg); font-size: 11px; font-weight: 500; color: var(--text-muted); cursor: pointer; font-family: inherit; transition: all 0.12s; }
.report-sidebar .source-selector:hover { border-color: var(--accent); color: var(--accent); }
.report-sidebar .source-selector.active { background: var(--accent-dim); border-color: var(--accent-border); color: var(--accent); font-weight: 600; }

/* EXPENSE / EXTRA ITEMS */
.report-sidebar .expense-item { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: var(--bg); border-radius: var(--radius); margin-bottom: 6px; }
.report-sidebar .expense-item:last-child { margin-bottom: 0; }
.report-sidebar .expense-item .name { font-size: 13px; color: var(--text); }
.report-sidebar .expense-item .amt { font-size: 13px; font-weight: 500; color: var(--text-muted); min-width: 56px; text-align: right; }
.report-sidebar .expense-item button { background: none; border: none; cursor: pointer; color: var(--text-muted); padding: 0; font-size: 18px; line-height: 1; display: flex; align-items: center; }
.report-sidebar .expense-item button:hover { color: #d63031; }
.report-sidebar .empty-note { font-size: 12px; color: var(--text-muted); padding: 4px 2px; font-style: italic; }

/* TOTAL ROWS */
.report-sidebar .total-row { display: flex; justify-content: space-between; align-items: center; padding: 7px 10px; border-radius: var(--radius); margin-top: 8px; }
.report-sidebar .total-row span { font-size: 12px; }
.report-sidebar .total-row strong { font-size: 13px; font-weight: 600; }
.report-sidebar .total-neutral { background: var(--bg); border: 1px solid var(--border); }
.report-sidebar .total-neutral span { color: var(--text-muted); }
.report-sidebar .total-neutral strong { color: var(--text); }

/* ADD BUTTONS */
.report-sidebar .add-btn { display: inline-flex; align-items: center; gap: 5px; padding: 5px 10px; border-radius: var(--radius); border: none; font-size: 12px; font-weight: 500; cursor: pointer; font-family: 'Inter', sans-serif; transition: opacity 0.12s; }
.report-sidebar .add-btn:hover { opacity: 0.8; }
.report-sidebar .add-btn.green { background: var(--green-dim); color: #007a63; }
.report-sidebar .add-btn.blue { background: #E6F1FB; color: #0C447C; }
.report-sidebar .add-btn.purple { background: var(--accent-dim); color: var(--accent); }

/* CONS TOGGLE */
.report-sidebar .cons-toggle-row { display: flex; align-items: center; justify-content: space-between; }
.report-sidebar .toggle { position: relative; width: 34px; height: 18px; flex-shrink: 0; }
.report-sidebar .toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
.report-sidebar .toggle-track { position: absolute; inset: 0; background: var(--border); border-radius: 9px; cursor: pointer; transition: background 0.2s; }
.report-sidebar .toggle input:checked + .toggle-track { background: var(--accent); }
.report-sidebar .toggle-thumb { position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; background: white; border-radius: 50%; transition: transform 0.2s; pointer-events: none; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
.report-sidebar .toggle input:checked ~ .toggle-thumb { transform: translateX(16px); }

/* INFO NOTE (W1 Refonte #10 — scope note at top of sidebar) */
.report-sidebar .info-note { font-size: 11px; color: var(--text-muted); background: var(--bg); border-radius: var(--radius); padding: 7px 10px; line-height: 1.5; margin: 10px 10px; border: 1px solid var(--border); }

/* INCOME / GUEST EXTRAS (W1 Refonte #10) */
.report-sidebar .divider-label.income { color: #3B6D11; }
.report-sidebar .extras-item { display: flex; align-items: center; gap: 8px; padding: 6px 10px; background: var(--bg); border-radius: var(--radius); margin-bottom: 6px; border-left: 3px solid var(--green); }
.report-sidebar .extras-item:last-child { margin-bottom: 0; }
.report-sidebar .extras-item .name { flex: 1; font-size: 13px; color: var(--text); }
.report-sidebar .extras-item .amt { font-size: 13px; font-weight: 500; color: var(--text); min-width: 56px; text-align: right; }
.report-sidebar .extras-item button { background: none; border: none; cursor: pointer; color: var(--text-muted); padding: 0; font-size: 14px; display: flex; align-items: center; }
.report-sidebar .extras-item button:hover { color: #d63031; }
.report-sidebar .total-row.total-income { background: var(--green-dim); border: 1px solid var(--green-border); }
.report-sidebar .total-row.total-income span,
.report-sidebar .total-row.total-income strong { color: #007a63; }

/* FOOTER */
.report-sidebar .footer-btns { display: flex; gap: 8px; padding: 8px 10px 4px; }
.report-sidebar .footer-btns button { flex: 1; padding: 8px; border-radius: var(--radius); font-size: 13px; font-weight: 500; cursor: pointer; font-family: 'Inter', sans-serif; transition: opacity 0.12s; border: none; }
.report-sidebar .footer-btns button:hover { opacity: 0.85; }
.report-sidebar .footer-btns button:disabled { opacity: 0.5; cursor: not-allowed; }
.report-sidebar .btn-reset { background: var(--bg); border: 1px solid var(--border); color: var(--text-muted); }
.report-sidebar .btn-save { background: #3b35a8; color: white; }
.report-sidebar .btn-save-this-month {
  width: 100%;
  padding: 8px;
  border-radius: var(--radius);
  background: var(--amber-dim);
  border: 1px solid var(--amber-border);
  color: var(--amber-text);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
}
.report-sidebar .btn-save-this-month:disabled { opacity: 0.5; cursor: not-allowed; }

/* CATEGORY BADGES (W3 client request — Combine expenses) */
.report-sidebar .expense-cat-badge { font-size: 9px; font-weight: 600; padding: 2px 7px; border-radius: 100px; text-transform: uppercase; letter-spacing: 0.04em; }
.report-sidebar .expense-cat-badge.maintenance { background: #fde9d0; color: #854F0B; }
.report-sidebar .expense-cat-badge.guest_extra { background: #e6f4d8; color: #3B6D11; }
.report-sidebar .expense-cat-badge.cleaning { background: #ddd5fb; color: #5b4fcf; }
.report-sidebar .expense-cat-badge.other { background: #f0eef8; color: #6b7280; }
.report-sidebar .expense-file-chip {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #5b4fcf;
  text-decoration: none;
  font-size: 11px;
  background: #ede9fe;
  padding: 2px 6px;
  border-radius: 4px;
}
.report-sidebar .expense-file-chip:hover { text-decoration: underline; }

/* Outer settings-panel hosts the report-sidebar — make sure it can scroll
   when the inner content is taller than the modal. */
.settings-panel {
  background: transparent;
  border: none;
  box-shadow: none;
  padding: 12px;
  overflow-y: auto;
  min-height: 0;
  height: 100%;
}

</style>